import {
  fetchSiteConditionState,
  hasSupportingActivityFacts,
  latestCompletedPt15mMeasurementValue,
  normalizeConditionStateAsOf,
  operationalMeasurementIdentityKey,
  recurringRecordedOccurrenceTotal,
  recurringRateFromSameBuckets,
  recurringSupportingActivityTotal,
  recurringUsableHours,
  ROLLING_24H_ANCHORED_REASON,
  supportingActivityTotal,
  type ContinuousSafetyFetchResult,
  type OperationalMeasurementProvenance,
  type SiteConditionMeasurementCoverage,
  type SiteConditionMeasurementWindow,
  type SiteConditionState,
  type SiteConditionStateResponse,
  type SiteConditionCurrentActivity,
  type ConditionLifecycleSummary,
} from './siteConditionState';
import { api } from '../../services/api';
import {
  overTimeWindowsFromSiteConditionResponse,
  readinessInputFromSiteConditionResponse,
  sinceControlWindowFromSiteConditionResponse,
} from '../sellableSiteExperience/shellContext';
import {
  formatMeasuredCalendarDay,
  formatRecurringCustomerWindowLabel,
  presentOverTime,
  recurringWindowResolution,
  resolveIncludedMeasurementThrough,
  resolveOverTimeHistoryAsOf,
  type OverTimeWindowChoice,
  type OverTimeWindowId,
} from '../sellableSiteExperience/overTimePresenter';
import { presentSinceControl } from '../sellableSiteExperience/sinceControlPresenter';
import { evaluateNowReadiness } from '../sellableSiteExperience/nowReadiness';
import {
  buyerPatternDisplayLabel,
} from '../overview/overviewBuyerDisplay';
import { mergeOverviewV2CompositeNow } from '../overview/overviewV2CompositeNow';
import { mergeOverviewV2CompositeRecurring } from '../overview/overviewV2CompositeRecurring';
import { mergeOverviewV2CompositeLifecycle } from '../overview/overviewV2CompositeLifecycle';
import { incidentsApi } from '../../services/incidentsApi';
import {
  isPeerSiteBuyerAlias,
  resolveCustomerCameraScopeLabel,
  resolveCustomerPlaceLabel,
} from '../../utils/customerPlaceLabel';
import type { SafetyLocationLookups } from '../../utils/locationDisplay';
import { loadSiteLocationLookups } from '../../utils/loadSiteLocationLookups';
import { sitesApi } from '../../services/sitesApi';
import { formatSiteDateTime, openSitePt15mWindowStart, resolveSiteTimeZone } from '../siteTime/siteTime';
import {
  OPERATIONAL_CURRENT_SUPPORT,
  OPERATIONAL_RATE_DISPLAY_ENABLED,
  PROVISIONAL_GROUPING_CAVEAT,
  REVIEW_LIST_NOT_OPENED,
  assertOperationalMeasurement,
  formatProvisionalEpisodesLabel,
  formatRecurringProvisionalEpisodesLabel,
  resolveOperationalCurrentMeasurement,
  sinceControlClaimComparisonAllowed,
  type MeasurementValueType,
} from './measurementContract';
import {
  buildMeasurementOpenWorkItems,
  sumExcludedPendingReviewCount,
  type MeasurementOpenWorkItem,
  type MeasurementOpenWorkKind,
} from './openWorkAttention';
import {
  alignCurrentRecurringBasis,
  recurringHistoryTrustNote,
  resolveRecurringHistoryTrustClass,
  type MeasurementBasisAlignment,
  type RecurringHistoryTrustClass,
} from './recurringMeasurementBasis';

export type OverviewFieldProvenance = {
  source: string | null;
  status: 'canonical' | 'missing';
};

export type OverviewAnswerTone = 'available' | 'limited' | 'unavailable';

export type OverviewNowCoverageState =
  | 'insufficient_history_no_basis'
  | 'insufficient_history_accumulating'
  | 'insufficient_history_unknown'
  | 'not_met';

export type OverviewSinceControlRef = {
  controlId: string | null;
  anchorStartedAt: string | null;
  observedThrough: string | null;
  availability: string;
  comparisonStatus: string | null;
  bucketCount: number;
};

export type OverviewConditionRow = {
  conditionId: string;
  label: string;
  location: string;
  measurementState: string;
  latestReadingAt: string | null;
  /** EPIC293: machine attention reason when Overview cutover gate is ON. */
  attentionReason?: string | null;
  attentionRequired?: boolean;
  /** Coverage pending-review count for canonical next-action CTAs. */
  pendingReviewCount?: number | null;
  /** Enumerable review-set identity; required before pendingReviewCount can become a CTA count. */
  reviewSetId?: string | null;
  tone: OverviewAnswerTone;
  zoneId: string | null;
  cameraId: string | null;
  deviceId: string | null;
  conditionFamily: string | null;
  /** Primary stored incident_type for Investigate / thumbs (e.g. zone_violation). */
  incidentType: string | null;
  /** Extra stored types to probe for stills when primary is empty. */
  candidateIncidentTypes?: string[];
  /** Per-condition completed measurement register fields. */
  cardShape: 'completed_with_result' | 'completed_without_result';
  completedAt: string;
  /** Incremental: closed window end / as-of freshness; scan_only mirrors completedAt. */
  currentThrough?: string;
  /** ISO end of the latest finalized condition OM (Last Completed authority). */
  currentThroughAt?: string | null;
  coverageLabel: string;
  coverageState?: OverviewNowCoverageState | null;
  coveragePct?: number | null;
  coverageShortfallReason?: string | null;
  coverageHoursAccumulated?: number | null;
  coverageHoursRequired?: number | null;
  coverageEstimatedDaysRemaining?: number | null;
  windowUsableHours?: number | null;
  windowExpectedHours?: number | null;
  shortfallReasonBreakdown?: SiteConditionMeasurementCoverage['shortfallReasonBreakdown'];
  coveragePartition?: SiteConditionMeasurementCoverage['coveragePartition'];
  coverageDiagnostics?: SiteConditionMeasurementCoverage['coverageDiagnostics'];
  coverageReconciliationInvalid?: boolean;
  /** Trailing telemetry outage age label — distinct from reading Age. */
  telemetryOutageLabel?: string | null;
  rateLabel?: string | null;
  /** Discriminated Current measurement type — never claim-bearing on this surface. */
  measurementType?: MeasurementValueType | null;
  producerStaleness?: boolean | null;
  readStale?: boolean;
  expectedRefreshLabel?: string | null;
  /**
   * Review fields are retained for backend/diagnostics only.
   * Current UI must not render them (EPIC284 Task 5.1).
   */
  reviewBlockerLabel?: string | null;
  reviewWindowStateLabel?: string | null;
  reviewProgressLabel?: string | null;
  oldestPendingLabel?: string | null;
  blockedByLabel?: string | null;
  resultLabel: string;
  ageLabel: string;
  nextUpdate: string;
  /** Claim-only reviewed numerator for this condition row. */
  measurementValue: number | null;
  /**
   * Latest completed PT15M bucket count. Showcase NOW uses this only when
   * measurementValue is null. It does not replace the reviewed claim.
   */
  latestCompletedPt15mValue?: number | null;
  /** Raw now-window display_state. Freshness only; not a measurement gate. */
  nowWindowDisplayState?: string | null;
  nowWindowReasonCodes?: string[] | null;
  /** Supporting bucket activity — independent of claim measurementValue. */
  supportingActivityLabel?: string | null;
  /** Provisional grouping caveat for Current rows (Task 5.4). */
  provisionalGroupingCaveat?: string | null;
  /**
   * Presentation state chip for the condition row (Brief Slice 1).
   * Operational posture only — never a claim/rate synonym.
   */
  stateChipLabel?: string | null;
  /**
   * Count with its denominator beside it when both are present
   * (e.g. activity · usable of expected hours).
   */
  countWithDenominatorLabel?: string | null;
  /**
   * One short operational body line for the calm condition-card face.
   * Integrity detail stays in Measurement health / View details.
   */
  bodyLine?: string | null;
  /** EPIC284 Task 2.2 — operational identity; twin/window swap must change this. */
  operationalProvenance?: OperationalMeasurementProvenance | null;
  measurementIdentityKey?: string | null;
  /** CM7 provisional open-window activity. Never an OM / claim-bearing rate. */
  currentActivity?: SiteConditionCurrentActivity | null;
  sinceControl?: OverviewSinceControlRef;
  /** COMPOSITE is a buyer subject, not a physical CORE_CONDITIONS row. */
  subjectKind?: 'COMPOSITE' | null;
  compositeIncidentId?: string | null;
  compositeEvidencePath?: string | null;
};

export type OverviewRecurringActivityBucket = {
  occurrences: number;
  usableHours: number | null;
  expectedHours: number | null;
  start: string | null;
  end: string | null;
  isPartial: boolean;
  resolution: string;
};

export type OverviewPeriodConditionRow = {
  conditionId: string;
  label: string;
  location: string;
  measuredPeriods: number;
  /**
   * Coverage-slot copy for the row. Never an episode count.
   * When 1D episode day total is fail-closed, includes an explicit withheld note.
   */
  measuredPeriodsLabel?: string | null;
  /** True when Recurring 1D episode day Σ is withheld for this row. */
  episodeDayTotalWithheld?: boolean;
  /** Per-camera usable hours for the selected Recurring window when present. */
  usableHours?: number | null;
  /** Per-camera expected hours for the selected Recurring window when present. */
  expectedHours?: number | null;
  comparisonStatus: string;
  tone: OverviewAnswerTone;
  zoneId: string | null;
  cameraId: string | null;
  deviceId?: string | null;
  conditionFamily?: string | null;
  incidentType: string | null;
  candidateIncidentTypes?: string[];
  /** Supporting-activity Σ for the selected Recurring window (provisional episodes). */
  supportingActivityTotal?: number | null;
  /** Customer label for per-condition provisional episode total. */
  supportingActivityLabel?: string | null;
  /** Authoritative recurrence_basis occurrence count; not gated by rate/day-total completeness. */
  recordedOccurrenceTotal?: number | null;
  /** Customer label for authoritative recorded occurrences. */
  recordedOccurrenceLabel?: string | null;
  /** Same-bucket finalized OM usable seconds as recordedOccurrenceTotal. */
  usableCameraSeconds?: number | null;
  /** API recurring_rate when present; presentation still derives headline from count/hours. */
  recurringRate?: number | null;
  /** Provisional per-period bucket values for sparkline shape only (not a trend claim). */
  activitySeries?: number[];
  /**
   * Recurring period buckets with occurrence counts and, when the OM exposes them,
   * per-bucket usable hours. Used by the Recurring rate chart. Never inferred from P/F/S.
   */
  activityBuckets?: OverviewRecurringActivityBucket[];
  operationalProvenance?: OperationalMeasurementProvenance | null;
  measurementIdentityKey?: string | null;
  /** Now/Current display_state — used to mark stale history rows. */
  nowDisplayState?: string | null;
  /**
   * When Now is not a qualifying current window (e.g. stale), customer note for
   * Trends/history — never shown under Current Issues.
   */
  notCurrentlyMeasuringLabel?: string | null;
  /** ISO end of the latest included finalized OM for THIS condition's Recurring population. */
  includedThroughAt?: string | null;
  sinceControl?: OverviewSinceControlRef;
  lifecycle?: ConditionLifecycleSummary | null;
  /** COMPOSITE is a buyer subject, not a physical CORE_CONDITIONS row. */
  subjectKind?: 'COMPOSITE' | null;
  compositePatternIncidentId?: string | null;
  compositeReviewPath?: string | null;
  compositeControlPath?: string | null;
};

export type OverviewDecisionSummary = {
  siteId: string;
  queryAsOf: string;
  /** sites.timezone IANA from measurement frozen basis; missing means formatters use UTC. */
  siteTimezone?: string | null;
  conditionCount: number;
  readCompleteness?: 'complete' | 'partial';
  siteTotalsAuthoritative?: boolean;
  failedReads?: SiteConditionStateResponse['failedReads'];
  primaryConditionId: string | null;
  now: {
    cardShape: 'completed_with_result' | 'completed_without_result';
    /** Customer primary answer — never a measurement-state synonym. */
    answer: string;
    answerTone: OverviewAnswerTone;
    supportLine: string;
    coverageState?: OverviewNowCoverageState | null;
    coveragePct?: number | null;
    coverageShortfallReason?: string | null;
    coverageHoursAccumulated?: number | null;
    coverageHoursRequired?: number | null;
    coverageEstimatedDaysRemaining?: number | null;
    windowUsableHours?: number | null;
    windowExpectedHours?: number | null;
    shortfallReasonBreakdown?: SiteConditionMeasurementCoverage['shortfallReasonBreakdown'];
    coveragePartition?: SiteConditionMeasurementCoverage['coveragePartition'];
    coverageDiagnostics?: SiteConditionMeasurementCoverage['coverageDiagnostics'];
    coverageReconciliationInvalid?: boolean;
    telemetryOutageLabel?: string | null;
    rateLabel?: string | null;
    /** Discriminated Current measurement type — never claim-bearing on this surface. */
    measurementType?: MeasurementValueType | null;
    producerStaleness?: boolean | null;
    readStale?: boolean;
    expectedRefreshLabel?: string | null;
    /**
     * Canonical freshness for the Current site window — same derivation as
     * Measurement health. Distinct from `ageLabel` (activity/window age).
     */
    freshnessLabel?: string | null;
    /**
     * Review fields are retained for backend/diagnostics only.
     * Current UI must not render them (EPIC284 Task 5.1).
     */
    reviewBlockerLabel?: string | null;
    reviewWindowStateLabel?: string | null;
    reviewProgressLabel?: string | null;
    oldestPendingLabel?: string | null;
    blockedByLabel?: string | null;
    /** Dashboard query timestamp — distinct from window through / history as-of. */
    dashboardAsOfLabel?: string | null;
    measurementState: string;
    updateMode: string;
    lastCompleted: string;
    measuredWindow: string;
    coverageLabel: string;
    resultLabel: string;
    ageLabel: string;
    /** Claim-only reviewed numerator; null when rate is not issuable. */
    measurementValue: number | null;
    /** Supporting bucket activity — independent of claim measurementValue. */
    supportingActivityLabel?: string | null;
    provisionalGroupingCaveat?: string | null;
    nextUpdate: string;
    cadenceContext: string;
    schedulingIncomplete: boolean;
    schedulingNote: string | null;
    canShowConditionSummary: boolean;
    conditionId: string | null;
    conditionFamily: string | null;
    zoneId: string | null;
    cameraId: string | null;
    deviceId: string | null;
    operationalProvenance?: OperationalMeasurementProvenance | null;
    measurementIdentityKey?: string | null;
    conditionRows: OverviewConditionRow[];
  };
  overTime: {
    answer: string;
    answerTone: OverviewAnswerTone;
    /** Short face sentence only — long caveats live in measurementDetailLines. */
    supportLine: string;
    /**
     * Anchored-day / PT15M / OM / provisional / trustworthy-history detail.
     * Render behind View measurement details — never above the numbers.
     */
    measurementDetailLines?: string[];
    coverageState?: OverviewNowCoverageState | null;
    stateLabel: string;
    asOfLabel: string;
    /** Claim-only reviewed numerator; null when rate is not issuable. */
    measurementValue: number | null;
    /**
     * Supporting-facts label from bucket activity — independently gated.
     * Never camera-hours; never derived from claim measurementValue.
     */
    observedValueLabel: string | null;
    /** Task 6.1 — same provisional caveat as Current wherever counts appear. */
    provisionalGroupingCaveat?: string | null;
    /** Canonical denominator status; never derived from supporting activity. */
    coverageBasisLabel?: string;
    /** Claim rate only when both gates pass and every bucket is fresh. */
    coverageRateLabel?: string | null;
    /** Plain-language note when 7d/30d/90d are the same short history. */
    historySpanNote: string | null;
    historyProgress: string | null;
    /**
     * True when Recurring 1D episode day Σ is fail-closed (incomplete/anchored)
     * while measured PT15M coverage slots may still be shown.
     */
    episodeDayTotalWithheld?: boolean;
    /**
     * Withheld / building faces: keep the card lean (headline + window chips +
     * location/thumb/status). Period essays stay in measurementDetailLines.
     */
    leanFace?: boolean;
    /** Amendment C2 — trustworthy-history clock class for this series. */
    historyTrustClass?: RecurringHistoryTrustClass | null;
    /** Customer note when series is not trustworthy-history start. */
    historyTrustNote?: string | null;
    /** Task 6.4 — Current vs Recurring basis alignment. */
    basisAlignment?: MeasurementBasisAlignment | null;
    selectedWindowId: OverTimeWindowId;
    windowLabel: string;
    /**
     * Window kind + resolution for the selected Recurring population.
     * Must not be read as Current COW PT15M when 1D (`rolling_24h`) is selected.
     */
    windowScopeLabel?: string | null;
    periodFacts: string;
    windowChoices: OverTimeWindowChoice[];
    conditionRows: OverviewPeriodConditionRow[];
  };
  sinceControl: {
    answer: string;
    answerTone: OverviewAnswerTone;
    supportLine: string;
    followUpState: string;
    observedResult: string;
    controlId: string | null;
    linkedConditionCount: number;
    totalConditionCount: number;
    /** Brief Slice 4 — presentation kind for Since Control state table. */
    presentationKind?: SinceControlPresentationKind;
    /** Baseline / follow-up sides when comparison issuable (server rates only). */
    comparisonSides?: SinceControlComparisonSide[] | null;
    variationCaveat?: string | null;
    provenanceLine?: string | null;
    /** Named basis difference when not comparable; never an averaged result. */
    basisDifferedLabel?: string | null;
    actionLabel?: string;
    /** Human location for the linked control, when a place label exists. */
    controlLocation?: string | null;
    /** Recorded-control date label, when the window emits one. */
    controlDateLabel?: string | null;
  };
  siteComposition: string;
  /**
   * Brief Slice 4 — integrity panel beside Actions.
   * Carries coverage / shortfall / producer / named issues — not operational claims.
   */
  measurementHealth: OverviewMeasurementHealth;
  /**
   * Brief Slice 4 — Actions requiring a decision (measurement Review blockers).
   * Controls Attention Decide rows are merged in the Overview component.
   */
  decisionActions: OverviewDecisionActionRow[];
  /**
   * EPIC284 Task 7 — measurement / claim Open Work only.
   * Distinct from Controls Attention; never includes Current-derived pending backlog.
   */
  openWork: {
    items: MeasurementOpenWorkItem[];
    /** Pending disposition counts excluded from Open Work (quarantine / non-claim). */
    excludedPendingReviewTotal: number;
  };
  provenance: Record<string, OverviewFieldProvenance>;
  /** Methodology / contract diagnostics — not the default customer answer. */
  methodology: {
    nowMeasurementValue: number | null;
    overTimeMeasurementValue: number | null;
    overTimeWindowLabel: string;
    nowLimitations: string;
    overTimeLimitations: string;
    sinceControlLimitations: string;
    diagnosticNotes: string[];
  };
};

/** Customer-facing Now label — never expose Limited / partial / Current jargon. */
function displayStateLabel(
  state: string,
  windowThroughLabel?: string | null,
): string {
  const through = String(windowThroughLabel || '').trim();
  const throughSuffix = through && through !== '—'
    ? ` · through ${through}`
    : '';
  if (state === 'current' || state === 'current_with_limitations') return 'Measured';
  // Delayed Current must scream the closed window date; OM `stale` alone can
  // still describe a fresh closed window (e.g. TELEMETRY_MISSING UNKNOWN).
  if (state === 'delayed') return `Older reading${throughSuffix}`;
  if (state === 'stale') return 'Out of date';
  if (state === 'never_measured') return 'No reading yet';
  if (state === 'unavailable') return 'No reading';
  return 'No reading';
}

/** Age label. “Stale window” only when the producer/coverage is actually behind. */
function formatAgeWithStaleWindow(params: {
  queryAsOf: string | null | undefined;
  ageBasis: string | null | undefined;
  windowThroughLabel: string | null | undefined;
  readStale?: boolean | null;
}): string {
  const age = formatMeasurementAge(params.queryAsOf, params.ageBasis);
  const through = String(params.windowThroughLabel || '').trim();
  if (
    params.readStale
    && through
    && through !== '—'
    && age !== 'Unavailable'
    && age !== 'Just completed'
  ) {
    return `${age} · stale window through ${through}`;
  }
  return age;
}

function customerStateTone(state: string): OverviewAnswerTone {
  if (state === 'current' || state === 'current_with_limitations') return 'available';
  if (state === 'delayed' || state === 'stale') return 'limited';
  return 'unavailable';
}

function updateModeLabel(value: string | null): string {
  if (value === 'scan_only') return 'Scheduled scans';
  if (value === 'incremental') return 'Incremental';
  return 'Unavailable';
}

function provenance(
  source: string | null | undefined,
): OverviewFieldProvenance {
  return source
    ? { source, status: 'canonical' }
    : { source: null, status: 'missing' };
}

function siteTimeZoneFromCoverage(
  coverage?: { frozenBasis?: { siteTimezone?: string | null } | null } | null,
): string | null {
  return resolveSiteTimeZone(coverage?.frozenBasis?.siteTimezone);
}

function formatCustomerTime(raw: string | null | undefined, timeZone?: string | null): string {
  if (!raw || raw === '—') return '—';
  return formatSiteDateTime(raw, timeZone, { includeYear: false }) || raw;
}

/**
 * Recurring History as of — keep anchored window-end calendar days stable.
 * Midnight-UTC bounds (common for rolling day windows) must not render as the
 * previous local evening (e.g. Aug 4 00:00Z → Aug 3 5:00 PM PT).
 */
function formatRecurringHistoryAsOf(raw: string | null | undefined, timeZone?: string | null): string {
  if (!raw || raw === '—') return '—';
  const isUtcDayBoundary = /T00:00:00(\.\d+)?Z$/i.test(String(raw).trim());
  if (isUtcDayBoundary) {
    return formatSiteDateTime(raw, 'UTC', { includeYear: false }) || raw;
  }
  return formatCustomerTime(raw, timeZone);
}

/**
 * Face "History as of" when Recurring 1D is anchored to last observed day.
 * Names the measured day and contrasts the dashboard query-as-of so operators
 * cannot mistake Aug 1–2 history for wall-clock today.
 */
export function formatAnchoredRecurringHistoryAsOfFace(args: {
  historyAsOf: string | null | undefined;
  queryAsOf?: string | null;
  anchored: boolean;
  timeZone?: string | null;
}): string {
  const measured = formatRecurringHistoryAsOf(args.historyAsOf, args.timeZone);
  if (!args.anchored || measured === '—') return measured;
  const measuredDay = formatMeasuredCalendarDay(args.historyAsOf, args.timeZone);
  if (!measuredDay) {
    return `${measured} (not wall-clock today)`;
  }
  const dashboardDay = formatMeasuredCalendarDay(args.queryAsOf, args.timeZone);
  const dashboardNote = dashboardDay
    ? `not wall-clock today; dashboard is ${dashboardDay}`
    : 'not wall-clock today';
  return `${measured} — last measured day ends ${measuredDay} (${dashboardNote})`;
}

/** Latest included measurement end across listed conditions for one window. */
function resolvePanelIncludedThrough(
  conditions: SiteConditionState[],
  windowId: OverTimeWindowId,
): string {
  let latest: string | null = null;
  for (const condition of conditions) {
    const window = condition.operationalMeasurements.overTime[windowId] || null;
    const candidate = resolveIncludedMeasurementThrough(window);
    if (!candidate || candidate === '—') continue;
    const latestMs = latest ? Date.parse(latest) : Number.NaN;
    const candidateMs = Date.parse(candidate);
    if (
      !latest
      || (Number.isFinite(candidateMs) && Number.isFinite(latestMs) && candidateMs >= latestMs)
      || (!(Number.isFinite(candidateMs) && Number.isFinite(latestMs)) && candidate > latest)
    ) {
      latest = candidate;
    }
  }
  return latest || '—';
}

/** Rolling 24h envelope end — inspectable metadata, not customer measured-through. */
function resolvePanelEnvelopeEnd(
  window: SiteConditionMeasurementWindow | null | undefined,
): string | null {
  if (!window) return null;
  const envelope = window.operationalMeasurementProvenance?.windowEnd
    || window.historyAsOf
    || resolveOverTimeHistoryAsOf(window);
  return envelope && envelope !== '—' ? envelope : null;
}

/** UTC calendar day for scope alignment (ignore intra-day bucket-end drift). */
function windowEndDayKey(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const ts = Date.parse(raw);
  if (!Number.isFinite(ts)) return String(raw).trim() || null;
  return new Date(ts).toISOString().slice(0, 10);
}

/**
 * Sum provisional episode totals across listed conditions when window scopes
 * align on the same UTC day. If days differ, return null so the UI does not
 * invent a combined total across mismatched histories.
 */
function aggregateAlignedEpisodeTotal(
  rows: Array<{
    supportingActivityTotal?: number | null;
    operationalProvenance?: OperationalMeasurementProvenance | null;
  }>,
): number | null {
  const withTotals = rows.filter(
    (row) => row.supportingActivityTotal != null
      && Number.isFinite(row.supportingActivityTotal),
  );
  if (withTotals.length === 0) return null;
  const days = new Set(
    withTotals
      .map((row) => windowEndDayKey(row.operationalProvenance?.windowEnd))
      .filter((day): day is string => Boolean(day)),
  );
  if (days.size > 1) return null;
  return withTotals.reduce(
    (sum, row) => sum + Number(row.supportingActivityTotal),
    0,
  );
}

/** Provisional per-period series for cheap sparklines (shape only — not a claim). */
function supportingActivitySeries(
  window: SiteConditionMeasurementWindow | null | undefined,
): number[] {
  const series: number[] = [];
  for (const bucket of window?.buckets || []) {
    if (bucket.measurementValue != null && Number.isFinite(bucket.measurementValue)) {
      series.push(Number(bucket.measurementValue));
    }
  }
  return series;
}

function secondsToHours(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return null;
  return value / 3600;
}

/** Recurring period buckets for the rate chart. Usable hours only when the OM exposes them. */
function supportingActivityBuckets(
  window: SiteConditionMeasurementWindow | null | undefined,
): OverviewRecurringActivityBucket[] {
  const buckets: OverviewRecurringActivityBucket[] = [];
  for (const bucket of window?.buckets || []) {
    const occurrences = bucket.measurementValue;
    const usableHours = secondsToHours(bucket.usableCameraSeconds);
    const expectedHours = secondsToHours(bucket.expectedCameraSeconds);
    const hasOccurrence = typeof occurrences === 'number' && Number.isFinite(occurrences);
    if (!hasOccurrence && usableHours == null && expectedHours == null) continue;
    buckets.push({
      occurrences: hasOccurrence ? Number(occurrences) : 0,
      usableHours,
      expectedHours,
      start: bucket.interval?.start || null,
      end: bucket.interval?.end || bucket.observedThrough || null,
      isPartial: Boolean(bucket.isPartial),
      resolution: String(bucket.resolution || ''),
    });
  }
  return buckets;
}

function formatCustomerNumber(value: number): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: value >= 10 ? 1 : 2,
  }).format(value);
}

function formatCustomerPercent(value: number | null | undefined): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Unavailable';
  return `${new Intl.NumberFormat(undefined, {
    maximumFractionDigits: 1,
    minimumFractionDigits: value % 1 === 0 ? 0 : 1,
  }).format(value)}%`;
}

function formatCustomerTimeOrLabel(raw: string | null | undefined, timeZone?: string | null): string {
  if (!raw || raw === 'Unavailable' || raw === 'N/A' || /^Unavailable\b/i.test(raw)) {
    return raw || 'Unavailable';
  }
  if (/not scheduled/i.test(raw)) {
    return 'Not scheduled';
  }
  return formatCustomerTime(raw, timeZone);
}

function formatOperationalIdentityNote(
  provenance: OperationalMeasurementProvenance | null | undefined,
): string | null {
  if (!provenance) return null;
  const windowId = provenance.windowId || 'unavailable';
  const conditionId = provenance.conditionId || 'unavailable';
  const bounds = provenance.windowStart && provenance.windowEnd
    ? ` bounds=${provenance.windowStart}→${provenance.windowEnd}`
    : '';
  return (
    `Operational measurement identity: window_id=${windowId}`
    + ` condition_id=${conditionId}${bounds}.`
  );
}

function formatMeasurementWindowLabel(
  window: SiteConditionMeasurementWindow | null | undefined,
): string {
  const resolution = String(window?.buckets?.[0]?.resolution || '').trim().toUpperCase();
  const minuteMatch = resolution.match(/^PT(\d+)M$/);
  if (minuteMatch) {
    const minutes = Number(minuteMatch[1]);
    return `${minutes} minute window`;
  }
  const hourMatch = resolution.match(/^PT(\d+)H$/);
  if (hourMatch) {
    const hours = Number(hourMatch[1]);
    return `${hours} hour${hours === 1 ? '' : 's'} window`;
  }
  const dayMatch = resolution.match(/^P(\d+)D$/);
  if (dayMatch) {
    const days = Number(dayMatch[1]);
    return `${days} day${days === 1 ? '' : 's'} window`;
  }
  if (window?.buckets?.[0]?.windowKind === 'current_operating_window') {
    return 'Current operating window';
  }
  return 'Measurement window unavailable';
}

function formatMeasurementAge(
  queryAsOf: string | null | undefined,
  completedAt: string | null | undefined,
): string {
  const queryTs = Date.parse(String(queryAsOf || ''));
  const completedTs = Date.parse(String(completedAt || ''));
  if (!Number.isFinite(queryTs) || !Number.isFinite(completedTs)) {
    return 'Unavailable';
  }
  const diffMs = Math.max(0, queryTs - completedTs);
  const diffMinutes = Math.floor(diffMs / (60 * 1000));
  if (diffMinutes < 1) return 'Just completed';
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} old`;
  }
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 48) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} old`;
  }
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} old`;
}

function coverageWindowEnd(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): string | null {
  const windowEnd = coverage?.frozenBasis?.windowEnd;
  return typeof windowEnd === 'string' && windowEnd.trim() ? windowEnd.trim() : null;
}

function expectedRefreshMsFromWindow(
  window: SiteConditionMeasurementWindow | null | undefined,
): number | null {
  const match = String(window?.buckets?.[0]?.resolution || '')
    .trim().toUpperCase().match(/^PT(\d+)M$/);
  if (!match) return null;
  const minutes = Number(match[1]);
  return Number.isFinite(minutes) && minutes > 0 ? minutes * 60 * 1000 : null;
}

function expectedRefreshLabelFromWindow(
  window: SiteConditionMeasurementWindow | null | undefined,
): string | null {
  const match = String(window?.buckets?.[0]?.resolution || '')
    .trim().toUpperCase().match(/^PT(\d+)M$/);
  return match ? `Every ${Number(match[1])} minute${match[1] === '1' ? '' : 's'}` : null;
}

/**
 * Incremental freshness: producer lag, or last-completed more than one PT15M
 * behind the open wall window. Do not call the previous closed quarter “stale”
 * merely because queryAsOf − lastClosedEnd > 15m while the next window is open.
 * Do not use OM display_state delayed/stale alone — continuous TELEMETRY_MISSING
 * UNKNOWN can keep a current window_end while OM as-of is old.
 */
function isIncrementalFreshnessStale(params: {
  producerStaleness: boolean | null | undefined;
  queryAsOf: string | null | undefined;
  windowEnd: string | null | undefined;
  expectedRefreshMs: number | null;
  timeZone?: string | null;
}): boolean {
  if (params.producerStaleness === true) return true;
  const endTs = Date.parse(String(params.windowEnd || ''));
  if (
    !Number.isFinite(endTs)
    || params.expectedRefreshMs == null
    || params.expectedRefreshMs <= 0
  ) {
    return false;
  }
  const openStartIso = openSitePt15mWindowStart(params.queryAsOf, params.timeZone);
  const openStartTs = openStartIso ? Date.parse(openStartIso) : Number.NaN;
  if (Number.isFinite(openStartTs)) {
    return endTs < openStartTs - params.expectedRefreshMs;
  }
  const queryTs = Date.parse(String(params.queryAsOf || ''));
  if (!Number.isFinite(queryTs)) return false;
  return Math.max(0, queryTs - endTs) > params.expectedRefreshMs;
}

/**
 * Canonical Current-window freshness for Overview (shared by Current band and
 * Measurement health). Prefer readStale over raw activity/window age so a
 * just-closed window with producer lag cannot read as "fresh".
 */
export function formatOverviewFreshnessLabel(params: {
  ageLabel: string | null | undefined;
  readStale: boolean | null | undefined;
}): string | null {
  if (params.readStale) return 'Older than expected refresh';
  const age = String(params.ageLabel || '').trim();
  if (!age || age === 'Unavailable') return null;
  return age;
}

function formatRatePerHour(value: number | null | undefined): string | null {
  return typeof value === 'number' && Number.isFinite(value)
    ? `${formatCustomerNumber(value)} / hour`
    : null;
}

/**
 * Shared display precision for a related hours set (usable / expected /
 * shortfall parts). Site-scale values (≥10h) use 1 decimal; smaller windows
 * keep 2 — never mix precisions inside one hand-checkable group.
 */
function hoursDisplayDecimals(
  ...values: Array<number | null | undefined>
): number {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value) && Math.abs(value) >= 10) {
      return 1;
    }
  }
  return 2;
}

/** Integer display units (tenths / hundredths) so hand-check sums stay exact. */
function hoursToDisplayUnits(value: number, decimals: number): number {
  return Math.round((value + Number.EPSILON) * (10 ** decimals));
}

function displayUnitsToHours(units: number, decimals: number): number {
  return units / (10 ** decimals);
}

function roundHoursForDisplay(value: number, decimals: number): number {
  return displayUnitsToHours(hoursToDisplayUnits(value, decimals), decimals);
}

function formatHoursAtDecimals(value: number, decimals: number): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: decimals,
  }).format(roundHoursForDisplay(value, decimals));
}

function formatHoursPlain(value: number): string {
  return formatHoursAtDecimals(value, hoursDisplayDecimals(value));
}

/** Usable + expected at one shared precision for hand-checkable coverage labels. */
function formatHoursCoveragePair(usable: number, expected: number): string {
  const decimals = hoursDisplayDecimals(usable, expected);
  return `${formatHoursAtDecimals(roundHoursForDisplay(usable, decimals), decimals)} of ${formatHoursAtDecimals(roundHoursForDisplay(expected, decimals), decimals)}`;
}

function formatCompactDurationFromHours(hours: number): string {
  const seconds = Math.max(0, Math.round(hours * 3600));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes <= 0) return `${remainder}s`;
  return remainder > 0 ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

/** Buyer NOW coverage — 15-minute window usable time, never scheduled-hours. */
export function formatNowMeasuredWindowCoverage(
  usableHours: number | null | undefined,
  expectedHours: number | null | undefined,
): string | null {
  const usable = finiteCount(usableHours);
  const expected = finiteCount(expectedHours);
  if (usable == null || expected == null || expected <= 0) return null;
  const expectedSeconds = Math.round(expected * 3600);
  const usableLabel = formatCompactDurationFromHours(usable);
  if (expectedSeconds >= 840 && expectedSeconds <= 960) {
    return `${usableLabel} usable of 15 min measured window`;
  }
  if (expected <= 1) {
    return `${usableLabel} usable of ${Math.round(expected * 60)} min measured window`;
  }
  return null;
}

function finiteCount(value: number | null | undefined): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function reviewPopulationCount(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): number | null {
  if (!coverage) return null;
  const itemCount = finiteCount(coverage.itemCount);
  if (itemCount != null) return itemCount;
  const confirmed = finiteCount(coverage.confirmedCount);
  const rejected = finiteCount(coverage.rejectedCount);
  const pending = finiteCount(coverage.pendingReviewCount);
  if (confirmed == null || rejected == null || pending == null) return null;
  return confirmed + rejected + pending;
}

function reviewDispositionedCount(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): number | null {
  if (!coverage) return null;
  const confirmed = finiteCount(coverage.confirmedCount);
  const rejected = finiteCount(coverage.rejectedCount);
  if (confirmed == null || rejected == null) return null;
  return confirmed + rejected;
}

function hasEnumerableReviewSet(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  if (!coverage) return false;
  const blocked = coverage.blockedBy || [];
  if (
    blocked.includes('no_enumerable_set')
    || blocked.includes('review_boundary_unavailable')
  ) {
    return false;
  }
  if (coverage.reviewSetId) return true;
  if (coverage.reviewWindowState) return true;
  if (finiteCount(coverage.pendingReviewCount) != null) return true;
  return reviewPopulationCount(coverage) != null;
}

function formatReviewWindowStateLabel(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): string | null {
  if (!coverage) return null;
  const state = String(coverage.reviewWindowState || '').trim().toUpperCase();
  if (state === 'OPEN_PENDING') return 'Open · pending review';
  if (state === 'CLOSED_COMPLETE') return 'Closed · complete';
  if (state === 'CLOSED_NULL_REVIEW') return 'Closed · null review';
  if (!hasEnumerableReviewSet(coverage)) return REVIEW_LIST_NOT_OPENED;
  return null;
}

function formatReviewProgressLabel(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): string | null {
  if (!coverage) return null;
  if (!hasEnumerableReviewSet(coverage)) {
    return REVIEW_LIST_NOT_OPENED;
  }
  const dispositioned = reviewDispositionedCount(coverage);
  const population = reviewPopulationCount(coverage);
  if (dispositioned == null || population == null) {
    return REVIEW_LIST_NOT_OPENED;
  }
  // Amendment B — empty review set · complete is forbidden; null-review or attestation only.
  if (population === 0) {
    const state = String(coverage.reviewWindowState || '').toUpperCase();
    if (state === 'CLOSED_NULL_REVIEW') return 'Closed · null review';
    return REVIEW_LIST_NOT_OPENED;
  }
  return `${dispositioned} of ${population} dispositioned`;
}

function formatOldestPendingLabel(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  queryAsOf: string,
): string | null {
  if (!coverage) return null;
  if (!hasEnumerableReviewSet(coverage)) {
    return REVIEW_LIST_NOT_OPENED;
  }
  const pending = finiteCount(coverage.pendingReviewCount);
  if (pending == null) return REVIEW_LIST_NOT_OPENED;
  if (pending <= 0) return 'None pending';
  if (!coverage.oldestPendingItemTime) {
    return `${pending} pending`;
  }
  return `${pending} pending; oldest ${formatMeasurementAge(queryAsOf, coverage.oldestPendingItemTime)}`;
}

function hasAttributedShortfallBreakdown(
  breakdown: SiteConditionMeasurementCoverage['shortfallReasonBreakdown'] | null | undefined,
): boolean {
  return Object.entries(breakdown || {}).some(([code, value]) => {
    const normalized = String(code || '').trim().toLowerCase();
    if (!normalized || normalized === 'unknown') return false;
    return value.shortfallHours == null || value.shortfallHours > 0;
  });
}

function formatBlockedByLabel(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): string | null {
  const blockers = (coverage?.blockedBy || [])
    .map((code) => String(code || '').trim().toLowerCase())
    .filter(Boolean);
  if (blockers.length === 0) return null;
  const labels = blockers.map((code) => {
    if (code === 'coverage') return 'coverage';
    if (code === 'review') return 'review';
    if (code === 'no_enumerable_set') return 'formal review list not opened';
    if (code === 'review_boundary_unavailable') return 'review boundary unavailable';
    return code.replace(/[_-]+/g, ' ');
  });
  return labels.join(', ');
}

function reviewBlockerLabel(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  queryAsOf: string,
): string | null {
  if (!coverage) return null;
  const pending = coverage.pendingReviewCount;
  const reviewBlocked = coverage.reviewGatePassed === false
    || (pending != null && pending > 0)
    || (coverage.blockedBy || []).some((code) => (
      code === 'review'
      || code === 'no_enumerable_set'
      || code === 'review_boundary_unavailable'
    ));
  const coverageBlocked = coverage.coverageGatePassed === false
    || coverage.blockedBy?.includes('coverage') === true;
  if (!reviewBlocked && !coverageBlocked) return null;
  const windowLabel = formatReviewWindowStateLabel(coverage);
  const progressLabel = formatReviewProgressLabel(coverage);
  // One shared absence line when no formal review list exists — do not repeat
  // the same “not opened / not enumerable” phrase across progress fields.
  const reviewText = reviewBlocked
    ? (
      pending != null && pending > 0
        ? `Review pending: ${pending} item${pending === 1 ? '' : 's'}`
          + (coverage.oldestPendingItemTime
            ? `; oldest ${formatMeasurementAge(queryAsOf, coverage.oldestPendingItemTime)}`
            : '')
        : !hasEnumerableReviewSet(coverage)
          ? REVIEW_LIST_NOT_OPENED
          : windowLabel || progressLabel || 'Review gate not met'
    )
    : null;
  const coverageText = coverageBlocked ? 'Coverage gate not met' : null;
  if (!hasEnumerableReviewSet(coverage) && reviewBlocked) {
    return [coverageText, REVIEW_LIST_NOT_OPENED].filter(Boolean).join('; ') || null;
  }
  const parts = [
    coverageText,
    reviewText,
    windowLabel && reviewBlocked ? `Window: ${windowLabel}` : null,
    progressLabel && reviewBlocked ? progressLabel : null,
  ].filter(Boolean);
  return Array.from(new Set(parts)).join('; ') || null;
}

function formatCurrentThrough(params: {
  coverage: SiteConditionMeasurementCoverage | null | undefined;
  asOf: string | null | undefined;
  timeZone?: string | null;
}): string {
  const timeZone = params.timeZone ?? siteTimeZoneFromCoverage(params.coverage);
  const windowEnd = params.coverage?.frozenBasis?.windowEnd;
  if (windowEnd) return formatCustomerTime(windowEnd, timeZone);
  return formatCustomerTime(params.asOf, timeZone) || '—';
}

function canPresentClaimRate(params: {
  measurementStatus: string | null | undefined;
  producerStaleness: boolean | null | undefined;
  readStale: boolean;
  coverageGatePassed?: boolean | null;
  reviewGatePassed?: boolean | null;
}): boolean {
  // Claim-bearing rate: both gates must pass. Absent gate fields keep legacy
  // MET presentation; explicit null/false withholds the reviewed rate.
  const coverageOk = params.coverageGatePassed === undefined
    || params.coverageGatePassed === true;
  const reviewOk = params.reviewGatePassed === undefined
    || params.reviewGatePassed === true;
  return String(params.measurementStatus || '').trim().toUpperCase() === 'MET'
    && coverageOk
    && reviewOk
    && params.producerStaleness !== true
    && !params.readStale;
}

/** Current/operational surfaces — review gate is not required. */
function canPresentOperationalRate(params: {
  producerStaleness: boolean | null | undefined;
  readStale: boolean;
  usableHours: number | null | undefined;
  activityCount: number | null;
  coverageState: OverviewNowCoverageState | null;
  coverageReconciliationInvalid?: boolean;
}): boolean {
  const usable = finiteCount(params.usableHours);
  if (usable == null || usable <= 0) return false;
  if (params.activityCount == null || params.activityCount < 0) return false;
  if (params.producerStaleness === true || params.readStale) return false;
  if (params.coverageState === 'insufficient_history_no_basis') return false;
  // EPIC284 Task 3.3 — fail closed when partitions do not reconcile.
  if (params.coverageReconciliationInvalid) return false;
  return true;
}

const COVERAGE_RECONCILIATION_UNAVAILABLE_COPY =
  'Operational rate unavailable because the measured-time breakdown could not be reconciled.';

function resolveOperationalCurrentPresentation(
  window: SiteConditionMeasurementWindow | null | undefined,
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  params: {
    producerStaleness: boolean | null | undefined;
    readStale: boolean;
    coverageState: OverviewNowCoverageState | null;
  },
): { rateLabel: string | null; measurementType: MeasurementValueType | null } {
  const activityCount = supportingActivityTotal(window);
  const measurement = resolveOperationalCurrentMeasurement({
    activityCount,
    usableHours: coverage?.usableHours,
  });
  if (!measurement) {
    return { rateLabel: null, measurementType: null };
  }
  // Task 5.2 — switch on discriminant; throw on claim-bearing types (no fallback).
  assertOperationalMeasurement(measurement);
  if (
    !canPresentOperationalRate({
      producerStaleness: params.producerStaleness,
      readStale: params.readStale,
      usableHours: coverage?.usableHours,
      activityCount,
      coverageState: params.coverageState,
      coverageReconciliationInvalid: coverage?.coverageReconciliationInvalid,
    })
  ) {
    // Freshness/usable withhold rate, but activity remains an operational signal.
    return {
      rateLabel: null,
      measurementType: 'OPERATIONAL_ACTIVITY',
    };
  }
  if (measurement.type === 'OPERATIONAL_RATE') {
    return {
      // Amendment A — compute type, but withhold rate display until Tasks 2+3.
      rateLabel: OPERATIONAL_RATE_DISPLAY_ENABLED
        ? formatRatePerHour(measurement.ratePerHour)
        : null,
      measurementType: 'OPERATIONAL_RATE',
    };
  }
  return {
    rateLabel: null,
    measurementType: 'OPERATIONAL_ACTIVITY',
  };
}

/** Claim-bearing numerator only when both gates pass (or legacy absent gates). */
function resolveClaimMeasurementValue(params: {
  rawValue: number | null | undefined;
  measurementStatus: string | null | undefined;
  producerStaleness: boolean | null | undefined;
  readStale: boolean;
  coverageGatePassed?: boolean | null;
  reviewGatePassed?: boolean | null;
}): number | null {
  const raw = params.rawValue;
  if (raw == null || !Number.isFinite(raw)) return null;
  if (
    params.coverageGatePassed === undefined
    && params.reviewGatePassed === undefined
  ) {
    return raw;
  }
  if (!canPresentClaimRate({
    measurementStatus: params.measurementStatus,
    producerStaleness: params.producerStaleness,
    readStale: params.readStale,
    coverageGatePassed: params.coverageGatePassed,
    reviewGatePassed: params.reviewGatePassed,
  })) {
    return null;
  }
  return raw;
}

function formatCadenceContext(params: {
  updateMode: string | null;
  scheduleCadence: string | null | undefined;
  nextTriggerDisplay: string | null | undefined;
}): string {
  if (params.updateMode === 'incremental') {
    return 'Incremental updates';
  }
  const cadence = String(params.scheduleCadence || '').trim();
  if (cadence) {
    const words = cadence
      .toLowerCase()
      .split(/[_\s-]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1));
    return `${words.join(' ')} scheduled scan cadence`;
  }
  if (/not scheduled/i.test(String(params.nextTriggerDisplay || ''))) {
    return 'Not scheduled';
  }
  if (params.updateMode === 'scan_only') {
    return 'Scheduled scans';
  }
  return 'Unavailable';
}

/** Wall-clock day cannot exceed ~24h for one camera; higher values are aggregates. */
function isSiteAggregateHours(expectedHours: number | null | undefined): boolean {
  return typeof expectedHours === 'number'
    && Number.isFinite(expectedHours)
    && expectedHours > 24;
}

function formatScheduledHoursCoverage(
  usable: number,
  expected: number,
): string {
  const nowCopy = formatNowMeasuredWindowCoverage(usable, expected);
  if (nowCopy) return `Coverage limited — ${nowCopy}`;
  const base = `Coverage limited — ${formatHoursCoveragePair(usable, expected)} scheduled hours usable`;
  return isSiteAggregateHours(expected)
    ? `${base} (site aggregate across cameras/conditions)`
    : base;
}

/** Coverage fraction for the shared band — usable of scheduled, never a rate. */
export function formatCoverageFractionLabel(params: {
  usableHours?: number | null;
  expectedHours?: number | null;
  /** Multi-camera / multi-condition panels always label as site aggregate. */
  forceSiteAggregate?: boolean;
}): string | null {
  const usable = finiteCount(params.usableHours);
  const expected = finiteCount(params.expectedHours);
  if (usable == null || expected == null) return null;
  const base = `${formatHoursCoveragePair(usable, expected)} scheduled hours`;
  if (params.forceSiteAggregate || isSiteAggregateHours(expected)) {
    // Explicit site-wide wording — never a per-condition episode denominator.
    return `${base} (site-wide coverage)`;
  }
  return base;
}

/** Condition-row state chip — supporting posture, never claim/rate wording. */
export function resolveConditionStateChipLabel(params: {
  coverageState?: OverviewNowCoverageState | null;
  producerStaleness?: boolean | null;
  supportingActivityLabel?: string | null;
  measurementValue?: number | null;
  measurementState?: string | null;
}): string {
  if (params.coverageState === 'not_met') return 'Coverage limited';
  if (params.coverageState === 'insufficient_history_no_basis') return 'No coverage basis';
  if (
    params.coverageState === 'insufficient_history_accumulating'
    || params.coverageState === 'insufficient_history_unknown'
  ) {
    return 'Coverage incomplete';
  }
  if (params.producerStaleness === true) return 'Producer behind';
  const activity = String(params.supportingActivityLabel || '');
  if (/^0\b/.test(activity) || params.measurementValue === 0) return 'No activity';
  if (activity || (params.measurementValue != null && params.measurementValue > 0)) {
    return 'Activity observed';
  }
  const state = String(params.measurementState || '').trim();
  return state || 'No reading';
}

/** Count beside its denominator when both surfaces are present. */
export function formatCountWithDenominatorLabel(params: {
  supportingActivityLabel?: string | null;
  resultLabel?: string | null;
  usableHours?: number | null;
  expectedHours?: number | null;
  /**
   * When true, keep only the per-condition count — site aggregate usable/scheduled
   * hours belong on the Current shared band once (Brief shared-facts-once).
   */
  omitSharedHours?: boolean;
}): string | null {
  const count = String(
    params.supportingActivityLabel
    || (
      params.resultLabel
      && params.resultLabel !== 'Not available'
        ? params.resultLabel
        : ''
    )
    || '',
  ).trim();
  if (!count) return null;
  if (params.omitSharedHours) return count;
  const usable = finiteCount(params.usableHours);
  const expected = finiteCount(params.expectedHours);
  // Never attach site-aggregate hours beside a per-condition episode count
  // (would read as "647 episodes in 12.2 hours").
  if (isSiteAggregateHours(expected)) return count;
  if (usable != null && expected != null) {
    return `${count} · ${formatHoursCoveragePair(usable, expected)} hours`;
  }
  if (usable != null) {
    return `${count} · ${formatHoursPlain(usable)} usable hours`;
  }
  return count;
}

/**
 * True when usable/scheduled hours are a site aggregate that must appear once
 * on the Current shared band — not on every condition row / count chip.
 */
export function shouldOmitSharedSiteAggregateHours(
  rows: Array<{
    windowUsableHours?: number | null;
    windowExpectedHours?: number | null;
    cameraId?: string | null;
  }>,
): boolean {
  if (!rows.length) return false;
  const withExpected = rows.filter(
    (row) => finiteCount(row.windowExpectedHours) != null,
  );
  if (withExpected.length === 0) return false;
  const firstExpected = finiteCount(withExpected[0].windowExpectedHours);
  const firstUsable = finiteCount(withExpected[0].windowUsableHours);
  if (firstExpected == null) return false;
  if (isSiteAggregateHours(firstExpected)) return true;
  const distinctCameras = new Set(
    rows.map((row) => String(row.cameraId || '').trim()).filter(Boolean),
  );
  if (distinctCameras.size > 1) {
    // Multi-camera panel: identical hours across rows are the site aggregate.
    return withExpected.every((row) => (
      finiteCount(row.windowExpectedHours) === firstExpected
      && finiteCount(row.windowUsableHours) === firstUsable
    ));
  }
  // Multi-condition same camera with identical hours — still shared once.
  if (rows.length > 1) {
    return withExpected.every((row) => (
      finiteCount(row.windowExpectedHours) === firstExpected
      && finiteCount(row.windowUsableHours) === firstUsable
    ));
  }
  return false;
}

/**
 * Forbidden on any Overview customer-facing string (Brief copy rules).
 * Matches cam_* tokens, demo/test/canary (including inside snake_case), and hashes.
 * Does not match ordinary "N/A" (slash is handled separately for workflow paths).
 */
export const OVERVIEW_FORBIDDEN_CUSTOMER_ID =
  /cam[_-][a-z0-9]+|\bdemo\b|_demo\b|\bdemo_|demo[a-z0-9_]|[a-z0-9]demo\b|\btest\b|_test\b|\btest_|canary|[a-f0-9]{12}/i;

export const OVERVIEW_OBJECT_NAME_UNAVAILABLE = 'Object name unavailable';

/** True when a string is unsafe to show as a customer Overview label. */
export function overviewCustomerStringLooksRaw(value: string | null | undefined): boolean {
  const text = String(value || '').trim();
  if (!text) return true;
  if (/^n\/a$/i.test(text)) return false;
  if (OVERVIEW_FORBIDDEN_CUSTOMER_ID.test(text)) return true;
  // Workflow / group-key paths (pipe or slash segmented technical ids).
  if (
    /[|/]/.test(text)
    && (
      /[|/].*[|/]/.test(text)
      || /cam[_-]|demo|zone_violation|near_miss|forklift/i.test(text)
    )
  ) {
    return true;
  }
  // Compact technical tokens / path fragments without spaces.
  if (!/\s/.test(text) && (/[_/|]/.test(text) || /^[a-z]+[-_][a-z0-9_-]+$/i.test(text))) {
    return true;
  }
  // Truncated / typo technical stubs (e.g. "manuall-").
  if (/^[a-z]{3,}[-_]$/i.test(text)) return true;
  return false;
}

/**
 * Resolve Controls Attention / workflow source labels into a customer object line.
 * Never returns raw workflow ids, cam_* paths, or peer-site buyer nicknames
 * (Warehouse #2). Place segment uses location API area_name / mount_label.
 */
export function resolveOverviewActionObjectLabel(
  raw: string | null | undefined,
  lookups: SafetyLocationLookups = {},
): string {
  const text = String(raw || '').trim();
  if (!text) return OVERVIEW_OBJECT_NAME_UNAVAILABLE;
  if (!overviewCustomerStringLooksRaw(text) && !isPeerSiteBuyerAlias(text)) {
    return stripPeerSiteFromCustomerLabel(text);
  }

  // Strip leading "Safety Case" / "Safety Case activity" prefixes.
  const withoutPrefix = text
    .replace(/^Safety Case(?:\s+activity)?\s+/i, '')
    .trim();
  const payload = withoutPrefix || text;
  const parts = payload
    .split(/[|/]/)
    .map((part) => String(part || '').trim())
    .filter(Boolean);

  let incidentType: string | null = null;
  let zoneId: string | null = null;
  let cameraId: string | null = null;
  for (const part of parts) {
    const lower = part.toLowerCase();
    if (
      !incidentType
      && (
        lower.includes('zone_violation')
        || lower.includes('zone_intrusion')
        || lower.includes('near_miss')
        || lower.includes('forklift')
        || lower.includes('restricted')
      )
    ) {
      incidentType = part;
      continue;
    }
    if (!cameraId && /^cam[_-]/i.test(part)) {
      cameraId = part;
      continue;
    }
    if (!zoneId && part !== incidentType && !/^cam[_-]/i.test(part)) {
      // Skip trailing device stubs like demo5 / edge1 when a richer zone exists.
      if (/^(demo|edge)\d+$/i.test(part) && parts.length > 2) continue;
      zoneId = part;
    }
  }

  // Bare camera id as the whole label.
  if (!incidentType && !zoneId && /^cam[_-]/i.test(payload)) {
    cameraId = payload;
  }

  const patternLabel = incidentType
    ? buyerPatternDisplayLabel({
      patternLabel: incidentType.replace(/[_-]+/g, ' '),
      incidentType: mapConditionFamilyToIncidentType(incidentType) || incidentType,
    })
    : null;
  // Place comes from location API SSOT (area_name / mount_label). Trust that
  // helper — do not re-apply id/demo forbid heuristics that drop registry mounts.
  const placeLabel = resolveCustomerPlaceLabel(
    { camera_id: cameraId, zone_id: zoneId },
    lookups,
  );
  const placeResolved = Boolean(
    placeLabel
    && placeLabel !== '—'
    && !isPeerSiteBuyerAlias(placeLabel),
  );

  const objectBits: string[] = [];
  if (patternLabel && !overviewCustomerStringLooksRaw(patternLabel)) {
    objectBits.push(patternLabel);
  }
  if (placeResolved && placeLabel) objectBits.push(placeLabel);

  if (objectBits.length === 0) return OVERVIEW_OBJECT_NAME_UNAVAILABLE;
  return objectBits.map((bit) => collapseOverviewPlaceLabel(bit)).join(' · ');
}

function stripPeerSiteFromCustomerLabel(value: string): string {
  if (!isPeerSiteBuyerAlias(value) && !/Warehouse\s*(?:Room)?\s*#?\s*\d+/i.test(value)) {
    return value;
  }
  // Drop peer-site segments from already-human labels.
  const cleaned = value
    .replace(/\s*[·|]\s*Warehouse\s*(?:Room)?\s*#?\s*\d+/gi, '')
    .replace(/\bWarehouse\s*(?:Room)?\s*#?\s*\d+\b/gi, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s*[·|]\s*$/g, '')
    .trim();
  return cleaned || OVERVIEW_OBJECT_NAME_UNAVAILABLE;
}

/** Strip leading "Camera " so place-name equality is content-based. */
export function overviewPlaceNameKey(label: string | null | undefined): string {
  return String(label || '')
    .replace(/^camera\s+/i, '')
    .trim()
    .toLowerCase();
}

export function overviewPlaceNamesMatch(
  left: string | null | undefined,
  right: string | null | undefined,
): boolean {
  const a = overviewPlaceNameKey(left);
  const b = overviewPlaceNameKey(right);
  return Boolean(a && b && a === b);
}

/** Prefer bare place name over "Camera <same place>" in Actions object lines. */
function collapseOverviewPlaceLabel(label: string): string {
  const text = String(label || '').trim();
  if (/^camera\s+/i.test(text)) {
    const bare = text.replace(/^camera\s+/i, '').trim();
    if (bare) return bare;
  }
  return text;
}

/**
 * Collect every customer-facing string the Overview decision summary can render.
 * Used by Brief acceptance: no raw identifier may appear in this set.
 */
export function collectOverviewCustomerFacingStrings(
  summary: OverviewDecisionSummary,
): string[] {
  const values: Array<string | null | undefined> = [
    summary.now.answer,
    summary.now.supportLine,
    summary.now.coverageLabel,
    summary.now.coverageShortfallReason,
    summary.now.rateLabel,
    summary.now.resultLabel,
    summary.now.supportingActivityLabel,
    summary.now.provisionalGroupingCaveat,
    summary.now.blockedByLabel,
    summary.now.dashboardAsOfLabel,
    summary.now.measuredWindow,
    summary.now.lastCompleted,
    summary.now.ageLabel,
    summary.now.freshnessLabel,
    summary.now.nextUpdate,
    summary.now.cadenceContext,
    summary.now.expectedRefreshLabel,
    summary.now.telemetryOutageLabel,
    summary.overTime.answer,
    summary.overTime.supportLine,
    summary.overTime.observedValueLabel,
    summary.overTime.provisionalGroupingCaveat,
    summary.overTime.coverageBasisLabel,
    summary.overTime.coverageRateLabel,
    summary.overTime.historyProgress,
    summary.overTime.historySpanNote,
    summary.overTime.historyTrustNote,
    summary.overTime.periodFacts,
    summary.overTime.asOfLabel,
    summary.overTime.windowLabel,
    summary.overTime.windowScopeLabel,
    summary.overTime.stateLabel,
    ...(summary.overTime.measurementDetailLines || []),
    summary.sinceControl.answer,
    summary.sinceControl.supportLine,
    summary.sinceControl.followUpState,
    summary.sinceControl.observedResult,
    summary.sinceControl.variationCaveat,
    summary.sinceControl.provenanceLine,
    summary.sinceControl.basisDifferedLabel,
    summary.sinceControl.actionLabel,
    ...(summary.sinceControl.comparisonSides || []).flatMap((side) => [
      side.label,
      side.rateLabel,
      side.bodyLine,
    ]),
    summary.siteComposition,
    summary.measurementHealth.cameraViewsLabel,
    summary.measurementHealth.coverageOwnedByCurrentBand
      ? null
      : summary.measurementHealth.coverageLabel,
    summary.measurementHealth.affectedCamerasLabel,
    summary.measurementHealth.producerCatchUpLabel,
    summary.measurementHealth.freshnessLabel,
    summary.measurementHealth.unattributedLabel,
    ...summary.measurementHealth.shortfallLines.map(
      (line) => `${line.reason} ${line.hoursLabel}`,
    ),
    ...summary.measurementHealth.issues.map((issue) => (
      issue.siteScoped
        ? issue.label
        : `${issue.label} — ${issue.conditionLabel}`
    )),
    ...summary.decisionActions.flatMap((row) => [
      row.objectLabel,
      row.detail,
      row.verb,
    ]),
    ...summary.now.conditionRows.flatMap((row) => [
      row.label,
      row.location,
      row.stateChipLabel,
      row.countWithDenominatorLabel,
      row.bodyLine,
      row.coverageLabel,
      row.coverageShortfallReason,
      row.resultLabel,
      row.supportingActivityLabel,
      row.provisionalGroupingCaveat,
      row.rateLabel,
    ]),
    ...summary.overTime.conditionRows.flatMap((row) => [
      row.label,
      row.location,
      row.measuredPeriodsLabel,
      row.comparisonStatus,
      row.supportingActivityLabel,
    ]),
  ];
  return values.filter((value): value is string => typeof value === 'string' && value.length > 0);
}

/** Brief Slice 2 — Current panel presentation kinds (claim vs facts stay separate). */
export type CurrentPresentationKind =
  | 'rate_issuable'
  | 'coverage_blocks'
  | 'review_blocks'
  | 'both_block'
  | 'verified_absence'
  | 'no_basis'
  | 'default';

function coverageBlocksCurrentClaim(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  coverageState: OverviewNowCoverageState | null | undefined,
): boolean {
  if (!coverage) return false;
  if (coverageState === 'insufficient_history_no_basis') return false;
  if (coverage.coverageReconciliationInvalid) return true;
  if (coverage.coverageGatePassed === false) return true;
  if ((coverage.blockedBy || []).some((code) => String(code).toLowerCase() === 'coverage')) {
    return true;
  }
  return (
    coverageState === 'not_met'
    || coverageState === 'insufficient_history_accumulating'
    || coverageState === 'insufficient_history_unknown'
  );
}

function reviewBlocksCurrentClaim(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  if (!coverage) return false;
  if (coverage.reviewGatePassed === false) return true;
  const pending = finiteCount(coverage.pendingReviewCount);
  if (pending != null && pending > 0) return true;
  return (coverage.blockedBy || []).some((code) => {
    const normalized = String(code || '').trim().toLowerCase();
    return (
      normalized === 'review'
      || normalized === 'no_enumerable_set'
      || normalized === 'review_boundary_unavailable'
    );
  });
}

function formatReviewedObservationsLabel(count: number): string {
  const label = formatCustomerNumber(count);
  return count === 1 ? `${label} reviewed observation` : `${label} reviewed observations`;
}

function formatUsableWindowBesideCount(params: {
  countLabel: string;
  usableHours?: number | null;
  expectedHours?: number | null;
}): string {
  const usable = finiteCount(params.usableHours);
  const expected = finiteCount(params.expectedHours);
  if (usable != null && expected != null) {
    return `${params.countLabel} · ${formatHoursCoveragePair(usable, expected)} usable hours`;
  }
  if (usable != null) {
    return `${params.countLabel} · ${formatHoursPlain(usable)} usable hours`;
  }
  return params.countLabel;
}

function formatCurrentCoverageBlocksHeadline(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  coverageState: OverviewNowCoverageState | null | undefined,
): string {
  const reason = formatOverviewCoverageReasonLead(coverage, coverageState || null);
  const usable = finiteCount(coverage?.usableHours);
  const expected = finiteCount(coverage?.expectedHours);
  const hoursBit = (
    usable != null
    && expected != null
    && !isSiteAggregateHours(expected)
  )
    ? `${formatHoursCoveragePair(usable, expected)} scheduled hours usable so far`
    : null;
  // Calm progress headline for generic incomplete/unknown coverage — never
  // customer-hostile "trustworthy coverage rate cannot be computed".
  if (
    reason
    && /Measurement is still in progress for this window|Some coverage intervals are still being classified/i.test(
      reason,
    )
  ) {
    if (hoursBit) {
      return `Measurement in progress — ${hoursBit}. Rate withheld · insufficient usable coverage.`;
    }
    return `${reason.replace(/\.\s*$/, '')}.`;
  }
  if (reason) {
    const trimmed = reason.replace(/\.\s*$/, '');
    return `${trimmed}. Rate withheld · insufficient usable coverage.`;
  }
  return 'Rate withheld · insufficient usable coverage.';
}

function humanDispositionBlocksReview(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): boolean {
  const pending = finiteCount(coverage?.pendingReviewCount);
  return pending != null && pending > 0;
}

function formatPendingReviewDecisionLead(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): string | null {
  const pending = finiteCount(coverage?.pendingReviewCount);
  if (pending == null || pending <= 0) return null;
  return pending === 1
    ? 'One item still needs a review decision before a rate can finalize'
    : `${pending} items still need a review decision before a rate can finalize`;
}

function formatCurrentReviewBlocksHeadline(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): string {
  const decisionLead = formatPendingReviewDecisionLead(coverage);
  if (decisionLead) return `${decisionLead}.`;
  if (!hasEnumerableReviewSet(coverage)) {
    return 'Review cannot finish because the formal review list is not open for this window.';
  }
  return 'Review is blocking a rate for this measured window.';
}

function formatCurrentBothBlockHeadline(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  coverageState: OverviewNowCoverageState | null | undefined,
): string {
  const coverageReason = formatOverviewCoverageReasonLead(coverage, coverageState || null);
  const coverageLead = coverageReason
    ? coverageReason.replace(/\.\s*$/, '')
    : 'Coverage did not meet the expected monitored time';
  const decisionLead = formatPendingReviewDecisionLead(coverage);
  // Only claim review when a human disposition is actually pending.
  return decisionLead ? `${coverageLead}. ${decisionLead}.` : `${coverageLead}.`;
}

function formatCurrentCoverageBlocksBody(params: {
  supportingActivityLabel?: string | null;
  coverage: SiteConditionMeasurementCoverage | null | undefined;
  coverageState: OverviewNowCoverageState | null | undefined;
}): string {
  const usable = finiteCount(params.coverage?.usableHours);
  const expected = finiteCount(params.coverage?.expectedHours);
  // Site-aggregate hours belong on the Current shared band as site-wide coverage.
  // Never put them beside a per-condition episode count in the support line.
  const hours = isSiteAggregateHours(expected)
    ? null
    : usable != null && expected != null
      ? `${formatHoursCoveragePair(usable, expected)} scheduled hours usable`
      : usable != null
        ? `${formatHoursPlain(usable)} usable hours`
        : null;
  // Coverage reason lives once on the Current headline (or shared band) —
  // do not repeat it in the support line. Middle dots keep blockers / count
  // / hours from reading as one run-on sentence.
  return [
    'Blocked by coverage',
    params.supportingActivityLabel,
    hours,
  ].filter(Boolean).join(' · ');
}

function formatCurrentReviewBlocksBody(params: {
  supportingActivityLabel?: string | null;
  coverage: SiteConditionMeasurementCoverage | null | undefined;
  queryAsOf: string;
}): string {
  const usable = finiteCount(params.coverage?.usableHours);
  const expected = finiteCount(params.coverage?.expectedHours);
  const hours = isSiteAggregateHours(expected)
    ? null
    : usable != null
      ? `${formatHoursPlain(usable)} usable hours`
      : null;
  const progress = formatReviewProgressLabel(params.coverage);
  const oldest = formatOldestPendingLabel(params.coverage, params.queryAsOf);
  const oldestFact = oldest && oldest !== 'None pending' && oldest !== REVIEW_LIST_NOT_OPENED
    ? `Oldest pending: ${oldest}`
    : oldest === REVIEW_LIST_NOT_OPENED
      ? REVIEW_LIST_NOT_OPENED
      : null;
  return [
    'Blocked by review',
    params.supportingActivityLabel,
    hours,
    progress && progress !== REVIEW_LIST_NOT_OPENED ? progress : null,
    oldestFact,
  ].filter(Boolean).join(' · ');
}

function formatCurrentBothBlockBody(params: {
  supportingActivityLabel?: string | null;
  coverage: SiteConditionMeasurementCoverage | null | undefined;
  coverageState: OverviewNowCoverageState | null | undefined;
  queryAsOf: string;
}): string {
  const coverageBody = formatCurrentCoverageBlocksBody({
    supportingActivityLabel: params.supportingActivityLabel,
    coverage: params.coverage,
    coverageState: params.coverageState,
  });
  const progress = formatReviewProgressLabel(params.coverage);
  const oldest = formatOldestPendingLabel(params.coverage, params.queryAsOf);
  const reviewBits = [
    'Blocked by review',
    progress && progress !== REVIEW_LIST_NOT_OPENED ? progress : null,
    oldest && oldest !== 'None pending' && oldest !== REVIEW_LIST_NOT_OPENED
      ? `Oldest pending: ${oldest}`
      : oldest === REVIEW_LIST_NOT_OPENED
        ? REVIEW_LIST_NOT_OPENED
        : null,
  ].filter(Boolean).join(' · ');
  return [coverageBody, reviewBits].filter(Boolean).join(' · ');
}

function formatCurrentRateIssuableBody(
  coverage: SiteConditionMeasurementCoverage,
): string {
  // Server-issued rate only — never compute a rate here.
  const rate = formatRatePerHour(coverage.ratePerHour);
  const reviewed = finiteCount(coverage.reviewedObservationCount);
  const usable = finiteCount(coverage.usableHours);
  const beneath = reviewed != null && usable != null
    ? `${formatReviewedObservationsLabel(reviewed)} / ${formatHoursPlain(usable)} usable hours`
    : reviewed != null
      ? formatReviewedObservationsLabel(reviewed)
      : usable != null
        ? `${formatHoursPlain(usable)} usable hours`
        : null;
  return [rate, beneath].filter(Boolean).join('. ');
}

/**
 * Brief Slice 2 — Current panel headline + body by presentation state.
 * Claim surface (rate) and supporting facts use separate gates.
 */
export function resolveCurrentPanelPresentation(params: {
  leadingAnswer: string;
  defaultSupportLine: string;
  coverage: SiteConditionMeasurementCoverage | null | undefined;
  coverageState: OverviewNowCoverageState | null | undefined;
  supportingActivityLabel?: string | null;
  producerStaleness?: boolean | null;
  readStale?: boolean;
  queryAsOf: string;
}): {
  kind: CurrentPresentationKind;
  answer: string;
  supportLine: string;
  rateLabel: string | null;
} {
  const coverage = params.coverage || null;
  const coverageState = params.coverageState || null;
  const coverageBlocked = coverageBlocksCurrentClaim(coverage, coverageState);
  const reviewBlocked = reviewBlocksCurrentClaim(coverage);
  // Customer copy: only treat pending human disposition as "review blocking".
  // Formal list not opened / gate flags without pending items must not claim
  // a review decision is required.
  const dispositionBlocks = humanDispositionBlocksReview(coverage);

  if (coverageState === 'insufficient_history_no_basis') {
    const activity = params.supportingActivityLabel;
    return {
      kind: 'no_basis',
      answer: 'No measurement result yet',
      supportLine: [
        activity,
        'No coverage denominator is available yet.',
      ].filter(Boolean).join(' '),
      rateLabel: null,
    };
  }

  if (coverageBlocked && dispositionBlocks) {
    return {
      kind: 'both_block',
      answer: formatCurrentBothBlockHeadline(coverage, coverageState),
      supportLine: formatCurrentBothBlockBody({
        supportingActivityLabel: params.supportingActivityLabel,
        coverage,
        coverageState,
        queryAsOf: params.queryAsOf,
      }),
      rateLabel: null,
    };
  }

  if (coverageBlocked) {
    return {
      kind: 'coverage_blocks',
      answer: formatCurrentCoverageBlocksHeadline(coverage, coverageState),
      supportLine: formatCurrentCoverageBlocksBody({
        supportingActivityLabel: params.supportingActivityLabel,
        coverage,
        coverageState,
      }),
      rateLabel: null,
    };
  }

  if (dispositionBlocks || reviewBlocked) {
    return {
      kind: 'review_blocks',
      answer: formatCurrentReviewBlocksHeadline(coverage),
      supportLine: formatCurrentReviewBlocksBody({
        supportingActivityLabel: params.supportingActivityLabel,
        coverage,
        queryAsOf: params.queryAsOf,
      }),
      rateLabel: null,
    };
  }

  const serverRate = coverage?.ratePerHour;
  const rateIssuable = (
    typeof serverRate === 'number'
    && Number.isFinite(serverRate)
    && canPresentClaimRate({
      measurementStatus: coverage?.measurementStatus,
      producerStaleness: params.producerStaleness,
      readStale: Boolean(params.readStale),
      coverageGatePassed: coverage?.coverageGatePassed,
      reviewGatePassed: coverage?.reviewGatePassed,
    })
  );
  if (rateIssuable && coverage) {
    return {
      kind: 'rate_issuable',
      answer: params.leadingAnswer,
      supportLine: formatCurrentRateIssuableBody(coverage),
      rateLabel: formatRatePerHour(serverRate),
    };
  }

  const reviewed = finiteCount(coverage?.reviewedObservationCount);
  const usable = finiteCount(coverage?.usableHours);
  if (reviewed === 0 && usable != null && usable > 0) {
    return {
      kind: 'verified_absence',
      answer: 'No reviewed observations in this measured window',
      supportLine: formatUsableWindowBesideCount({
        countLabel: formatReviewedObservationsLabel(0),
        usableHours: usable,
        expectedHours: coverage?.expectedHours,
      }),
      rateLabel: null,
    };
  }

  // Producer-behind is shown in the shared band; facts still render here.
  return {
    kind: 'default',
    answer: params.leadingAnswer,
    supportLine: params.defaultSupportLine,
    rateLabel: null,
  };
}

/** Brief Slice 4 — Since Control presentation kinds. */
export type SinceControlPresentationKind =
  | 'no_linked_control'
  | 'follow_up_open'
  | 'comparison_issuable'
  | 'not_comparable'
  | 'default';

export type SinceControlComparisonSide = {
  label: string;
  rateLabel: string | null;
  bodyLine: string;
  usableHours: number | null;
};

export const SINCE_CONTROL_VARIATION_CAVEAT =
  'One baseline week against the following week cannot separate the site action from normal week-to-week operating variation.';

export const SINCE_CONTROL_PROVENANCE_LINE =
  'Edgentik reports what changed. The site determines why and what to do next.';

export type OverviewMeasurementHealthIssue = {
  id: string;
  label: string;
  conditionLabel: string;
  cameraLabel: string;
  /**
   * When true, this is a site-level collapsed kind line.
   * `cameraLabel` then names cameras or “across measured cameras”.
   */
  siteScoped?: boolean;
  kind?: MeasurementOpenWorkKind;
};

/** Bundle / inject marker for Measurement health kind collapse. */
export const OVERVIEW_HEALTH_COLLAPSE_MARKER = 'overview_health_collapse_v1';

/** Bundle / inject marker for calm Current condition-card hierarchy. */
export const OVERVIEW_CALM_CARDS_MARKER = 'overview_calm_cards_v1';

/** Bundle / inject marker: Actions face shows top priority rows only. */
export const OVERVIEW_ACTIONS_FACE_MARKER = 'overview_actions_face_v1';

/** Bundle / inject marker: quiet Measurement health when cameras available. */
export const OVERVIEW_HEALTH_QUIET_MARKER = 'overview_health_quiet_v1';

/** Bundle / inject marker: lean Recurring withheld/building face. */
export const OVERVIEW_RECURRING_LEAN_MARKER = 'overview_recurring_lean_v1';

/** Max decision rows on the Overview Actions face before Show all / Open Safety Cases. */
export const OVERVIEW_ACTIONS_FACE_LIMIT = 3;

/**
 * Usual coverage/staleness kinds — quiet under View issues when cameras are up.
 * Critical claim/control kinds stay visible.
 */
export const MEASUREMENT_HEALTH_USUAL_COVERAGE_STALE_KINDS: ReadonlySet<MeasurementOpenWorkKind> = new Set<MeasurementOpenWorkKind>([
  'camera_telemetry_gap',
  'unknown_coverage_interval',
  'source_stream_stale',
]);

export const MEASUREMENT_HEALTH_CRITICAL_KINDS: ReadonlySet<MeasurementOpenWorkKind> = new Set<MeasurementOpenWorkKind>([
  'claim_review_incomplete',
  'since_control_comparability_failed',
  'control_recheck_unscheduled',
]);

export type OverviewMeasurementHealthFaceMode = 'quiet' | 'attention';

export type OverviewMeasurementHealth = {
  cameraViewsLabel: string;
  availableCameraCount?: number;
  totalCameraCount?: number;
  /** True when every measured camera view is available (N of N, N > 0). */
  camerasAvailable?: boolean;
  /**
   * `quiet`: lead with calm cameras + integrity; usual coverage/stale issue
   * bullets collapse under View issues. Integrity (shortfall / unattributed /
   * producer / freshness) stays expanded. `attention`: cameras down or critical
   * issues — keep issue bullets visible. Default/missing treats as attention.
   */
  faceMode?: OverviewMeasurementHealthFaceMode;
  /**
   * Usable/scheduled coverage for this window. When site-wide hours already live
   * on the Current shared band, prefer omitting this on the face (see
   * `coverageOwnedByCurrentBand`).
   */
  coverageLabel: string;
  /**
   * True when usable/scheduled hours are a site aggregate that Current already
   * shows once — Measurement health must not repeat the fraction on the face.
   */
  coverageOwnedByCurrentBand?: boolean;
  /** Cameras named once in the health header; issue bullets omit camera repeats. */
  affectedCamerasLabel?: string | null;
  shortfallLines: Array<{ reason: string; hoursLabel: string; hours: number }>;
  shortfallSumHours: number | null;
  expectedMinusUsableHours: number | null;
  shortfallReconciles: boolean;
  producerCatchUpLabel: string | null;
  freshnessLabel: string | null;
  unattributedLabel: string | null;
  /** Raw open-work item count before kind collapse across cameras. */
  rawOpenWorkCount?: number;
  /**
   * Collapse toggle copy — names “types” when kinds are collapsed vs raw items
   * (never a silent filter of 6→3).
   */
  issuesToggleLabel?: string | null;
  issues: OverviewMeasurementHealthIssue[];
};

export type OverviewDecisionActionRow = {
  id: string;
  objectLabel: string;
  verb: 'Review' | 'Decide';
  detail: string | null;
  href: string | null;
  kind: 'pending_review' | 'controls_attention';
};

const BASIS_DIFFERENCE_LABELS: Array<{ match: RegExp; label: string }> = [
  { match: /overlap_dedup|dedup/, label: 'dedup version' },
  { match: /schedule|calendar|shift/, label: 'schedule' },
  { match: /coverage_basis|coverage_policy/, label: 'coverage basis' },
  { match: /scope|camera_set|zone/, label: 'scope' },
  { match: /measurement_epoch|epoch/, label: 'epoch' },
  { match: /numerator|episode_definition|inclusion_policy/, label: 'numerator definition' },
  { match: /measurement_basis|basis_not_comparable|basis_mismatch/, label: 'measurement basis' },
];

function formatComparisonSideBody(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
): SinceControlComparisonSide | null {
  if (!coverage) return null;
  const rateLabel = formatRatePerHour(coverage.ratePerHour);
  const reviewed = finiteCount(coverage.reviewedObservationCount);
  const usable = finiteCount(coverage.usableHours);
  const beneath = reviewed != null && usable != null
    ? `${formatReviewedObservationsLabel(reviewed)} / ${formatHoursPlain(usable)} usable hours`
    : reviewed != null
      ? formatReviewedObservationsLabel(reviewed)
      : usable != null
        ? `${formatHoursPlain(usable)} usable hours`
        : null;
  if (!rateLabel && !beneath) return null;
  return {
    label: '',
    rateLabel,
    bodyLine: [rateLabel, beneath].filter(Boolean).join(' · '),
    usableHours: usable,
  };
}

function formatBasisDifferedLabel(
  window: SiteConditionMeasurementWindow | null | undefined,
): string | null {
  if (!window) return null;
  const codes = [
    ...(window.reasonCodes || []),
    ...((window.summary.measurementCoverage?.blockedBy) || []),
  ].map((code) => String(code || '').trim().toLowerCase()).filter(Boolean);
  const named: string[] = [];
  for (const code of codes) {
    for (const entry of BASIS_DIFFERENCE_LABELS) {
      if (entry.match.test(code) && !named.includes(entry.label)) {
        named.push(entry.label);
      }
    }
  }
  if (named.length === 0) {
    if (codes.some((code) => /not_comparable|comparison_unavailable|cannot_compare/.test(code))) {
      return 'measurement basis';
    }
    return null;
  }
  if (named.length === 1) return named[0];
  if (named.length === 2) return `${named[0]} and ${named[1]}`;
  return `${named.slice(0, -1).join(', ')}, and ${named[named.length - 1]}`;
}

function formatControlNamedSentence(params: {
  hasAnchor: boolean;
  anchorDateLabel: string | null;
  controlLocation?: string | null;
}): string {
  if (!params.hasAnchor) return 'No control recorded';
  const place = String(params.controlLocation || '').trim();
  const date = params.anchorDateLabel && params.anchorDateLabel !== '—'
    ? params.anchorDateLabel
    : null;
  if (date && place) {
    return `Follow-up after the recorded control from ${date} at ${place}.`;
  }
  if (date) return `Follow-up after the recorded control from ${date}.`;
  if (place) return `Follow-up after the recorded control at ${place}.`;
  return 'Follow-up after the recorded control.';
}

function formatObservedRateDirection(
  baselineRate: number | null | undefined,
  followUpRate: number | null | undefined,
): string | null {
  if (
    typeof baselineRate !== 'number'
    || !Number.isFinite(baselineRate)
    || typeof followUpRate !== 'number'
    || !Number.isFinite(followUpRate)
  ) {
    return null;
  }
  if (followUpRate < baselineRate) {
    return 'Observed rate is lower in the latest comparable window';
  }
  if (followUpRate > baselineRate) {
    return 'Observed rate is higher in the latest comparable window';
  }
  return 'Observed rate is unchanged in the latest comparable window';
}

/**
 * Brief Slice 4 — Since Control headline + body by presentation state.
 * Server rates only; never invent baseline/follow-up or average incompatible bases.
 */
export function resolveSinceControlPanelPresentation(params: {
  window: SiteConditionMeasurementWindow | null | undefined;
  linkedConditionCount: number;
  comparisonBlocked: boolean;
  defaultAnswer: string;
  defaultSupportLine: string;
  defaultTone: OverviewAnswerTone;
  controlLocation?: string | null;
}): {
  kind: SinceControlPresentationKind;
  answer: string;
  supportLine: string;
  answerTone: OverviewAnswerTone;
  comparisonSides: SinceControlComparisonSide[] | null;
  variationCaveat: string | null;
  provenanceLine: string | null;
  basisDifferedLabel: string | null;
  actionLabel: string;
  followUpState: string;
  observedResult: string;
  controlLocation: string | null;
  controlDateLabel: string | null;
} {
  const window = params.window || null;
  const hasAnchor = Boolean(window?.windowAnchorId && String(window.windowAnchorId).trim());
  const bucketCount = window
    ? (window.summary.bucketCount || window.buckets.length || 0)
    : 0;
  const coverage = window?.summary.measurementCoverage || null;
  const timeZone = siteTimeZoneFromCoverage(coverage);
  const anchorDateLabel = formatCustomerTime(
    window?.anchorStartedAt || null,
    timeZone,
  );
  const followUpEndLabel = formatCustomerTime(
    coverage?.frozenBasis?.windowEnd
    || window?.operationalMeasurementProvenance?.windowEnd
    || window?.observedThrough
    || window?.displayAsOf
    || null,
    timeZone,
  );
  const controlLocation = String(params.controlLocation || '').trim() || null;
  const controlDateLabel = anchorDateLabel && anchorDateLabel !== '—'
    ? anchorDateLabel
    : null;
  const namedControl = {
    hasAnchor,
    anchorDateLabel,
    controlLocation,
  };

  if (params.linkedConditionCount === 0 || !hasAnchor) {
    return {
      kind: 'no_linked_control',
      answer: 'No control recorded',
      supportLine: 'Record a control to begin follow-up measurement.',
      answerTone: 'unavailable',
      comparisonSides: null,
      variationCaveat: null,
      provenanceLine: null,
      basisDifferedLabel: null,
      actionLabel: 'Record a control',
      followUpState: 'No anchor',
      observedResult: 'Not available',
      controlLocation: null,
      controlDateLabel: null,
    };
  }

  if (bucketCount === 0) {
    return {
      kind: 'follow_up_open',
      answer: formatControlNamedSentence(namedControl),
      supportLine: [
        controlDateLabel ? `Date: ${controlDateLabel}` : 'Control date unavailable',
        controlLocation ? `Location: ${controlLocation}` : null,
        followUpEndLabel && followUpEndLabel !== '—'
          ? `Since control through ${followUpEndLabel}`
          : 'Monitoring continues',
        'More comparable measurement is needed before a comparison is possible.',
      ].filter(Boolean).join('. '),
      answerTone: 'unavailable',
      comparisonSides: null,
      variationCaveat: null,
      provenanceLine: null,
      basisDifferedLabel: null,
      actionLabel: 'Review the comparison',
      followUpState: 'Awaiting follow-up',
      observedResult: 'Not available',
      controlLocation,
      controlDateLabel,
    };
  }

  const claimAllowed = window
    ? sinceControlClaimComparisonAllowed({
      comparisonStatus: window.comparisonStatus,
      coverageGatePassed: coverage?.coverageGatePassed,
      reviewGatePassed: coverage?.reviewGatePassed,
      reviewedObservationCount: coverage?.reviewedObservationCount,
      ratePerHour: coverage?.ratePerHour,
    })
    : false;

  // Limited / blocked comparisons are not issuable claim faces.
  if (claimAllowed && !params.comparisonBlocked && coverage) {
    const followUpSide = formatComparisonSideBody(coverage);
    const baselineSide = formatComparisonSideBody(window?.comparisonBaselineCoverage || null);
    const sides: SinceControlComparisonSide[] = [];
    if (baselineSide) {
      sides.push({ ...baselineSide, label: 'Before control' });
    }
    if (followUpSide) {
      sides.push({ ...followUpSide, label: 'Since control' });
    }
    const baselineHours = baselineSide?.usableHours ?? null;
    const followUpHours = followUpSide?.usableHours ?? null;
    const unequalHours = (
      baselineHours != null
      && followUpHours != null
      && Math.abs(baselineHours - followUpHours) > 0.001
    );
    const changeLine = baselineSide?.rateLabel && followUpSide?.rateLabel
      ? `Change: ${baselineSide.rateLabel} to ${followUpSide.rateLabel}`
      : null;
    const rateDirection = unequalHours
      ? null
      : formatObservedRateDirection(
        window?.comparisonBaselineCoverage?.ratePerHour,
        coverage.ratePerHour,
      );
    const hoursNote = unequalHours
      ? `Usable hours differ (before control ${formatHoursPlain(baselineHours!)} · since control ${formatHoursPlain(followUpHours!)}); this is not an hours-matched comparison.`
      : baselineHours != null && followUpHours != null
        ? `Before control and since control each use ${formatHoursPlain(followUpHours)} usable hours.`
        : null;
    const sideLines = sides.map((side) => `${side.label}: ${side.bodyLine}`);
    return {
      kind: 'comparison_issuable',
      answer: formatControlNamedSentence(namedControl),
      supportLine: [
        controlLocation ? `Location: ${controlLocation}` : null,
        ...sideLines,
        changeLine,
        rateDirection,
        hoursNote,
        SINCE_CONTROL_VARIATION_CAVEAT,
        SINCE_CONTROL_PROVENANCE_LINE,
      ].filter(Boolean).join(' '),
      answerTone: 'available',
      comparisonSides: sides.length > 0 ? sides : null,
      variationCaveat: SINCE_CONTROL_VARIATION_CAVEAT,
      provenanceLine: SINCE_CONTROL_PROVENANCE_LINE,
      basisDifferedLabel: null,
      actionLabel: 'Review the comparison',
      followUpState: 'Complete',
      observedResult: rateDirection || changeLine || followUpSide?.rateLabel || 'Measured change',
      controlLocation,
      controlDateLabel,
    };
  }

  // Not comparable — name which basis differed; never show an averaged result.
  const basisDiffered = formatBasisDifferedLabel(window);
  const basisClause = basisDiffered
    ? `Comparison is not possible because ${basisDiffered} differed.`
    : 'Comparison is not possible on the current measurement basis.';
  return {
    kind: 'not_comparable',
    answer: formatControlNamedSentence(namedControl),
    supportLine: [
      controlLocation ? `Location: ${controlLocation}` : null,
      basisClause,
      'No averaged result is shown.',
      params.comparisonBlocked
        ? 'More comparable measurement is needed on the same basis.'
        : 'Monitoring continues.',
    ].filter(Boolean).join(' '),
    answerTone: 'limited',
    comparisonSides: null,
    variationCaveat: null,
    provenanceLine: null,
    basisDifferedLabel: basisDiffered,
    actionLabel: 'Review the comparison',
    followUpState: 'Not comparable',
    observedResult: 'Not available',
    controlLocation,
    controlDateLabel,
  };
}

function shortfallReasonCustomerLabel(code: string): string {
  const normalized = String(code || '').trim().toLowerCase();
  if (normalized === 'telemetry_missing') return 'Telemetry outage';
  if (normalized === 'partial_analyzable_time') return 'Partial analyzable time';
  if (normalized === 'no_analyzable_time') return 'No analyzable time';
  if (normalized === 'device_scope_unresolved') return 'Device scope unresolved';
  if (normalized === 'insufficient_history') return 'Insufficient history';
  if (normalized === 'unknown' || normalized === 'unknown_analyzable') {
    return 'Unattributed';
  }
  return code.replace(/[_-]+/g, ' ');
}

function parseOpenWorkIssueParts(item: MeasurementOpenWorkItem): {
  label: string;
  conditionLabel: string;
  cameraLabel: string;
} {
  const parts = String(item.label || '').split(' — ');
  const cameraPart = parts.length > 1 ? parts[parts.length - 1] : '';
  const cameraLabel = cameraPart.includes('·')
    ? cameraPart.split('·').slice(1).join('·').trim() || cameraPart.trim()
    : cameraPart.trim() || 'Camera name unavailable';
  const conditionLabel = cameraPart.includes('·')
    ? cameraPart.split('·')[0].trim()
    : cameraPart.trim() || 'Condition';
  return {
    label: parts[0] || item.label,
    conditionLabel,
    cameraLabel,
  };
}

function formatNamedCameraList(cameras: string[]): string {
  const unique = Array.from(new Set(cameras.map((name) => name.trim()).filter(Boolean)));
  if (unique.length === 0) return 'across measured cameras';
  if (unique.length === 1) return unique[0];
  if (unique.length === 2) return `${unique[0]} and ${unique[1]}`;
  return 'across measured cameras';
}

/** Health-header scope line — cameras once; issue bullets stay kind/reason only. */
export function formatMeasurementHealthAffectedCamerasLabel(
  cameras: string[],
): string | null {
  const unique = Array.from(new Set(cameras.map((name) => name.trim()).filter(Boolean)));
  if (unique.length === 0) return null;
  if (unique.length === 1) return `Affects ${unique[0]}`;
  if (unique.length === 2) {
    return `Affects both measured cameras · ${unique[0]} and ${unique[1]}`;
  }
  return 'Affects measured cameras';
}

/**
 * Collapse identical issue kinds that repeat across conditions/cameras into one
 * site-level line. Keep per-condition·camera lines when a kind appears once or
 * when kind labels differ.
 */
export function collapseOverviewMeasurementHealthIssues(
  openWorkItems: MeasurementOpenWorkItem[],
): OverviewMeasurementHealthIssue[] {
  void OVERVIEW_HEALTH_COLLAPSE_MARKER;
  if (!openWorkItems.length) return [];

  const byKind = new Map<MeasurementOpenWorkKind, MeasurementOpenWorkItem[]>();
  for (const item of openWorkItems) {
    const group = byKind.get(item.kind) || [];
    group.push(item);
    byKind.set(item.kind, group);
  }

  const issues: OverviewMeasurementHealthIssue[] = [];
  Array.from(byKind.entries()).forEach(([kind, group]) => {
    const parsed = group.map((item) => ({ item, ...parseOpenWorkIssueParts(item) }));
    const reasonLabels = new Set(parsed.map((entry) => entry.label));
    // Same kind with identical reason across cameras → one site line.
    if (parsed.length > 1 && reasonLabels.size === 1) {
      const cameras = parsed.map((entry) => entry.cameraLabel);
      issues.push({
        id: `${kind}:site`,
        label: parsed[0].label,
        conditionLabel: 'Site',
        cameraLabel: formatNamedCameraList(cameras),
        siteScoped: true,
        kind,
      });
      return;
    }
    // Differing reasons (or a single occurrence) stay actionable per condition·camera.
    parsed.forEach((entry) => {
      issues.push({
        id: entry.item.id,
        label: entry.label,
        conditionLabel: entry.conditionLabel,
        cameraLabel: entry.cameraLabel,
        siteScoped: false,
        kind,
      });
    });
  });
  return issues;
}

/**
 * Short face body for a Current condition card — never a rate claim.
 * Coverage shortfall reason belongs once on the shared band / headline, not
 * under every calm row (state chip + count own the row face).
 */
export function formatConditionFaceBodyLine(params: {
  coverageShortfallReason?: string | null;
  supportingActivityLabel?: string | null;
  countWithDenominatorLabel?: string | null;
  resultLabel?: string | null;
  stateChipLabel?: string | null;
  /** When true (default), omit coverage reason — it lives on the shared band. */
  omitCoverageReason?: boolean;
}): string | null {
  void OVERVIEW_CALM_CARDS_MARKER;
  const omitReason = params.omitCoverageReason !== false;
  if (!omitReason) {
    const reason = String(params.coverageShortfallReason || '').trim();
    if (reason) return reason;
  }
  const count = String(params.countWithDenominatorLabel || '').trim();
  if (count) {
    // Count+denominator already has its own face slot; keep body off the calm face
    // when chip/count are present (avoid repeating coverage reason under each row).
    return null;
  }
  const activity = String(params.supportingActivityLabel || '').trim();
  if (activity) return activity;
  const result = String(params.resultLabel || '').trim();
  if (
    result
    && result !== 'Not available'
    && result !== 'Unavailable'
    // Never surface a rate claim on the calm face body.
    && !/\/\s*hour/i.test(result)
  ) {
    return result;
  }
  return null;
}

/** Label for collapsed Measurement health issues — never silently filter kinds. */
export function formatMeasurementHealthIssuesToggleLabel(params: {
  collapsedCount: number;
  rawCount: number;
}): string | null {
  if (params.collapsedCount <= 0) return null;
  if (params.rawCount > params.collapsedCount) {
    return `View issues (${params.collapsedCount} types)`;
  }
  return `View issues (${params.collapsedCount})`;
}

/** True when the Current headline already carries the coverage reason sentence. */
export function coverageReasonDuplicatedInAnswer(params: {
  answer?: string | null;
  coverageShortfallReason?: string | null;
}): boolean {
  const reason = String(params.coverageShortfallReason || '').trim().replace(/\.\s*$/, '');
  const answer = String(params.answer || '').trim();
  if (!reason || !answer) return false;
  return answer.toLowerCase().includes(reason.toLowerCase());
}

/**
 * Prefer CoveragePartition hours for Measurement health shortfall so the face
 * always matches the same partition as displayed site-wide usable/expected.
 * shortfallReasonBreakdown alone can lag when top-level hours advance.
 */
function shortfallLinesFromCoveragePartition(
  partition: NonNullable<SiteConditionMeasurementCoverage['coveragePartition']>,
): {
  shortfallLines: Array<{ reason: string; hours: number }>;
  unattributedHours: number | null;
  hasNumericShortfall: boolean;
} {
  const shortfallLines: Array<{ reason: string; hours: number }> = [];
  const parts: Array<{ hours: number | null; reason: string }> = [
    {
      hours: finiteCount(partition.telemetryUnavailableHours),
      reason: shortfallReasonCustomerLabel('telemetry_missing'),
    },
    {
      hours: finiteCount(partition.unanalyzableHours),
      reason: shortfallReasonCustomerLabel('partial_analyzable_time'),
    },
    {
      hours: finiteCount(partition.sourceUnavailableHours),
      reason: shortfallReasonCustomerLabel('device_scope_unresolved'),
    },
  ];
  for (const part of parts) {
    if (part.hours == null || part.hours <= 0) continue;
    shortfallLines.push({
      reason: part.reason,
      hours: part.hours,
    });
  }
  const unattributedHours = finiteCount(partition.unknownHours);
  return {
    shortfallLines,
    unattributedHours:
      unattributedHours != null && unattributedHours > 0 ? unattributedHours : null,
    hasNumericShortfall: shortfallLines.length > 0,
  };
}

/**
 * Round usable/expected/shortfall at one shared precision, then put any
 * residual into Unattributed so displayed parts sum exactly to displayed gap.
 */
function reconcileMeasurementHealthHoursDisplay(params: {
  usable: number | null;
  expected: number | null;
  shortfallLines: Array<{ reason: string; hours: number }>;
  unattributedHours: number | null;
}): {
  coverageLabel: string;
  shortfallLines: OverviewMeasurementHealth['shortfallLines'];
  shortfallSumHours: number | null;
  expectedMinusUsableHours: number | null;
  unattributedHours: number | null;
  unattributedLabel: string | null;
} {
  const { usable, expected } = params;
  const decimals = hoursDisplayDecimals(
    usable,
    expected,
    ...params.shortfallLines.map((line) => line.hours),
    params.unattributedHours,
  );

  const usableUnits = usable != null ? hoursToDisplayUnits(usable, decimals) : null;
  const expectedUnits = expected != null ? hoursToDisplayUnits(expected, decimals) : null;
  const gapUnits = usableUnits != null && expectedUnits != null
    ? Math.max(0, expectedUnits - usableUnits)
    : null;
  const displayUsable = usableUnits != null ? displayUnitsToHours(usableUnits, decimals) : null;
  const displayExpected = expectedUnits != null ? displayUnitsToHours(expectedUnits, decimals) : null;
  const displayGap = gapUnits != null ? displayUnitsToHours(gapUnits, decimals) : null;

  const coverageLabel = formatNowMeasuredWindowCoverage(usable, expected)
    || (
      displayUsable != null && displayExpected != null
        ? `${formatHoursAtDecimals(displayUsable, decimals)} of ${formatHoursAtDecimals(displayExpected, decimals)} scheduled hours usable`
        : displayUsable != null
          ? `${formatHoursAtDecimals(displayUsable, decimals)} usable hours`
          : 'Coverage unavailable for this window'
    );

  type LineUnits = { reason: string; units: number };
  const lineUnits: LineUnits[] = params.shortfallLines
    .map((line) => ({
      reason: line.reason,
      units: hoursToDisplayUnits(line.hours, decimals),
    }))
    .filter((line) => line.units > 0);

  let attributedUnits = lineUnits.reduce((acc, line) => acc + line.units, 0);
  let unattributedUnits: number | null = null;

  if (gapUnits != null && lineUnits.length > 0) {
    // Hand-check rule: displayed gap = sum(displayed shortfall) + displayed Unattributed.
    // Residual (including raw unknown + rounding) lands in Unattributed — never invent
    // an Unattributed bucket when there are no attributed shortfall lines.
    let residualUnits = gapUnits - attributedUnits;
    if (residualUnits < 0) {
      // Rounding overflow: shave from the largest attributed line so the sum matches.
      let largestIdx = 0;
      for (let i = 1; i < lineUnits.length; i += 1) {
        if (lineUnits[i].units > lineUnits[largestIdx].units) largestIdx = i;
      }
      const adjusted = lineUnits[largestIdx].units + residualUnits;
      if (adjusted > 0) {
        lineUnits[largestIdx] = { ...lineUnits[largestIdx], units: adjusted };
      } else {
        lineUnits.splice(largestIdx, 1);
      }
      attributedUnits = lineUnits.reduce((acc, line) => acc + line.units, 0);
      residualUnits = gapUnits - attributedUnits;
    }
    unattributedUnits = residualUnits > 0 ? residualUnits : null;
  } else if (params.unattributedHours != null && params.unattributedHours > 0) {
    unattributedUnits = hoursToDisplayUnits(params.unattributedHours, decimals);
  }

  const shortfallLines: OverviewMeasurementHealth['shortfallLines'] = lineUnits.map((line) => {
    const hours = displayUnitsToHours(line.units, decimals);
    return {
      reason: line.reason,
      hours,
      hoursLabel: `${formatHoursAtDecimals(hours, decimals)} h`,
    };
  });
  const attributedSum = shortfallLines.length > 0
    ? displayUnitsToHours(attributedUnits, decimals)
    : null;
  const displayUnattributed = unattributedUnits != null && unattributedUnits > 0
    ? displayUnitsToHours(unattributedUnits, decimals)
    : null;

  return {
    coverageLabel,
    shortfallLines,
    shortfallSumHours: attributedSum,
    expectedMinusUsableHours: displayGap,
    unattributedHours: displayUnattributed,
    unattributedLabel: displayUnattributed != null
      ? `Unattributed: ${formatHoursAtDecimals(displayUnattributed, decimals)} h`
      : null,
  };
}

function coveragePartitionIsPresent(
  partition: SiteConditionMeasurementCoverage['coveragePartition'] | null | undefined,
): partition is NonNullable<SiteConditionMeasurementCoverage['coveragePartition']> {
  if (!partition) return false;
  return (
    finiteCount(partition.usableHours) != null
    || finiteCount(partition.telemetryUnavailableHours) != null
    || finiteCount(partition.unanalyzableHours) != null
    || finiteCount(partition.sourceUnavailableHours) != null
    || finiteCount(partition.unknownHours) != null
  );
}

export function buildOverviewMeasurementHealth(params: {
  conditions: SiteConditionState[];
  usableHours: number | null | undefined;
  expectedHours: number | null | undefined;
  shortfallReasonBreakdown: SiteConditionMeasurementCoverage['shortfallReasonBreakdown'] | null | undefined;
  coveragePartition: SiteConditionMeasurementCoverage['coveragePartition'] | null | undefined;
  producerStaleness: boolean | null | undefined;
  ageLabel: string | null | undefined;
  /** Same Current-window readStale flag used by the Current shared band. */
  readStale?: boolean | null;
  openWorkItems: MeasurementOpenWorkItem[];
  locationLookups?: SafetyLocationLookups;
}): OverviewMeasurementHealth {
  const lookups = params.locationLookups || {};
  const cameraIds = Array.from(new Set(
    params.conditions
      .map((condition) => String(condition.canonicalScope?.cameraIds?.[0] || '').trim())
      .filter(Boolean),
  ));
  const availableCameraIds = Array.from(new Set(
    params.conditions
      .filter((condition) => {
        const state = String(condition.operationalMeasurements.now.displayState || '')
          .trim()
          .toLowerCase();
        return state !== 'unavailable' && state !== 'never_measured';
      })
      .map((condition) => String(condition.canonicalScope?.cameraIds?.[0] || '').trim())
      .filter(Boolean),
  ));
  const totalCameras = cameraIds.length;
  const availableCameras = availableCameraIds.length;
  const cameraViewsLabel = totalCameras === 0
    ? 'Camera views unavailable'
    : `${availableCameras} of ${totalCameras} camera views available (uptime)`;
  const cameraLabels = cameraIds
    .map((id) => resolveCustomerCameraScopeLabel(id, lookups))
    .filter((label) => label && label !== '—');
  const affectedCamerasLabel = formatMeasurementHealthAffectedCamerasLabel(cameraLabels);

  // Prefer partition usable when present so health coverage matches Current band.
  const coveragePartition = coveragePartitionIsPresent(params.coveragePartition)
    ? params.coveragePartition
    : null;
  const usable = coveragePartition
    ? (finiteCount(coveragePartition.usableHours) ?? finiteCount(params.usableHours))
    : finiteCount(params.usableHours);
  const expected = finiteCount(params.expectedHours);
  const coverageOwnedByCurrentBand = Boolean(
    usable != null
    && expected != null
    && (
      isSiteAggregateHours(expected)
      || cameraIds.length > 1
    ),
  );

  const rawShortfallLines: Array<{ reason: string; hours: number }> = [];
  let rawUnattributedHours: number | null = null;
  let hasNumericShortfall = false;
  let rawAttributedSum = 0;

  if (coveragePartition) {
    const fromPartition = shortfallLinesFromCoveragePartition(coveragePartition);
    rawShortfallLines.push(...fromPartition.shortfallLines);
    hasNumericShortfall = fromPartition.hasNumericShortfall;
    rawUnattributedHours = fromPartition.unattributedHours;
    rawAttributedSum = fromPartition.shortfallLines.reduce((acc, line) => acc + line.hours, 0);
  } else {
    for (const [code, value] of Object.entries(params.shortfallReasonBreakdown || {})) {
      const hours = finiteCount(value?.shortfallHours);
      if (hours == null || hours <= 0) continue;
      const normalized = String(code || '').trim().toLowerCase();
      if (normalized === 'unknown' || normalized === 'unknown_analyzable') {
        // Unattributed is reported separately — not as a shortfall reason.
        continue;
      }
      hasNumericShortfall = true;
      rawAttributedSum += hours;
      rawShortfallLines.push({
        reason: shortfallReasonCustomerLabel(code),
        hours,
      });
    }
    rawUnattributedHours = finiteCount(params.shortfallReasonBreakdown?.unknown?.shortfallHours)
      ?? finiteCount(params.shortfallReasonBreakdown?.UNKNOWN?.shortfallHours);
  }

  const rawExpectedMinusUsable = usable != null && expected != null
    ? Math.max(0, expected - usable)
    : null;
  const rawUnattributedForReconcile = (
    rawUnattributedHours != null && rawUnattributedHours > 0 ? rawUnattributedHours : 0
  );
  // Underlying integrity: sum(shortfall reasons) + unattributed + usable ≈ expected.
  const rawResidual = rawExpectedMinusUsable != null
    && (hasNumericShortfall || rawUnattributedForReconcile > 0)
    ? Math.abs(
      rawExpectedMinusUsable
      - rawAttributedSum
      - rawUnattributedForReconcile,
    )
    : null;
  const shortfallReconciles = rawResidual == null || rawResidual <= 0.05;

  const display = reconcileMeasurementHealthHoursDisplay({
    usable,
    expected,
    shortfallLines: rawShortfallLines,
    unattributedHours: rawUnattributedHours,
  });

  const producerCatchUpLabel = params.producerStaleness === true
    ? 'Not caught up through this window'
    : params.producerStaleness === false
      ? 'Producer caught up through this window'
      : null;
  // Same canonical freshness as Current band (readStale wins over age wording).
  const freshnessValue = formatOverviewFreshnessLabel({
    ageLabel: params.ageLabel,
    readStale: params.readStale,
  });
  const freshnessLabel = freshnessValue
    ? `Freshness: ${freshnessValue}`
    : null;

  const issues = collapseOverviewMeasurementHealthIssues(params.openWorkItems);
  const rawOpenWorkCount = params.openWorkItems.length;
  const camerasAvailable = totalCameras > 0 && availableCameras === totalCameras;
  const faceMode = resolveMeasurementHealthFaceMode({
    camerasAvailable,
    issues,
  });
  void OVERVIEW_HEALTH_QUIET_MARKER;

  return {
    cameraViewsLabel,
    availableCameraCount: availableCameras,
    totalCameraCount: totalCameras,
    camerasAvailable,
    faceMode,
    coverageLabel: display.coverageLabel,
    coverageOwnedByCurrentBand,
    affectedCamerasLabel,
    shortfallLines: display.shortfallLines,
    shortfallSumHours: display.shortfallSumHours,
    expectedMinusUsableHours: display.expectedMinusUsableHours,
    shortfallReconciles,
    producerCatchUpLabel,
    freshnessLabel,
    unattributedLabel: display.unattributedLabel,
    rawOpenWorkCount,
    issuesToggleLabel: formatMeasurementHealthIssuesToggleLabel({
      collapsedCount: issues.length,
      rawCount: rawOpenWorkCount,
    }),
    issues,
  };
}

/**
 * Quiet face when every camera view is available and open issues are only the
 * usual coverage/stale set. Cameras down or critical claim/control issues stay
 * attention (issue bullets visible).
 */
export function resolveMeasurementHealthFaceMode(params: {
  camerasAvailable: boolean;
  issues: Array<{ kind?: MeasurementOpenWorkKind | null }>;
}): OverviewMeasurementHealthFaceMode {
  if (!params.camerasAvailable) return 'attention';
  const kinds = params.issues
    .map((issue) => issue.kind)
    .filter((kind): kind is MeasurementOpenWorkKind => Boolean(kind));
  if (kinds.some((kind) => MEASUREMENT_HEALTH_CRITICAL_KINDS.has(kind))) {
    return 'attention';
  }
  if (
    kinds.length > 0
    && !kinds.every((kind) => MEASUREMENT_HEALTH_USUAL_COVERAGE_STALE_KINDS.has(kind))
  ) {
    return 'attention';
  }
  return 'quiet';
}

export function buildOverviewPendingReviewDecisionActions(params: {
  siteId: string;
  queryAsOf: string;
  conditions: SiteConditionState[];
  locationLookups?: SafetyLocationLookups;
}): OverviewDecisionActionRow[] {
  const lookups = params.locationLookups || {};
  const rows: OverviewDecisionActionRow[] = [];
  for (const condition of params.conditions) {
    const coverage = condition.operationalMeasurements.now.summary.measurementCoverage || null;
    if (!coverage) continue;
    const pending = finiteCount(coverage.pendingReviewCount);
    if (pending == null || pending <= 0) continue;
    if (!hasEnumerableReviewSet(coverage)) continue;
    // Pending review is a measurement blocker here, not housekeeping.
    if (coverage.reviewGatePassed !== false && !reviewBlocksCurrentClaim(coverage)) {
      continue;
    }
    const familyRaw = String(condition.conditionFamily || '').trim();
    const familyLabel = familyRaw
      ? buyerPatternDisplayLabel({
        patternLabel: familyRaw.replace(/[-_]+/g, ' '),
        incidentType: mapConditionFamilyToIncidentType(familyRaw) || familyRaw,
      })
      : 'Condition';
    const safeFamily = overviewCustomerStringLooksRaw(familyLabel)
      ? 'Condition'
      : familyLabel;
    const cameraId = condition.canonicalScope?.cameraIds?.[0] || null;
    const zoneId = condition.canonicalScope?.zoneIds?.[0] || null;
    const deviceId = condition.canonicalScope?.deviceIds?.[0] || null;
    const resolvedPlace = resolveCustomerPlaceLabel(
      { camera_id: cameraId, zone_id: zoneId },
      lookups,
    );
    const placeLabel = resolvedPlace && resolvedPlace !== '—'
      ? resolvedPlace
      : null;
    const objectCore = placeLabel
      ? `${safeFamily} · ${placeLabel}`
      : safeFamily;
    const oldest = formatOldestPendingLabel(coverage, params.queryAsOf);
    const oldestDetail = oldest
      && oldest !== 'None pending'
      && oldest !== REVIEW_LIST_NOT_OPENED
      ? oldest
      : pending === 1
        ? '1 pending review item'
        : `${pending} pending review items`;
    // Measurement-health shape: human sentence — object · place.
    rows.push({
      id: `pending_review:${condition.conditionId}`,
      objectLabel: `Review needed — ${objectCore}`,
      verb: 'Review',
      detail: oldestDetail,
      href: buildInvestigateEvidencePath({
        siteId: params.siteId,
        zoneId,
        cameraId,
        deviceId,
        conditionId: condition.conditionId,
        conditionFamily: condition.conditionFamily,
        windowStart: condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowStart,
        windowEnd: condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowEnd,
      }),
      kind: 'pending_review',
    });
  }
  return rows;
}

/**
 * Merge measurement Review blockers with Controls Attention Decide rows.
 * Brief: Review vs Decide verbs; one row per item with the object named.
 * Controls Attention source labels are sanitized — never raw workflow/ids.
 */
export function mergeOverviewDecisionActionRows(params: {
  measurementRows: OverviewDecisionActionRow[];
  attentionItems?: Array<{
    id: string;
    sourceLabel?: string | null;
    reason?: string | null;
    href?: string | null;
    primaryActionLabel?: string | null;
    ageLabel?: string | null;
  }> | null;
  locationLookups?: SafetyLocationLookups;
}): OverviewDecisionActionRow[] {
  const lookups = params.locationLookups || {};
  const decideRows: OverviewDecisionActionRow[] = (params.attentionItems || []).map((item) => {
    const actionHint = String(item.primaryActionLabel || item.reason || '').toLowerCase();
    const verb: 'Review' | 'Decide' = /\breview\b/.test(actionHint) ? 'Review' : 'Decide';
    const object = resolveOverviewActionObjectLabel(
      item.sourceLabel || item.reason || null,
      lookups,
    );
    const reasonText = String(item.reason || '').trim();
    const customerReason = /^awaiting sign[- ]off$/i.test(reasonText)
      ? 'Ready for final review'
      : /^insufficient coverage$/i.test(reasonText)
        ? 'More measurement needed'
        : reasonText;
    const sentence = customerReason && !overviewCustomerStringLooksRaw(customerReason)
      ? customerReason
      : verb === 'Review'
        ? 'Review needs a decision'
        : 'Control decision needed';
    const customerAge = item.ageLabel
      ? (/^Pending\b/i.test(item.ageLabel)
        ? item.ageLabel.replace(/^Pending\b/i, 'Open')
        : `Open ${item.ageLabel}`)
      : null;
    const detailParts = [
      customerAge,
      customerReason
      && !overviewCustomerStringLooksRaw(customerReason)
      && customerReason !== sentence
        ? customerReason
        : null,
    ].filter(Boolean);
    return {
      id: `attention:${item.id}`,
      objectLabel: `${sentence} — ${object}`,
      verb,
      detail: detailParts.length > 0 ? detailParts.join(' · ') : null,
      href: item.href || null,
      kind: 'controls_attention' as const,
    };
  });
  const seen = new Set<string>();
  const out: OverviewDecisionActionRow[] = [];
  for (const row of [...params.measurementRows, ...decideRows]) {
    if (seen.has(row.id)) continue;
    seen.add(row.id);
    const customerRow = row.kind === 'pending_review'
      ? {
          ...row,
          objectLabel: row.objectLabel.replace(
            /^Pending review needs a decision\b/i,
            'Review needed',
          ),
          detail: row.detail?.replace(/^Measurement blocker\s*·\s*/i, '') || null,
        }
      : row;
    // Final guard — never let a raw identifier reach the Actions panel.
    if (overviewCustomerStringLooksRaw(customerRow.objectLabel)) {
      out.push({
        ...customerRow,
        objectLabel: customerRow.kind === 'pending_review'
          ? `Review needed — ${OVERVIEW_OBJECT_NAME_UNAVAILABLE}`
          : `Control decision needed — ${OVERVIEW_OBJECT_NAME_UNAVAILABLE}`,
      });
      continue;
    }
    out.push(customerRow);
  }
  return out;
}

function overviewDecisionActionPriority(row: OverviewDecisionActionRow): number {
  const unresolved = row.objectLabel.includes(OVERVIEW_OBJECT_NAME_UNAVAILABLE);
  // Prefer true Review/Decide decisions with a resolvable object on the face.
  if (row.kind === 'pending_review' && !unresolved) return 0;
  if (row.verb === 'Review' && !unresolved) return 1;
  if (row.verb === 'Decide' && !unresolved) return 2;
  if (row.kind === 'pending_review') return 3;
  if (row.verb === 'Review') return 4;
  if (row.verb === 'Decide') return 5;
  return 6;
}

/** Sort Actions so decision-needed Review/Decide rows lead; unresolved last. */
export function prioritizeOverviewDecisionActionRows(
  rows: OverviewDecisionActionRow[],
): OverviewDecisionActionRow[] {
  void OVERVIEW_ACTIONS_FACE_MARKER;
  return [...rows].sort((left, right) => {
    const byPriority = overviewDecisionActionPriority(left)
      - overviewDecisionActionPriority(right);
    if (byPriority !== 0) return byPriority;
    return left.id.localeCompare(right.id);
  });
}

/**
 * Face shows top N prioritized decision rows; remainder behind Show all /
 * Open Safety Cases — never dump a long engineering list on the Overview face.
 */
export function selectOverviewDecisionActionFace(
  rows: OverviewDecisionActionRow[],
  limit: number = OVERVIEW_ACTIONS_FACE_LIMIT,
): { faceRows: OverviewDecisionActionRow[]; hiddenCount: number; totalCount: number } {
  const prioritized = prioritizeOverviewDecisionActionRows(rows);
  const capped = Math.max(0, Math.floor(limit));
  const faceRows = prioritized.slice(0, capped);
  return {
    faceRows,
    hiddenCount: Math.max(0, prioritized.length - faceRows.length),
    totalCount: prioritized.length,
  };
}

/**
 * Recurring withheld / building faces stay lean — headline + window chips +
 * location/thumb/status rows. Period/slot essays move behind details.
 * Never invent peak rates or area rate tables.
 */
export function isRecurringLeanFaceState(params: {
  episodeDayTotalWithheld?: boolean | null;
  answer?: string | null;
  observedValueLabel?: string | null;
  coverageState?: OverviewNowCoverageState | null;
}): boolean {
  void OVERVIEW_RECURRING_LEAN_MARKER;
  if (params.episodeDayTotalWithheld) return true;
  const answer = String(params.answer || '');
  if (/history is still building|building history/i.test(answer)) return true;
  if (
    params.coverageState === 'insufficient_history_no_basis'
    && !params.observedValueLabel
  ) {
    return true;
  }
  return false;
}

function formatCoverageLabel(params: {
  coverageState: OverviewNowCoverageState | null;
  coveragePct: number | null | undefined;
  measurementStatus: string | null | undefined;
  usableHours?: number | null;
  expectedHours?: number | null;
  shortfallReasonBreakdown?: SiteConditionMeasurementCoverage['shortfallReasonBreakdown'] | null;
}): string {
  const usable = finiteCount(params.usableHours);
  const expected = finiteCount(params.expectedHours);
  const attributed = hasAttributedShortfallBreakdown(params.shortfallReasonBreakdown);

  if (params.coverageState === 'insufficient_history_no_basis') {
    return 'No coverage basis yet';
  }
  if (params.coverageState === 'insufficient_history_accumulating') {
    if (usable != null && expected != null) {
      return formatScheduledHoursCoverage(usable, expected);
    }
    return params.coveragePct == null
      ? 'Accumulating usable hours'
      : `Accumulating usable hours (${formatCustomerPercent(params.coveragePct)})`;
  }
  if (params.coverageState === 'insufficient_history_unknown') {
    // Headline copy only — API status stays INSUFFICIENT_HISTORY_UNKNOWN.
    // Reserve "unknown" for unattributed shortfall line items when a precise
    // usable/expected or attributed breakdown is already known.
    if (usable != null && expected != null) {
      return formatScheduledHoursCoverage(usable, expected);
    }
    if (attributed) return 'Coverage limited';
    return 'Coverage unknown';
  }
  if (params.coverageState === 'not_met') {
    if (usable != null && expected != null && attributed) {
      return formatScheduledHoursCoverage(usable, expected);
    }
    return params.coveragePct == null
      ? 'Not met'
      : `Not met (${formatCustomerPercent(params.coveragePct)})`;
  }
  if (String(params.measurementStatus || '').trim().toUpperCase() === 'MET') {
    return params.coveragePct == null ? 'Met' : formatCustomerPercent(params.coveragePct);
  }
  return params.coveragePct == null ? 'Unavailable' : formatCustomerPercent(params.coveragePct);
}

type OverviewNowCoverageProgress =
  | {
      state: 'insufficient_history_no_basis';
      coveragePct: number | null;
      coverageShortfallReason: string | null;
      hoursAccumulated: null;
      hoursRequired: null;
      estimatedDaysRemaining: null;
    }
  | {
      state: 'insufficient_history_accumulating';
      coveragePct: number | null;
      coverageShortfallReason: string | null;
      hoursAccumulated: number | null;
      hoursRequired: number | null;
      estimatedDaysRemaining: number | null;
    }
  | {
      state: 'insufficient_history_unknown';
      coveragePct: number | null;
      coverageShortfallReason: string | null;
      hoursAccumulated: null;
      hoursRequired: null;
      estimatedDaysRemaining: null;
    }
  | {
      state: 'not_met';
      coveragePct: number | null;
      coverageShortfallReason: string | null;
      hoursAccumulated: null;
      hoursRequired: null;
      estimatedDaysRemaining: null;
    };

const NO_BASIS_SHORTFALL_CODES = new Set([
  'no_coverage_buckets',
  'coverage_evidence_missing',
  'measurement_window_unavailable',
]);

const HARD_COVERAGE_SHORTFALL_CODES = new Set([
  'partial_analyzable_time',
  'no_analyzable_time',
  'device_scope_unresolved',
]);

const EXPLICIT_INSUFFICIENT_STATUSES = new Set([
  'INSUFFICIENT_HISTORY_NO_BASIS',
  'INSUFFICIENT_HISTORY_ACCUMULATING',
  'INSUFFICIENT_HISTORY_UNKNOWN',
  // Legacy coarse umbrella — subtype via shortfall / hour patterns below.
  'INSUFFICIENT_HISTORY',
]);

function telemetryMissingWhollyAbsent(
  coverage: SiteConditionMeasurementCoverage,
): boolean {
  const codes = shortfallCodes(coverage.shortfallReasonBreakdown);
  if (!codes.includes('telemetry_missing')) return false;
  const usable = coverage.usableHours;
  if (usable != null && Number.isFinite(usable) && usable > 0) return false;
  // "All" when usable is zero/absent and every shortfall reason is telemetry missing.
  return codes.every((code) => code === 'telemetry_missing');
}

function shortfallReasonLabel(
  code: string,
  state: OverviewNowCoverageState | null = null,
  coverage: SiteConditionMeasurementCoverage | null = null,
): string {
  const normalized = String(code || '').trim().toLowerCase();
  if (normalized === 'partial_analyzable_time') {
    return 'Only part of the expected monitored time was analyzable';
  }
  if (normalized === 'no_analyzable_time') {
    return 'None of the expected monitored time was analyzable';
  }
  if (normalized === 'insufficient_history') {
    return 'Not enough measured history is available yet';
  }
  if (normalized === 'device_scope_unresolved') {
    return 'Coverage could not be matched to the expected device scope';
  }
  if (normalized === 'telemetry_missing') {
    if (coverage && telemetryMissingWhollyAbsent(coverage)) {
      return 'Camera telemetry was unavailable for all of this window.';
    }
    // EPIC284 Task 3.4 — short Overview telemetry reason.
    return 'Camera telemetry was unavailable during part of the window.';
  }
  if (normalized === 'unknown') {
    return 'The cause of this coverage shortfall is unknown';
  }
  if (NO_BASIS_SHORTFALL_CODES.has(normalized) || state === 'insufficient_history_no_basis') {
    return 'Measurement coverage recording has not started for this window';
  }
  if (state === 'insufficient_history_unknown') {
    return 'Some coverage intervals are still being classified. Measurement continues; rate withheld — insufficient coverage';
  }
  const words = normalized
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) {
    return 'Coverage did not meet the expected monitored time';
  }
  return words
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Overview support: one short primary reason — not additive category totals. */
function formatOverviewCoverageReasonLead(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  state: OverviewNowCoverageState | null | undefined,
): string | null {
  if (!coverage) return null;
  if (coverage.coverageReconciliationInvalid) {
    return COVERAGE_RECONCILIATION_UNAVAILABLE_COPY;
  }
  return selectCoverageShortfallReason(coverage, state || null);
}

function selectPrimaryShortfallCode(
  coverage: SiteConditionMeasurementCoverage,
): string | null {
  const ranked = Object.entries(coverage.shortfallReasonBreakdown || {}).sort(
    ([leftCode, left], [rightCode, right]) => {
      const leftShortfall = left.shortfallHours ?? -1;
      const rightShortfall = right.shortfallHours ?? -1;
      if (rightShortfall !== leftShortfall) {
        return rightShortfall - leftShortfall;
      }
      if (right.bucketCount !== left.bucketCount) {
        return right.bucketCount - left.bucketCount;
      }
      return leftCode.localeCompare(rightCode);
    },
  );
  return ranked[0]?.[0] || null;
}

function selectCoverageShortfallReason(
  coverage: SiteConditionMeasurementCoverage,
  state: OverviewNowCoverageState | null = null,
): string | null {
  if (state === 'insufficient_history_no_basis') {
    return 'Measurement coverage recording has not started for this window';
  }
  const primaryCode = selectPrimaryShortfallCode(coverage);
  if (state === 'insufficient_history_unknown') {
    if (primaryCode && String(primaryCode).trim().toLowerCase() === 'telemetry_missing') {
      return shortfallReasonLabel(primaryCode, state, coverage);
    }
    if (hasAttributedShortfallBreakdown(coverage.shortfallReasonBreakdown)) {
      return 'Measurement is still in progress for this window. Usable hours are accumulating. Rate withheld · insufficient usable coverage';
    }
    return 'Some coverage intervals are still being classified. Measurement continues; rate withheld — insufficient coverage';
  }
  return primaryCode ? shortfallReasonLabel(primaryCode, state, coverage) : null;
}

function coverageInstrumentationTone(
  coverageState: OverviewNowCoverageState | null | undefined,
): OverviewAnswerTone | null {
  // Neutral incomplete / instrumentation states — not amber warnings.
  if (
    coverageState === 'insufficient_history_unknown'
    || coverageState === 'insufficient_history_no_basis'
    || coverageState === 'insufficient_history_accumulating'
  ) {
    return 'unavailable';
  }
  if (coverageState === 'not_met') {
    return 'limited';
  }
  return null;
}

function formatTelemetryOutageLabel(
  coverage: SiteConditionMeasurementCoverage | null | undefined,
  queryAsOf: string | null | undefined,
): string | null {
  if (!coverage) return null;
  const since = coverage.telemetryMissingSince;
  if (!since) return null;
  const hours = coverage.telemetryMissingHours;
  if (hours != null && Number.isFinite(hours)) {
    if (hours < 1) {
      const minutes = Math.max(1, Math.round(hours * 60));
      return `${minutes} minute${minutes === 1 ? '' : 's'}`;
    }
    const rounded = Math.round(hours * 10) / 10;
    return `${rounded} hour${rounded === 1 ? '' : 's'}`;
  }
  return formatMeasurementAge(queryAsOf, since);
}

function shortfallCodes(
  shortfalls: Record<string, unknown> | null | undefined,
): string[] {
  return Object.keys(shortfalls || {}).map((code) => String(code || '').trim().toLowerCase());
}

function resolveCoverageState(
  window: SiteConditionMeasurementWindow | null | undefined,
): OverviewNowCoverageState | null {
  const coverage = window?.summary.measurementCoverage;
  if (!coverage) return null;

  const measurementStatus = String(coverage.measurementStatus || '').trim().toUpperCase();
  if (measurementStatus === 'INSUFFICIENT_HISTORY_NO_BASIS') {
    return 'insufficient_history_no_basis';
  }
  if (measurementStatus === 'INSUFFICIENT_HISTORY_ACCUMULATING') {
    return 'insufficient_history_accumulating';
  }
  if (measurementStatus === 'INSUFFICIENT_HISTORY_UNKNOWN') {
    return 'insufficient_history_unknown';
  }

  const codes = shortfallCodes(coverage.shortfallReasonBreakdown);
  const missingDenominator =
    coverage.usableHours == null
    && coverage.expectedHours == null
    && coverage.coveragePct == null;
  const hasNoBasisShortfall = codes.some((code) => NO_BASIS_SHORTFALL_CODES.has(code));
  const hasHardShortfall = codes.some((code) => HARD_COVERAGE_SHORTFALL_CODES.has(code));
  const hasInsufficientHistoryShortfall = codes.includes('insufficient_history');
  const hoursKnown =
    coverage.usableHours != null
    && Number.isFinite(coverage.usableHours)
    && coverage.expectedHours != null
    && Number.isFinite(coverage.expectedHours)
    && coverage.expectedHours > 0;
  const looksInsufficient =
    EXPLICIT_INSUFFICIENT_STATUSES.has(measurementStatus)
    || hasNoBasisShortfall
    || hasInsufficientHistoryShortfall
    || (measurementStatus === 'NOT_MET' && missingDenominator);

  // Legacy coarse INSUFFICIENT_HISTORY / empty-denominator NOT_MET → subtype.
  if (looksInsufficient) {
    if (hasNoBasisShortfall || (missingDenominator && !hasInsufficientHistoryShortfall && !hoursKnown)) {
      return 'insufficient_history_no_basis';
    }
    if (
      hoursKnown
      && coverage.usableHours! < coverage.expectedHours!
      && !hasHardShortfall
    ) {
      return 'insufficient_history_accumulating';
    }
    return 'insufficient_history_unknown';
  }
  if (measurementStatus === 'NOT_MET') {
    return 'not_met';
  }
  return null;
}

function resolveAccumulatingProgress(
  coverage: SiteConditionMeasurementCoverage,
): OverviewNowCoverageProgress {
  const hoursAccumulated = coverage.usableHours;
  const hoursRequired = coverage.expectedHours;
  const windowStart = Date.parse(String(coverage.frozenBasis?.windowStart || ''));
  const windowEnd = Date.parse(String(coverage.frozenBasis?.windowEnd || ''));
  const shortfallReason = selectCoverageShortfallReason(
    coverage,
    'insufficient_history_accumulating',
  );
  const base = {
    state: 'insufficient_history_accumulating' as const,
    coveragePct: coverage.coveragePct,
    coverageShortfallReason: shortfallReason,
    hoursAccumulated:
      hoursAccumulated != null && Number.isFinite(hoursAccumulated)
        ? hoursAccumulated
        : null,
    hoursRequired:
      hoursRequired != null && Number.isFinite(hoursRequired)
        ? hoursRequired
        : null,
    estimatedDaysRemaining: null as number | null,
  };
  if (
    base.hoursAccumulated == null
    || base.hoursRequired == null
    || !Number.isFinite(windowStart)
    || !Number.isFinite(windowEnd)
    || windowEnd <= windowStart
    || base.hoursRequired <= base.hoursAccumulated
  ) {
    return base;
  }

  const windowDays = Math.max((windowEnd - windowStart) / (24 * 60 * 60 * 1000), 1);
  const expectedHoursPerDay = base.hoursRequired / windowDays;
  if (!Number.isFinite(expectedHoursPerDay) || expectedHoursPerDay <= 0) {
    return base;
  }

  return {
    ...base,
    estimatedDaysRemaining: Math.max(
      1,
      Math.ceil((base.hoursRequired - base.hoursAccumulated) / expectedHoursPerDay),
    ),
  };
}

function resolveNowCoverageState(
  window: SiteConditionMeasurementWindow | null | undefined,
): OverviewNowCoverageProgress | null {
  const coverage = window?.summary.measurementCoverage;
  if (!coverage) return null;
  const state = resolveCoverageState(window);
  if (state === 'insufficient_history_accumulating') {
    return resolveAccumulatingProgress(coverage);
  }
  if (state === 'insufficient_history_no_basis') {
    return {
      state,
      coveragePct: coverage.coveragePct,
      coverageShortfallReason: selectCoverageShortfallReason(coverage, state),
      hoursAccumulated: null,
      hoursRequired: null,
      estimatedDaysRemaining: null,
    };
  }
  if (state === 'insufficient_history_unknown') {
    return {
      state,
      coveragePct: coverage.coveragePct,
      coverageShortfallReason: selectCoverageShortfallReason(coverage, state),
      hoursAccumulated: null,
      hoursRequired: null,
      estimatedDaysRemaining: null,
    };
  }
  if (state !== 'not_met') return null;
  return {
    state: 'not_met',
    coveragePct: coverage.coveragePct,
    coverageShortfallReason: selectCoverageShortfallReason(coverage, state),
    hoursAccumulated: null,
    hoursRequired: null,
    estimatedDaysRemaining: null,
  };
}

function conditionFamilyLabel(raw: string): string {
  const normalized = String(raw || '').trim().replace(/[-_]+/g, ' ');
  if (!normalized) return '';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

function physicalScopeKey(condition: SiteConditionState): string {
  const family = String(condition.conditionFamily || '').trim().toLowerCase();
  const zones = [...(condition.canonicalScope?.zoneIds || [])].map(String).sort().join(',');
  const cameras = [...(condition.canonicalScope?.cameraIds || [])].map(String).sort().join(',');
  return `${family}|z:${zones}|c:${cameras}`;
}

function hasDeviceScope(condition: SiteConditionState): boolean {
  return Boolean(condition.canonicalScope?.deviceIds?.length);
}

function isUsableNow(condition: SiteConditionState): boolean {
  const state = condition.operationalMeasurements.now.displayState;
  return state === 'current' || state === 'current_with_limitations';
}

/**
 * Qualifying Current Issues row — current-window states only.
 *
 * OM may label TELEMETRY_MISSING UNKNOWN as `stale` while the COW is still a
 * short current window (≤24h expected). Multi-day / site-aggregate stale
 * envelopes (Room A style) leave Current and stay under Recurring Patterns.
 */
export function hasQualifyingCurrentMeasurement(
  displayState: string | null | undefined,
  options?: {
    expectedHours?: number | null;
    windowStart?: string | null;
    windowEnd?: string | null;
  },
): boolean {
  const state = String(displayState || '').trim().toLowerCase();
  if (
    state === 'current'
    || state === 'current_with_limitations'
    || state === 'delayed'
  ) {
    return true;
  }
  if (state === 'unavailable' || state === 'never_measured' || !state) {
    return false;
  }
  if (state === 'stale') {
    const expected = options?.expectedHours;
    if (typeof expected === 'number' && Number.isFinite(expected) && expected <= 24) {
      return true;
    }
    const start = Date.parse(String(options?.windowStart || ''));
    const end = Date.parse(String(options?.windowEnd || ''));
    if (
      Number.isFinite(start)
      && Number.isFinite(end)
      && (end - start) <= 36 * 3600 * 1000
      && (typeof expected !== 'number' || !Number.isFinite(expected) || expected <= 24)
    ) {
      return true;
    }
    return false;
  }
  return false;
}

function formatNotCurrentlyMeasuringLabel(params: {
  displayState: string | null | undefined;
  expectedHours?: number | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  lastMeasuredLabel: string | null | undefined;
}): string | null {
  if (hasQualifyingCurrentMeasurement(params.displayState, {
    expectedHours: params.expectedHours,
    windowStart: params.windowStart,
    windowEnd: params.windowEnd,
  })) {
    return null;
  }
  const state = String(params.displayState || '').trim().toLowerCase();
  if (state === 'unavailable' || state === 'never_measured' || !state) return null;
  const last = String(params.lastMeasuredLabel || '').trim();
  const lastBit = last && last !== '—'
    ? `Last measured ${last}`
    : 'Last measured previously';
  return `Not currently measuring · ${lastBit} · View history`;
}

/** Higher = fresher customer-facing Now/Current posture. */
function nowFreshnessScore(condition: SiteConditionState): number {
  const state = String(
    condition.operationalMeasurements.now.displayState || '',
  ).trim().toLowerCase();
  if (state === 'current') return 400;
  if (state === 'current_with_limitations') return 300;
  if (state === 'delayed') return 200;
  if (state === 'stale') return 100;
  return 0;
}

/**
 * Recency of the closed Now/Current window (or OM as-of). Used so a fresher
 * no-device twin wins over a delayed device-scoped twin pinned on old COW.
 */
function nowRecencyMs(condition: SiteConditionState): number {
  const now = condition.operationalMeasurements.now;
  const coverage = now.summary?.measurementCoverage;
  const candidates = [
    coverage?.frozenBasis?.windowEnd,
    now.displayAsOf,
    now.observedThrough,
    now.buckets?.[0]?.displayAsOf,
    now.buckets?.[0]?.observedThrough,
    // Rolling history as a last resort when Now lacks a window end.
    condition.operationalMeasurements.overTime.rolling_7d?.summary
      ?.measurementCoverage?.frozenBasis?.windowEnd,
    condition.operationalMeasurements.overTime.rolling_30d?.summary
      ?.measurementCoverage?.frozenBasis?.windowEnd,
  ];
  for (const candidate of candidates) {
    const ts = Date.parse(String(candidate || ''));
    if (Number.isFinite(ts)) return ts;
  }
  return -1;
}

function compareCustomerFacingTwins(
  left: SiteConditionState,
  right: SiteConditionState,
): number {
  const freshnessDelta = nowFreshnessScore(right) - nowFreshnessScore(left);
  if (freshnessDelta !== 0) return freshnessDelta;
  const recencyDelta = nowRecencyMs(right) - nowRecencyMs(left);
  if (recencyDelta !== 0) return recencyDelta;
  const deviceDelta = Number(hasDeviceScope(right)) - Number(hasDeviceScope(left));
  if (deviceDelta !== 0) return deviceDelta;
  const leftValue = left.operationalMeasurements.now.summary.measurementValue ?? -1;
  const rightValue = right.operationalMeasurements.now.summary.measurementValue ?? -1;
  return rightValue - leftValue;
}

/**
 * Collapse K1 twins that share family+zone+camera into one customer-facing
 * condition. Freshness / current_with_limitations always wins over a delayed,
 * stale, or unavailable sibling — including when the stale twin is
 * device-scoped and the fresh twin is not. Device scope is only a tiebreaker
 * among equally fresh identities (suppresses incomplete orphans when the
 * device-scoped sibling is itself usable/current).
 */
export function selectCustomerFacingConditions(
  conditions: SiteConditionState[],
): SiteConditionState[] {
  const groups = new Map<string, SiteConditionState[]>();
  for (const condition of conditions) {
    const key = physicalScopeKey(condition);
    const existing = groups.get(key) || [];
    existing.push(condition);
    groups.set(key, existing);
  }

  const selected: SiteConditionState[] = [];
  for (const group of Array.from(groups.values())) {
    if (group.length === 1) {
      selected.push(group[0]);
      continue;
    }
    const ranked = group.slice().sort(compareCustomerFacingTwins);
    selected.push(ranked[0]);
  }

  // Cross-family ordering: usable first, then higher observed value.
  // Twin arbitration above already applied freshness-over-device within a group.
  return selected.sort((left, right) => {
    const leftUsable = isUsableNow(left) ? 0 : 1;
    const rightUsable = isUsableNow(right) ? 0 : 1;
    if (leftUsable !== rightUsable) return leftUsable - rightUsable;
    const leftValue = left.operationalMeasurements.now.summary.measurementValue ?? -1;
    const rightValue = right.operationalMeasurements.now.summary.measurementValue ?? -1;
    return rightValue - leftValue;
  });
}

function conditionLocation(
  condition: SiteConditionState,
  lookups: SafetyLocationLookups = {},
): string {
  const cameraId = condition.canonicalScope?.cameraIds?.[0] || null;
  const zoneId = condition.canonicalScope?.zoneIds?.[0] || null;
  const areaId = condition.canonicalScope?.areaId || null;
  const place = resolveCustomerPlaceLabel(
    {
      camera_id: cameraId,
      zone_id: zoneId,
      area_label: areaId,
    },
    lookups,
  );
  if (place && place !== '—') return place;
  return 'Location not recorded';
}

function siteCompositionLabel(
  conditions: SiteConditionState[],
  lookups: SafetyLocationLookups = {},
): string {
  const measuringNow = conditions.filter((condition) => {
    const coverage = condition.operationalMeasurements.now.summary?.measurementCoverage;
    return hasQualifyingCurrentMeasurement(
      condition.operationalMeasurements.now.displayState,
      {
        expectedHours: coverage?.expectedHours ?? null,
        windowStart: coverage?.frozenBasis?.windowStart
          || condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowStart
          || null,
        windowEnd: coverage?.frozenBasis?.windowEnd
          || condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowEnd
          || null,
      },
    );
  });
  const withHistory = conditions.filter((condition) => {
    const state = condition.operationalMeasurements.now.displayState;
    return state !== 'unavailable' && state !== 'never_measured';
  });
  const nowLocations = Array.from(new Set(
    measuringNow.map((condition) => conditionLocation(condition, lookups)),
  )).filter((label) => label && label !== 'Location not recorded');
  const historyLocations = Array.from(new Set(
    withHistory.map((condition) => conditionLocation(condition, lookups)),
  )).filter((label) => label && label !== 'Location not recorded');
  const nowCount = nowLocations.length;
  const historyCount = historyLocations.length;
  if (nowCount === 0 && historyCount === 0) {
    return 'No measured locations in the current condition scope.';
  }
  const nowBit = nowCount === 0
    ? '0 locations with current measurement'
    : nowCount === 1
      ? `${nowLocations[0]} · 1 location with current measurement`
      : `${nowLocations.join(' · ')} · ${nowCount} locations with current measurement`;
  const historyBit = historyCount === 1
    ? '1 with history'
    : `${historyCount} with history`;
  return `${nowBit} · ${historyBit}`;
}

function choosePrimaryCondition(conditions: SiteConditionState[]): SiteConditionState | null {
  if (!conditions.length) return null;
  const current = conditions.find(
    (condition) => condition.operationalMeasurements.now.displayState === 'current',
  );
  if (current) return current;
  const limited = conditions.find(
    (condition) => condition.operationalMeasurements.now.displayState === 'current_with_limitations',
  );
  if (limited) return limited;
  const delayed = conditions.find(
    (condition) => condition.operationalMeasurements.now.displayState === 'delayed',
  );
  if (delayed) return delayed;
  // Fall back for Recurring/methodology wiring only. Current Issues rows still
  // filter via hasQualifyingCurrentMeasurement — stale must not appear there.
  return conditions[0];
}

function chooseSinceControlCondition(
  conditions: SiteConditionState[],
  primary: SiteConditionState | null,
): SiteConditionState | null {
  if (primary?.operationalMeasurements.sinceControl.windowAnchorId) return primary;
  return conditions.find(
    (condition) => Boolean(condition.operationalMeasurements.sinceControl.windowAnchorId),
  ) || primary;
}

function joinCustomerList(items: string[]): string {
  if (items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
}

function windowHistorySignature(
  window: SiteConditionMeasurementWindow | null | undefined,
): string {
  if (!window) return 'none';
  const periods = windowMeasuredPeriods(window);
  if (periods <= 0 || window.availability === 'unavailable') return 'none';
  return [
    // Claim and supporting facts are independent — include both so collapsed
    // history detection does not treat claim-null windows as identical when
    // bucket activity differs.
    window.summary.measurementValue ?? 'null',
    supportingActivityTotal(window) ?? 'null',
    periods,
    window.comparisonStatus || '',
    window.displayState || '',
  ].join('|');
}

function windowMeasuredPeriods(
  window: SiteConditionMeasurementWindow | null | undefined,
): number {
  return window?.summary.bucketCount || window?.buckets.length || 0;
}

function overTimeWindowDays(windowId: OverTimeWindowId): 1 | 7 | 30 | 90 {
  if (windowId === 'rolling_24h') return 1;
  if (windowId === 'rolling_7d') return 7;
  if (windowId === 'rolling_90d') return 90;
  return 30;
}

/** Scope-literal label so Recurring 1D is not read as Current COW. */
export function formatRecurringWindowScopeLabel(
  windowId: OverTimeWindowId,
  window: SiteConditionMeasurementWindow | null | undefined,
): string {
  const kind = window?.buckets?.[0]?.windowKind || windowId;
  const resolution = window?.buckets?.[0]?.resolution
    || recurringWindowResolution(windowId);
  if (windowId === 'rolling_24h') {
    const anchored = (window?.reasonCodes || []).includes(
      ROLLING_24H_ANCHORED_REASON,
    );
    return anchored
      ? `${kind} · ${resolution} — last observed day (not wall-clock today; not Current COW)`
      : `${kind} · ${resolution} — not the Current operating window`;
  }
  return `${kind} · ${resolution}`;
}

/**
 * EPIC284 Task 6.2 — Recurring history progress copy.
 * Measured periods = PT15M/P1D coverage slots with OM present — never episode volume.
 */
function describeRecurringHistoryProgress(
  window: SiteConditionMeasurementWindow | null | undefined,
  windowDays: 1 | 7 | 30 | 90,
): {
  isColdStart: boolean;
  historyProgress: string | null;
  answer: string | null;
  supportLine: string | null;
} {
  const measuredPeriods = windowMeasuredPeriods(window);
  // 1D uses PT15M source buckets (≠ Current COW). Show useful activity without
  // waiting for a full 96-bucket day before leaving cold-start.
  if (windowDays === 1) {
    if (measuredPeriods === 0) {
      return {
        isColdStart: true,
        historyProgress:
          '0 of 96 measured periods (PT15M coverage slots with OM — not episode count)',
        answer: '0 of 96 periods measured. History is still building.',
        supportLine:
          'Measured periods are PT15M coverage slots with observation/measurement present — not episode totals.',
      };
    }
    return {
      isColdStart: false,
      historyProgress:
        `${measuredPeriods} measured periods (PT15M coverage slots with OM — not episode count)`,
      answer: null,
      supportLine: null,
    };
  }

  const clampedPeriods = Math.min(measuredPeriods, windowDays);
  const historyProgress =
    `${clampedPeriods} of ${windowDays} measured periods (coverage slots with OM — not episode count)`;
  const windowPhrase = `${windowDays}-day`;

  if (measuredPeriods < windowDays) {
    return {
      isColdStart: true,
      historyProgress,
      answer: `${clampedPeriods} of ${windowDays} periods measured. History is still building.`,
      supportLine:
        `${historyProgress}. The ${windowPhrase} history is still building. Episode activity is listed separately per condition.`,
    };
  }

  return {
    isColdStart: false,
    historyProgress,
    answer: null,
    supportLine: null,
  };
}

/** Per-row measured-period copy — coverage completeness only; camera-scoped. */
function formatMeasuredPeriodsLabel(params: {
  periods: number;
  episodeDayTotalWithheld: boolean;
  cameraId?: string | null;
  usableHours?: number | null;
  expectedHours?: number | null;
  locationLookups?: SafetyLocationLookups;
}): string | null {
  if (params.periods <= 0) return null;
  const scope = params.cameraId
    ? resolveCustomerCameraScopeLabel(params.cameraId, params.locationLookups || {})
    : '—';
  const cameraBit = scope && scope !== '—'
    ? `${scope} · `
    : '';
  const base = `${cameraBit}${params.periods} measured period${params.periods === 1 ? '' : 's'} (coverage slots — not episodes)`;
  const usable = finiteCount(params.usableHours);
  const expected = finiteCount(params.expectedHours);
  const coverageBit = usable != null && expected != null
    ? ` · ${formatHoursCoveragePair(usable, expected)} usable hours`
    : usable != null
      ? ` · ${formatHoursPlain(usable)} usable hours`
      : '';
  const withheldBit = params.episodeDayTotalWithheld
    ? ' · Episode day total: withheld'
    : '';
  return `${base}${coverageBit}${withheldBit}`;
}

/**
 * Facts-surface label for supporting bucket activity.
 * Gated only on supporting fields — never on claim measurementValue / rate.
 * Never labels activity as camera-hours, a rate, unreviewed, or qualifying.
 */
function formatSupportingActivityLabel(
  window: SiteConditionMeasurementWindow | null | undefined,
): string | null {
  if (!window || !hasSupportingActivityFacts(window)) return null;
  const total = supportingActivityTotal(window);
  if (total != null && Number.isFinite(total)) {
    return formatProvisionalEpisodesLabel(formatCustomerNumber(total));
  }
  const posture = Object.entries(window.summary.activityCounts || {})
    .filter(([, count]) => typeof count === 'number' && Number.isFinite(count) && count > 0)
    .map(([activity, count]) => `${formatCustomerNumber(count)} ${String(activity).replace(/[_-]+/g, ' ')}`)
    .join(', ');
  return posture || null;
}

/** Recurring summary activity line (Task 6.2) — includes "across available periods". */
function formatRecurringObservedActivityLabel(
  window: SiteConditionMeasurementWindow | null | undefined,
  windowId: OverTimeWindowId,
): string | null {
  const total = recurringSupportingActivityTotal(window, windowId);
  if (total != null && Number.isFinite(total)) {
    return formatRecurringProvisionalEpisodesLabel(formatCustomerNumber(total));
  }
  return null;
}

function formatRecordedEpisodesLabel(total: number): string | null {
  if (!Number.isFinite(total) || total < 0) return null;
  const noun = total === 1 ? 'episode' : 'episodes';
  return `${formatCustomerNumber(total)} ${noun} recorded`;
}

function formatRecurringRecordedOccurrenceLabel(
  rows: Array<{ total: number }>,
): string | null {
  if (rows.length !== 1) return null;
  const row = rows[0];
  if (!Number.isFinite(row.total) || row.total <= 0) return null;
  return formatRecordedEpisodesLabel(row.total);
}

/** Per-condition Recurring activity — fail-closed for incomplete/anchored 1D. */
function formatRecurringConditionActivityLabel(
  window: SiteConditionMeasurementWindow | null | undefined,
  windowId: OverTimeWindowId,
): string | null {
  const total = recurringSupportingActivityTotal(window, windowId);
  if (total != null && Number.isFinite(total)) {
    return formatProvisionalEpisodesLabel(formatCustomerNumber(total));
  }
  return null;
}

function formatRecurringCoverageBasisLabel(
  window: SiteConditionMeasurementWindow | null | undefined,
): string {
  const coverage = window?.summary.measurementCoverage;
  if (!coverage) return 'Not available';

  const state = resolveCoverageState(window);
  if (state === 'insufficient_history_no_basis') return 'Not available';
  if (state) {
    return formatCoverageLabel({
      coverageState: state,
      coveragePct: coverage.coveragePct,
      measurementStatus: coverage.measurementStatus,
      usableHours: coverage.usableHours,
      expectedHours: coverage.expectedHours,
      shortfallReasonBreakdown: coverage.shortfallReasonBreakdown,
    });
  }

  if (String(coverage.measurementStatus || '').trim().toUpperCase() === 'MET') {
    return coverage.coveragePct == null
      ? 'Met'
      : `Met (${formatCustomerPercent(coverage.coveragePct)})`;
  }
  return 'Not available';
}

function formatRecurringCoverageRateLabel(
  window: SiteConditionMeasurementWindow | null | undefined,
): string | null {
  const coverage = window?.summary.measurementCoverage;
  if (!coverage || !canPresentClaimRate({
    measurementStatus: coverage.measurementStatus,
    producerStaleness: coverage.producerStaleness,
    readStale: false,
    coverageGatePassed: coverage.coverageGatePassed,
    reviewGatePassed: coverage.reviewGatePassed,
  })) {
    return null;
  }

  const displayState = String(window?.displayState || '').trim().toLowerCase();
  const allBucketsFresh = Boolean(window?.buckets.length)
    && window!.buckets.every(
      (bucket) => String(bucket.freshnessState || '').trim().toLowerCase() === 'fresh',
    );
  if (
    !allBucketsFresh
    || displayState === 'stale'
    || displayState === 'delayed'
    || displayState === 'unavailable'
    || displayState === 'never_measured'
  ) {
    return null;
  }
  return formatRatePerHour(coverage.ratePerHour);
}

function hasComparableHistory(
  window: SiteConditionMeasurementWindow | null | undefined,
): boolean {
  return windowHistorySignature(window) !== 'none'
    && window?.comparisonStatus === 'comparable';
}

/** True when longer calendar windows are not actually longer in K1 data. */
export function detectCollapsedOverTimeHistory(
  conditions: SiteConditionState[],
): {
  oneDayAvailable: boolean;
  sevenEqualsThirty: boolean;
  thirtyEqualsNinety: boolean;
  historySpanNote: string | null;
  preferredWindowId: OverTimeWindowId;
} {
  const withHistory = conditions.filter((condition) => (
    hasComparableHistory(condition.operationalMeasurements.overTime.rolling_24h)
    || hasComparableHistory(condition.operationalMeasurements.overTime.rolling_7d)
    || hasComparableHistory(condition.operationalMeasurements.overTime.rolling_30d)
    || hasComparableHistory(condition.operationalMeasurements.overTime.rolling_90d)
  ));
  const oneDayAvailable = conditions.some((condition) => (
    hasComparableHistory(condition.operationalMeasurements.overTime.rolling_24h)
    || windowMeasuredPeriods(condition.operationalMeasurements.overTime.rolling_24h) > 0
  ));
  if (withHistory.length === 0) {
    return {
      oneDayAvailable,
      sevenEqualsThirty: false,
      thirtyEqualsNinety: false,
      historySpanNote: null,
      preferredWindowId: 'rolling_24h',
    };
  }
  const sevenEqualsThirty = withHistory.every((condition) => {
    const seven = windowHistorySignature(condition.operationalMeasurements.overTime.rolling_7d);
    const thirty = windowHistorySignature(condition.operationalMeasurements.overTime.rolling_30d);
    return seven === thirty;
  });
  const thirtyEqualsNinety = withHistory.every((condition) => {
    const thirty = windowHistorySignature(condition.operationalMeasurements.overTime.rolling_30d);
    const ninety = windowHistorySignature(condition.operationalMeasurements.overTime.rolling_90d);
    return thirty === ninety;
  });
  const historySpanNote = sevenEqualsThirty
    ? (
      oneDayAvailable
        ? 'Only a short history so far — 1 day is the useful view; 7 day and 30 day match right now (still selectable).'
        : 'Only a short history so far — 7 day and 30 day views are the same right now (still selectable).'
    )
    : null;
  // Default 1D while building / short history; otherwise prefer 30d once spans diverge.
  return {
    oneDayAvailable,
    sevenEqualsThirty,
    thirtyEqualsNinety,
    historySpanNote,
    preferredWindowId: sevenEqualsThirty ? 'rolling_24h' : 'rolling_30d',
  };
}

/**
 * Map condition family → primary *stored* incident_type for Investigate/thumbs.
 * Zone intrusion conditions persist danger-zone evidence as zone_violation
 * (never zone_intrusion on yyz). Near-miss persists as near_miss.
 */
export function mapConditionFamilyToIncidentType(family: string | null | undefined): string | null {
  const normalized = String(family || '').trim().toLowerCase().replace(/-/g, '_');
  if (!normalized) return null;
  if (
    normalized === 'zone_intrusion'
    || normalized === 'zone_violation'
    || normalized === 'restricted_zone'
    || normalized === 'zone_breach'
  ) {
    return 'zone_violation';
  }
  if (
    normalized === 'near_miss'
    || normalized === 'forklift_proximity'
    || normalized === 'person_forklift_proximity'
  ) {
    return 'near_miss';
  }
  if (normalized === 'restricted_area_entry') {
    return 'zone_violation';
  }
  if (normalized === 'ppe_non_compliance' || normalized === 'ppe_compliance') {
    return 'ppe_violation';
  }
  return normalized;
}

/** Extra stored types to probe when the primary filter returns empty. */
export function candidateIncidentTypesForFamily(family: string | null | undefined): string[] {
  const normalized = String(family || '').trim().toLowerCase().replace(/-/g, '_');
  if (!normalized) return [];
  if (
    normalized === 'zone_intrusion'
    || normalized === 'zone_violation'
    || normalized === 'restricted_zone'
    || normalized === 'zone_breach'
  ) {
    return ['zone_violation', 'zone_breach', 'zone_intrusion'];
  }
  if (
    normalized === 'near_miss'
    || normalized === 'forklift_proximity'
    || normalized === 'person_forklift_proximity'
  ) {
    return ['near_miss', 'forklift_proximity', 'forklift_person', 'person_forklift_proximity'];
  }
  if (normalized === 'restricted_area_entry' || normalized === 'restricted_zone') {
    return ['zone_violation', 'zone_breach', 'zone_intrusion', 'restricted_area_entry'];
  }
  if (normalized === 'ppe_non_compliance' || normalized === 'ppe_compliance' || normalized === 'ppe_violation') {
    return ['ppe_violation', 'ppe_compliance', 'ppe_non_compliance'];
  }
  const primary = mapConditionFamilyToIncidentType(normalized);
  return primary ? [primary] : [];
}

export function buildInvestigateEvidencePath(params: {
  siteId: string;
  zoneId?: string | null;
  cameraId?: string | null;
  deviceId?: string | null;
  incidentType?: string | null;
  conditionId?: string | null;
  conditionFamily?: string | null;
  windowStart?: string | null;
  windowEnd?: string | null;
  groupKey?: string | null;
  workflowId?: string | null;
  incidentId?: string | null;
  review?: boolean;
  reviewScope?: string | null;
}): string {
  const query = new URLSearchParams();
  query.set('site', params.siteId);
  if (params.zoneId) query.set('zone_id', params.zoneId);
  if (params.cameraId) query.set('camera_id', params.cameraId);
  if (params.deviceId) query.set('device_id', params.deviceId);
  if (params.incidentType) query.set('incident_type', params.incidentType);
  if (params.conditionId) query.set('condition_id', params.conditionId);
  if (params.conditionFamily) query.set('condition_family', params.conditionFamily);
  if (params.windowStart) query.set('from_date', params.windowStart);
  if (params.windowEnd) query.set('to_date', params.windowEnd);
  query.set('table_mode', 'grouped');
  query.set('tab', 'all');
  const workflowId = String(params.workflowId || params.groupKey || '').trim();
  if (params.groupKey) query.set('group_key', params.groupKey);
  if (workflowId) query.set('workflow_id', workflowId);
  if (params.incidentId) query.set('incident_id', params.incidentId);
  if (params.review) query.set('review', '1');
  if (params.reviewScope) query.set('review_scope', params.reviewScope);
  return `/incidents?${query.toString()}`;
}

export type OverviewPatternResolutionOutcome =
  | 'missing_pattern_scope'
  | 'no_pattern'
  | 'unique_pattern'
  | 'ambiguous_pattern'
  | 'unavailable';

export type OverviewPatternResolution = {
  path: string;
  outcome: OverviewPatternResolutionOutcome;
  matchCount: number;
  groupKey: string | null;
};

type OverviewIncidentGroupList = (params: {
  status?: 'open' | 'closed' | 'all';
  from_date?: string;
  to_date?: string;
  incident_type?: string;
  zone_id?: string;
  device_id?: string;
  camera_id?: string;
  limit?: number;
  prefer_persisted?: boolean;
}) => Promise<{
  items?: Array<{
    group_key?: string | null;
  }>;
}>;

export async function resolveOverviewPatternEvidencePath(
  params: {
    siteId: string;
    zoneId?: string | null;
    cameraId?: string | null;
    deviceId?: string | null;
    incidentType?: string | null;
    conditionId?: string | null;
    conditionFamily?: string | null;
    windowStart?: string | null;
    windowEnd?: string | null;
  },
  listIncidentGroups: OverviewIncidentGroupList,
): Promise<OverviewPatternResolution> {
  const fallbackPath = buildInvestigateEvidencePath(params);
  const incidentType = String(params.incidentType || '').trim();
  const zoneId = String(params.zoneId || '').trim();
  const cameraId = String(params.cameraId || '').trim();
  const deviceId = String(params.deviceId || '').trim();
  if (!incidentType || !zoneId || !cameraId || !deviceId) {
    return {
      path: fallbackPath,
      outcome: 'missing_pattern_scope',
      matchCount: 0,
      groupKey: null,
    };
  }

  try {
    const response = await listIncidentGroups({
      status: 'all',
      from_date: params.windowStart || undefined,
      to_date: params.windowEnd || undefined,
      incident_type: incidentType,
      zone_id: zoneId,
      camera_id: cameraId,
      device_id: deviceId,
      limit: 3,
      prefer_persisted: true,
    });
    const groups = response.items || [];
    if (groups.length !== 1) {
      return {
        path: fallbackPath,
        outcome: groups.length === 0 ? 'no_pattern' : 'ambiguous_pattern',
        matchCount: groups.length,
        groupKey: null,
      };
    }

    const group = groups[0];
    const groupKey = String(group.group_key || '').trim();
    if (!groupKey) {
      return {
        path: fallbackPath,
        outcome: 'no_pattern',
        matchCount: 1,
        groupKey: null,
      };
    }

    return {
      path: buildInvestigateEvidencePath({
        ...params,
        groupKey,
        workflowId: groupKey,
        review: true,
      }),
      outcome: 'unique_pattern',
      matchCount: 1,
      groupKey,
    };
  } catch {
    return {
      path: fallbackPath,
      outcome: 'unavailable',
      matchCount: 0,
      groupKey: null,
    };
  }
}

export function buildOverviewDecisionSummary(
  response: SiteConditionStateResponse,
  options?: {
    selectedOverTimeWindowId?: OverTimeWindowId;
    /** Amendment C2/C3 — fixture or producer-declared trustworthy-history class. */
    historyTrustClass?: RecurringHistoryTrustClass | null;
    isProvisionalRebuild?: boolean | null;
    sourceRecordType?: {
      current?: string | null;
      recurring?: string | null;
    } | null;
    /** Registry site/area placements for customer place labels. */
    locationLookups?: SafetyLocationLookups;
    /** Authoritative sites.timezone IANA zone. */
    siteTimezone?: string | null;
    /**
     * EPIC293 Path A: when true, Current Issues membership = latched attention.
     * Default false — keep legacy hasQualifyingCurrentMeasurement filter.
     */
    attentionPolicyOverviewCutover?: boolean;
  },
): OverviewDecisionSummary {
  const locationLookups = options?.locationLookups || {};
  const customerConditions = selectCustomerFacingConditions(response.conditions);
  const k1SuppressedOrphans =
    response.diagnostics?.suppressedDeviceScopeOrphanTwins || [];
  const overviewSuppressedOrphanCount = Math.max(
    0,
    response.conditions.length - customerConditions.length,
  );
  const collapsedHistory = detectCollapsedOverTimeHistory(customerConditions);
  const primary = choosePrimaryCondition(customerConditions);
  const nowWindow = primary?.operationalMeasurements.now || null;
  const readiness = evaluateNowReadiness(
    readinessInputFromSiteConditionResponse(response, primary?.conditionId),
  );
  const isIncremental = readiness.updateMode === 'incremental';
  const nowCoverageWindow = isIncremental
    ? nowWindow
    : primary?.operationalMeasurements.overTime.rolling_30d || null;
  const nowCoverageEnvelope = nowCoverageWindow?.summary.measurementCoverage || null;
  const siteTimeZone = resolveSiteTimeZone(options?.siteTimezone)
    || siteTimeZoneFromCoverage(nowCoverageEnvelope)
    || customerConditions.reduce<string | null>((found, condition) => (
      found
      || siteTimeZoneFromCoverage(condition.operationalMeasurements.now.summary.measurementCoverage)
      || siteTimeZoneFromCoverage(
        condition.operationalMeasurements.overTime.rolling_24h?.summary.measurementCoverage,
      )
      || siteTimeZoneFromCoverage(
        condition.operationalMeasurements.overTime.rolling_30d?.summary.measurementCoverage,
      )
    ), null);
  const nowCoverageState = resolveNowCoverageState(nowCoverageWindow);
  const nowProducerStaleness = nowCoverageEnvelope?.producerStaleness ?? null;
  const nowWindowEnd = isIncremental ? coverageWindowEnd(nowCoverageEnvelope) : null;
  const nowExpectedRefreshMs = isIncremental
    ? expectedRefreshMsFromWindow(nowWindow)
    : null;
  const nowReadStale = isIncremental
    && isIncrementalFreshnessStale({
      producerStaleness: nowProducerStaleness,
      queryAsOf: response.queryAsOf,
      windowEnd: nowWindowEnd,
      expectedRefreshMs: nowExpectedRefreshMs,
      timeZone: siteTimeZone,
    });
  const nowExpectedRefreshLabel = isIncremental
    ? expectedRefreshLabelFromWindow(nowWindow)
    : null;
  const nowOperational = isIncremental
    ? resolveOperationalCurrentPresentation(nowWindow, nowCoverageEnvelope, {
      producerStaleness: nowProducerStaleness,
      readStale: nowReadStale,
      coverageState: nowCoverageState?.state || null,
    })
    : { rateLabel: null, measurementType: null };
  const nowRateLabel = nowOperational.rateLabel;
  const nowMeasurementType = nowOperational.measurementType;
  const nowRatePresentable = nowRateLabel != null;
  const overTimeWindows = overTimeWindowsFromSiteConditionResponse(response, primary?.conditionId);
  const requestedWindowId = options?.selectedOverTimeWindowId || collapsedHistory.preferredWindowId;
  const selectedOverTimeWindowId = (
    collapsedHistory.sevenEqualsThirty
    && (
      requestedWindowId === 'rolling_7d'
      || requestedWindowId === 'rolling_30d'
      || requestedWindowId === 'rolling_90d'
    )
    && !options?.selectedOverTimeWindowId
  )
    ? 'rolling_24h'
    : requestedWindowId;
  const overTime = presentOverTime({
    windows: overTimeWindows,
    selectedWindowId: selectedOverTimeWindowId,
  });
  const selectedOverTimeWindow = overTimeWindows[selectedOverTimeWindowId] || null;
  const recurringHistory = describeRecurringHistoryProgress(
    selectedOverTimeWindow,
    overTimeWindowDays(selectedOverTimeWindowId),
  );
  const recurringWindowScopeLabel = formatRecurringWindowScopeLabel(
    selectedOverTimeWindowId,
    selectedOverTimeWindow,
  );
  // Keep 7D/30D clickable even when they match the same short series; only
  // hide/disable 90D until a longer distinct history exists.
  const windowChoices = overTime.windowChoices
    .map((choice) => {
      if (
        choice.id === 'rolling_90d'
        && (collapsedHistory.sevenEqualsThirty || collapsedHistory.thirtyEqualsNinety)
      ) {
        return { ...choice, eligible: false, isPartial: true };
      }
      return choice;
    })
    .filter((choice) => {
      if (choice.id !== 'rolling_90d') return true;
      return choice.eligible && !collapsedHistory.thirtyEqualsNinety;
    });
  const sinceControlWindow = sinceControlWindowFromSiteConditionResponse(
    response,
    chooseSinceControlCondition(customerConditions, primary)?.conditionId,
  );
  const sinceControl = presentSinceControl({
    window: sinceControlWindow,
  });

  const canShowConditionSummary =
    readiness.status === 'ready'
    && Boolean(primary?.conditionFamily);

  const conditionRows: OverviewConditionRow[] = customerConditions.map((condition) => {
    const conditionReadiness = evaluateNowReadiness(
      readinessInputFromSiteConditionResponse(response, condition.conditionId),
    );
    // Current Issues: qualifying current-window states only (not stale history).
    // Readiness gaps must not hide an existing qualifying reading.
    const state = condition.operationalMeasurements.now.displayState || 'unavailable';
    const coverageWindow = isIncremental
      ? condition.operationalMeasurements.now
      : condition.operationalMeasurements.overTime.rolling_30d;
    const coverageEnvelope = coverageWindow?.summary.measurementCoverage || null;
    const hasCompletedMeasurement = hasQualifyingCurrentMeasurement(state, {
      expectedHours: coverageEnvelope?.expectedHours ?? null,
      windowStart: coverageEnvelope?.frozenBasis?.windowStart
        || condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowStart
        || null,
      windowEnd: coverageEnvelope?.frozenBasis?.windowEnd
        || condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowEnd
        || null,
    });
    const rowCoverageState = resolveNowCoverageState(coverageWindow);
    const rowProducerStaleness = isIncremental
      ? (coverageEnvelope?.producerStaleness ?? null)
      : null;
    const rowWindowEnd = isIncremental ? coverageWindowEnd(coverageEnvelope) : null;
    const rowExpectedRefreshMs = isIncremental
      ? expectedRefreshMsFromWindow(condition.operationalMeasurements.now)
      : null;
    const rowReadStale = isIncremental
      && isIncrementalFreshnessStale({
        producerStaleness: rowProducerStaleness,
        queryAsOf: response.queryAsOf,
        windowEnd: rowWindowEnd,
        expectedRefreshMs: rowExpectedRefreshMs,
        timeZone: siteTimeZone,
      });
    const rowExpectedRefreshLabel = isIncremental
      ? expectedRefreshLabelFromWindow(condition.operationalMeasurements.now)
      : null;
    // Review helpers remain for claim/diagnostic paths; Current omits them (5.1/5.6).
    const rowReviewBlockerLabel = isIncremental
      ? null
      : reviewBlockerLabel(coverageEnvelope, response.queryAsOf);
    const rowReviewWindowStateLabel = isIncremental
      ? null
      : formatReviewWindowStateLabel(coverageEnvelope);
    const rowReviewProgressLabel = isIncremental
      ? null
      : formatReviewProgressLabel(coverageEnvelope);
    const rowOldestPendingLabel = isIncremental
      ? null
      : formatOldestPendingLabel(coverageEnvelope, response.queryAsOf);
    const rowBlockedByLabel = isIncremental
      ? null
      : formatBlockedByLabel(coverageEnvelope);
    const rowOperational = isIncremental
      ? resolveOperationalCurrentPresentation(
        condition.operationalMeasurements.now,
        coverageEnvelope,
        {
          producerStaleness: rowProducerStaleness,
          readStale: rowReadStale,
          coverageState: rowCoverageState?.state || null,
        },
      )
      : { rateLabel: null, measurementType: null };
    const rowRateLabel = rowOperational.rateLabel;
    const rowMeasurementType = rowOperational.measurementType;
    const rowSupportingActivityLabel = formatSupportingActivityLabel(
      condition.operationalMeasurements.now,
    );
    // Amendment A: activity is the Current headline until rate display is enabled.
    const rowHasOperationalHeadline = isIncremental
      ? (rowRateLabel != null || rowSupportingActivityLabel != null)
      : false;
    const rowCardShape = rowCoverageState
      || (isIncremental && !rowHasOperationalHeadline)
      ? 'completed_without_result'
      : 'completed_with_result';
    const rowCoveragePct =
      coverageEnvelope?.coveragePct ?? rowCoverageState?.coveragePct ?? null;
    const rowMeasurementValue = resolveClaimMeasurementValue({
      rawValue: condition.operationalMeasurements.now.summary.measurementValue,
      measurementStatus: coverageEnvelope?.measurementStatus,
      producerStaleness: rowProducerStaleness,
      readStale: rowReadStale,
      coverageGatePassed: coverageEnvelope?.coverageGatePassed,
      reviewGatePassed: coverageEnvelope?.reviewGatePassed,
    });
    const rowResultLabel = isIncremental
      ? (rowRateLabel || rowSupportingActivityLabel || 'Not available')
      : rowCardShape === 'completed_without_result' || rowReviewBlockerLabel
        ? 'Not available'
        : rowMeasurementValue != null
          ? formatCustomerNumber(rowMeasurementValue)
          : 'Unavailable';
    const instrumentationTone = coverageInstrumentationTone(rowCoverageState?.state || null);
    const rowTone: OverviewAnswerTone = instrumentationTone
      || (rowCoverageState?.state === 'not_met'
        || (isIncremental && (rowReadStale || rowProducerStaleness))
        ? 'limited'
        : customerStateTone(state));
    const rowTelemetryOutageLabel = isIncremental
      ? formatTelemetryOutageLabel(coverageEnvelope, response.queryAsOf)
      : null;
    const rowTimeZone = siteTimeZoneFromCoverage(coverageEnvelope) || siteTimeZone;
    const rowCompletedAt = hasCompletedMeasurement
      ? formatCustomerTime(conditionReadiness.asOf, rowTimeZone)
      : '—';
    const rowAgeBasis = isIncremental
      ? rowWindowEnd
      : conditionReadiness.asOf;
    const rowCurrentThrough = hasCompletedMeasurement
      ? formatCurrentThrough({
        coverage: coverageEnvelope,
        asOf: conditionReadiness.asOf,
        timeZone: rowTimeZone,
      })
      : '—';
    return {
      conditionId: condition.conditionId,
      label: conditionFamilyLabel(condition.conditionFamily) || 'Unnamed condition',
      location: conditionLocation(condition, locationLookups),
      measurementState: displayStateLabel(state, rowCurrentThrough),
      latestReadingAt: hasCompletedMeasurement
        ? formatCustomerTime(conditionReadiness.asOf, rowTimeZone)
        : null,
      attentionReason: condition.attentionReason ?? null,
      attentionRequired: Boolean(condition.attentionRequired),
      pendingReviewCount: finiteCount(coverageEnvelope?.pendingReviewCount),
      reviewSetId: coverageEnvelope?.reviewSetId ? String(coverageEnvelope.reviewSetId) : null,
      tone: rowTone,
      zoneId: condition.canonicalScope?.zoneIds?.[0] || null,
      cameraId: condition.canonicalScope?.cameraIds?.[0] || null,
      deviceId: condition.canonicalScope?.deviceIds?.[0] || null,
      conditionFamily: condition.conditionFamily || null,
      incidentType: mapConditionFamilyToIncidentType(condition.conditionFamily),
      candidateIncidentTypes: candidateIncidentTypesForFamily(condition.conditionFamily),
      cardShape: rowCardShape,
      completedAt: rowCompletedAt,
      currentThrough: rowCurrentThrough,
      currentThroughAt: hasCompletedMeasurement
        ? (coverageEnvelope?.frozenBasis?.windowEnd
          || condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowEnd
          || conditionReadiness.asOf
          || null)
        : null,
      coverageLabel: formatCoverageLabel({
        coverageState: rowCoverageState?.state || null,
        coveragePct: rowCoveragePct,
        measurementStatus: coverageEnvelope?.measurementStatus,
        usableHours: coverageEnvelope?.usableHours,
        expectedHours: coverageEnvelope?.expectedHours,
        shortfallReasonBreakdown: coverageEnvelope?.shortfallReasonBreakdown,
      }),
      coverageState: rowCoverageState?.state || null,
      coveragePct: rowCoveragePct,
      coverageShortfallReason: rowCoverageState?.coverageShortfallReason ?? null,
      coverageHoursAccumulated: rowCoverageState?.hoursAccumulated ?? null,
      coverageHoursRequired: rowCoverageState?.hoursRequired ?? null,
      coverageEstimatedDaysRemaining: rowCoverageState?.estimatedDaysRemaining ?? null,
      windowUsableHours: isIncremental ? (coverageEnvelope?.usableHours ?? null) : null,
      windowExpectedHours: isIncremental ? (coverageEnvelope?.expectedHours ?? null) : null,
      shortfallReasonBreakdown: isIncremental
        ? (coverageEnvelope?.shortfallReasonBreakdown || {})
        : {},
      coveragePartition: isIncremental
        ? (coverageEnvelope?.coveragePartition ?? null)
        : null,
      coverageDiagnostics: isIncremental
        ? (coverageEnvelope?.coverageDiagnostics || [])
        : [],
      coverageReconciliationInvalid: Boolean(
        coverageEnvelope?.coverageReconciliationInvalid,
      ),
      telemetryOutageLabel: rowTelemetryOutageLabel,
      rateLabel: rowRateLabel,
      measurementType: rowMeasurementType,
      producerStaleness: rowProducerStaleness,
      readStale: rowReadStale,
      expectedRefreshLabel: rowExpectedRefreshLabel,
      reviewBlockerLabel: rowReviewBlockerLabel,
      reviewWindowStateLabel: rowReviewWindowStateLabel,
      reviewProgressLabel: rowReviewProgressLabel,
      oldestPendingLabel: rowOldestPendingLabel,
      blockedByLabel: rowBlockedByLabel,
      resultLabel: rowResultLabel,
      ageLabel: hasCompletedMeasurement
        ? formatAgeWithStaleWindow({
          queryAsOf: response.queryAsOf,
          ageBasis: rowAgeBasis,
          windowThroughLabel: rowCurrentThrough,
          readStale: rowReadStale,
        })
        : 'Unavailable',
      nextUpdate: formatCustomerTimeOrLabel(
        conditionReadiness.nextTriggerDisplay || 'Unavailable',
        rowTimeZone,
      ),
      measurementValue: rowMeasurementValue,
      latestCompletedPt15mValue: latestCompletedPt15mMeasurementValue(
        condition.operationalMeasurements.now.buckets,
      ),
      nowWindowDisplayState: state,
      nowWindowReasonCodes: condition.operationalMeasurements.now.reasonCodes || [],
      supportingActivityLabel: rowSupportingActivityLabel,
      // Brief: provisional note once per panel (panel details), never on every row.
      provisionalGroupingCaveat: null,
      stateChipLabel: resolveConditionStateChipLabel({
        coverageState: rowCoverageState?.state || null,
        producerStaleness: rowProducerStaleness,
        supportingActivityLabel: rowSupportingActivityLabel,
        measurementValue: rowMeasurementValue
          ?? (
            typeof condition.operationalMeasurements.now.summary.measurementValue === 'number'
              ? condition.operationalMeasurements.now.summary.measurementValue
              : null
          ),
        measurementState: displayStateLabel(state, rowCurrentThrough),
      }),
      countWithDenominatorLabel: null,
      bodyLine: null,
      operationalProvenance:
        condition.operationalMeasurements.now.operationalMeasurementProvenance ?? null,
      measurementIdentityKey: operationalMeasurementIdentityKey(
        condition.operationalMeasurements.now.operationalMeasurementProvenance,
      ),
      currentActivity: condition.currentActivity || null,
      sinceControl: {
        controlId: condition.operationalMeasurements.sinceControl.windowAnchorId || null,
        anchorStartedAt: condition.operationalMeasurements.sinceControl.anchorStartedAt || null,
        observedThrough: condition.operationalMeasurements.sinceControl.observedThrough
          || condition.operationalMeasurements.sinceControl.displayAsOf || null,
        availability: condition.operationalMeasurements.sinceControl.availability,
        comparisonStatus: condition.operationalMeasurements.sinceControl.comparisonStatus || null,
        bucketCount: condition.operationalMeasurements.sinceControl.summary.bucketCount
          || condition.operationalMeasurements.sinceControl.buckets.length,
      },
    };
  });
  // Current Issues only — recompute count+hours after stale/history exclusion so a
  // stale multi-day envelope cannot strip the current row's own COW hours.
  // EPIC293 Path A: gate OFF → legacy measurement filter; gate ON → latched attention.
  const attentionCutover = Boolean(options?.attentionPolicyOverviewCutover);
  const latestReadingRows = conditionRows.filter((row) => (
    attentionCutover
      ? Boolean(row.attentionRequired)
      : row.latestReadingAt !== null
  ));
  const omitCurrentSharedHours = shouldOmitSharedSiteAggregateHours(latestReadingRows);
  for (const row of latestReadingRows) {
    row.countWithDenominatorLabel = formatCountWithDenominatorLabel({
      supportingActivityLabel: row.supportingActivityLabel,
      resultLabel: row.resultLabel,
      usableHours: omitCurrentSharedHours ? null : row.windowUsableHours,
      expectedHours: omitCurrentSharedHours ? null : row.windowExpectedHours,
      omitSharedHours: omitCurrentSharedHours,
    });
    row.bodyLine = formatConditionFaceBodyLine({
      coverageShortfallReason: row.coverageShortfallReason,
      supportingActivityLabel: row.supportingActivityLabel,
      countWithDenominatorLabel: row.countWithDenominatorLabel,
      resultLabel: row.resultLabel,
      stateChipLabel: row.stateChipLabel,
    });
  }
  const defaultNowAnswer = latestReadingRows.length === 0
    ? 'No measurement result yet'
    : latestReadingRows.length === 1
      ? `${latestReadingRows[0].label} at ${latestReadingRows[0].location}`
      : joinCustomerList(latestReadingRows.map((row) => row.label));
  const defaultNowTone: OverviewAnswerTone = latestReadingRows.some(
    (row) => row.tone === 'limited',
  )
    ? 'limited'
    : latestReadingRows.some((row) => row.tone === 'available')
      ? 'available'
      : 'unavailable';

  const schedulingIncomplete =
    readiness.updateMode === 'scan_only'
    && (
      !readiness.nextTriggerDisplay
      || readiness.nextTriggerDisplay === 'Unavailable'
      || readiness.nextTriggerDisplay === 'Not scheduled'
      || /^Unavailable\b/i.test(readiness.nextTriggerDisplay)
    );
  // Keep scheduling gaps out of the primary customer line — methodology only.
  const schedulingNote = null;

  const defaultNowSupportLine = latestReadingRows.length === 0
    ? (isIncremental
      ? 'No current rolling window is available for this site.'
      : 'No completed measurement event is available for this site.')
    : isIncremental
      ? OPERATIONAL_CURRENT_SUPPORT
      : latestReadingRows.length === 1
        ? 'This card summarizes the most recent completed measurement event for the selected scope.'
        : 'Each row is the latest completed measurement event for that condition.';
  const nowSupportingActivityLabel = formatSupportingActivityLabel(nowWindow);
  const currentPresentation = resolveCurrentPanelPresentation({
    leadingAnswer: defaultNowAnswer,
    defaultSupportLine: defaultNowSupportLine,
    coverage: nowCoverageEnvelope,
    coverageState: nowCoverageState?.state || null,
    supportingActivityLabel: nowSupportingActivityLabel,
    producerStaleness: nowProducerStaleness,
    readStale: nowReadStale,
    queryAsOf: response.queryAsOf,
  });
  // Brief Slice 2 — server-issued rate only when rate_issuable; never client-compute.
  const nowIssuedRateLabel = currentPresentation.rateLabel;
  const nowAnswer = currentPresentation.answer;
  let nowSupportLine = currentPresentation.supportLine;
  const nowHasOperationalHeadline = isIncremental
    ? (
      nowIssuedRateLabel != null
      || nowRatePresentable
      || nowSupportingActivityLabel != null
      || currentPresentation.kind === 'verified_absence'
    )
    : currentPresentation.kind === 'rate_issuable'
      || currentPresentation.kind === 'verified_absence';
  const nowCardShape = (
    currentPresentation.kind === 'rate_issuable'
    || currentPresentation.kind === 'verified_absence'
  )
    ? 'completed_with_result'
    : nowCoverageState
      || (isIncremental && !nowHasOperationalHeadline)
        ? 'completed_without_result'
        : 'completed_with_result';
  const nowInstrumentationTone = coverageInstrumentationTone(nowCoverageState?.state || null);
  const nowTone: OverviewAnswerTone = nowInstrumentationTone
    || (nowCoverageState?.state === 'not_met'
      || nowReadStale
      || nowProducerStaleness
      ? 'limited'
      : defaultNowTone);
  const nowTelemetryOutageLabel = isIncremental
    ? formatTelemetryOutageLabel(nowCoverageEnvelope, response.queryAsOf)
    : null;
  const nowCoveragePct = nowCoverageEnvelope?.coveragePct ?? nowCoverageState?.coveragePct ?? null;
  // Review diagnostic labels remain for scan_only / claim packaging; incremental
  // Current omits them on the row surface (Task 5.1). Panel body uses Brief copy.
  const nowReviewBlockerLabel = isIncremental
    ? null
    : reviewBlockerLabel(nowCoverageEnvelope, response.queryAsOf);
  const nowReviewWindowStateLabel = isIncremental
    ? null
    : formatReviewWindowStateLabel(nowCoverageEnvelope);
  const nowReviewProgressLabel = isIncremental
    ? null
    : formatReviewProgressLabel(nowCoverageEnvelope);
  const nowOldestPendingLabel = isIncremental
    ? null
    : formatOldestPendingLabel(nowCoverageEnvelope, response.queryAsOf);
  const nowBlockedByLabel = isIncremental
    ? null
    : formatBlockedByLabel(nowCoverageEnvelope);
  // Task 5.1 — never append claim-bearing / review-boundary copy on Current rows.
  const nowResultLabel = isIncremental
    ? (nowIssuedRateLabel || nowRateLabel || nowSupportingActivityLabel || 'Not available')
    : nowCardShape === 'completed_without_result' || nowReviewBlockerLabel
      ? 'Not available'
      : nowWindow?.summary.measurementValue != null
        ? formatCustomerNumber(nowWindow.summary.measurementValue)
        : 'Unavailable';
  const nowMeasuredWindow = formatMeasurementWindowLabel(nowWindow);
  const nowCurrentThrough = formatCurrentThrough({
    coverage: nowCoverageEnvelope,
    asOf: readiness.asOf,
    timeZone: siteTimeZone,
  });
  const nowDisplayState = readiness.displayState || 'unavailable';
  const nowAgeLabel = formatAgeWithStaleWindow({
    queryAsOf: response.queryAsOf,
    ageBasis: isIncremental ? nowWindowEnd : readiness.asOf,
    windowThroughLabel: nowCurrentThrough,
    readStale: nowReadStale,
  });
  if (
    String(nowDisplayState || '').trim().toLowerCase() === 'delayed'
    && nowCurrentThrough
    && nowCurrentThrough !== '—'
    && latestReadingRows.length === 1
    && currentPresentation.kind === 'default'
  ) {
    nowSupportLine = [
      `Showing an older closed window through ${nowCurrentThrough} (${nowAgeLabel}).`,
      'This is not a fresh current measurement.',
      nowSupportLine,
    ].filter(Boolean).join(' ');
  }
  const nowNextUpdate = formatCustomerTimeOrLabel(
    readiness.nextTriggerDisplay || 'Unavailable',
    siteTimeZone,
  );
  const nowCadenceContext = formatCadenceContext({
    updateMode: readiness.updateMode,
    scheduleCadence: nowCoverageEnvelope?.frozenBasis?.scheduleCadence,
    nextTriggerDisplay: readiness.nextTriggerDisplay,
  });
  const nowCoverageLabel = formatCoverageLabel({
    coverageState: nowCoverageState?.state || null,
    coveragePct: nowCoveragePct,
    measurementStatus: nowCoverageEnvelope?.measurementStatus,
    usableHours: nowCoverageEnvelope?.usableHours,
    expectedHours: nowCoverageEnvelope?.expectedHours,
    shortfallReasonBreakdown: nowCoverageEnvelope?.shortfallReasonBreakdown,
  });
  const periodConditionRows: OverviewPeriodConditionRow[] = customerConditions.map(
    (condition) => {
      const window = condition.operationalMeasurements.overTime[selectedOverTimeWindowId];
      const periods = windowMeasuredPeriods(window);
      const comparable =
        window?.comparisonStatus === 'comparable'
        && periods > 0
        && window.availability !== 'unavailable';
      const periodProvenance = window?.operationalMeasurementProvenance ?? null;
      const rowActivityTotal = recurringSupportingActivityTotal(
        window,
        selectedOverTimeWindowId,
      );
      const rowActivityLabel = formatRecurringConditionActivityLabel(
        window,
        selectedOverTimeWindowId,
      );
      const rowRecordedOccurrenceTotal = recurringRecordedOccurrenceTotal(window);
      const rowRecordedOccurrenceLabel = rowRecordedOccurrenceTotal != null
        && Number.isFinite(rowRecordedOccurrenceTotal)
        && rowRecordedOccurrenceTotal > 0
        ? formatRecordedEpisodesLabel(rowRecordedOccurrenceTotal)
        : null;
      const rowActivitySeries = supportingActivitySeries(window);
      const rowActivityBuckets = supportingActivityBuckets(window);
      const rowCoverage = window?.summary.measurementCoverage || null;
      const pairUsableHours = recurringUsableHours(window);
      const pairUsableSeconds = window?.recurrenceBasis?.usableCameraSeconds ?? null;
      const rowUsableHours = pairUsableHours ?? finiteCount(rowCoverage?.usableHours);
      const rowExpectedHours = finiteCount(rowCoverage?.expectedHours);
      const rowRecurringRate = recurringRateFromSameBuckets(window);
      const rowCameraId = condition.canonicalScope?.cameraIds?.[0] || null;
      // 1D fail-closed: periods can remain as coverage completeness while episode Σ is hidden.
      const rowEpisodeDayTotalWithheld =
        selectedOverTimeWindowId === 'rolling_24h'
        && periods > 0
        && rowActivityTotal == null;
      const nowState = condition.operationalMeasurements.now.displayState || 'unavailable';
      const nowCoverage = condition.operationalMeasurements.now.summary?.measurementCoverage;
      const nowWindowStart = nowCoverage?.frozenBasis?.windowStart
        || condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowStart
        || null;
      const nowWindowEnd = nowCoverage?.frozenBasis?.windowEnd
        || condition.operationalMeasurements.now.operationalMeasurementProvenance?.windowEnd
        || null;
      const lastMeasuredLabel = formatCurrentThrough({
        coverage: nowCoverage || null,
        asOf: condition.operationalMeasurements.now.displayAsOf
          || condition.operationalMeasurements.now.observedThrough
          || null,
        timeZone: siteTimeZoneFromCoverage(nowCoverage) || siteTimeZone,
      });
      const qualifiesAsCurrent = hasQualifyingCurrentMeasurement(nowState, {
        expectedHours: nowCoverage?.expectedHours ?? null,
        windowStart: nowWindowStart,
        windowEnd: nowWindowEnd,
      });
      return {
        conditionId: condition.conditionId,
        label: conditionFamilyLabel(condition.conditionFamily) || 'Unnamed condition',
        location: conditionLocation(condition, locationLookups),
        measuredPeriods: periods,
        measuredPeriodsLabel: formatMeasuredPeriodsLabel({
          periods,
          episodeDayTotalWithheld: rowEpisodeDayTotalWithheld,
          cameraId: rowCameraId,
          usableHours: rowUsableHours,
          expectedHours: rowExpectedHours,
          locationLookups,
        }),
        episodeDayTotalWithheld: rowEpisodeDayTotalWithheld,
        usableHours: rowUsableHours,
        expectedHours: rowExpectedHours,
        usableCameraSeconds: pairUsableSeconds,
        recurringRate: rowRecurringRate,
        comparisonStatus: comparable ? 'Has history' : 'No history',
        tone: !qualifiesAsCurrent
          ? 'limited'
          : comparable
            ? 'available'
            : rowActivityLabel || rowEpisodeDayTotalWithheld
              ? 'limited'
              : 'unavailable',
        zoneId: condition.canonicalScope?.zoneIds?.[0] || null,
        cameraId: rowCameraId,
        deviceId: condition.canonicalScope?.deviceIds?.[0] || null,
        conditionFamily: condition.conditionFamily || null,
        incidentType: mapConditionFamilyToIncidentType(condition.conditionFamily),
        candidateIncidentTypes: candidateIncidentTypesForFamily(condition.conditionFamily),
        supportingActivityTotal: rowActivityTotal,
        supportingActivityLabel: rowActivityLabel,
        recordedOccurrenceTotal: rowRecordedOccurrenceTotal,
        recordedOccurrenceLabel: rowRecordedOccurrenceLabel,
        activitySeries: rowActivitySeries,
        activityBuckets: rowActivityBuckets,
        operationalProvenance: periodProvenance,
        measurementIdentityKey: operationalMeasurementIdentityKey(periodProvenance),
        nowDisplayState: nowState,
        includedThroughAt: resolveIncludedMeasurementThrough(window) || null,
        notCurrentlyMeasuringLabel: formatNotCurrentlyMeasuringLabel({
          displayState: nowState,
          expectedHours: nowCoverage?.expectedHours ?? null,
          windowStart: nowWindowStart,
          windowEnd: nowWindowEnd,
          lastMeasuredLabel,
        }),
        sinceControl: {
          controlId: condition.operationalMeasurements.sinceControl.windowAnchorId || null,
          anchorStartedAt: condition.operationalMeasurements.sinceControl.anchorStartedAt || null,
          observedThrough: condition.operationalMeasurements.sinceControl.observedThrough
            || condition.operationalMeasurements.sinceControl.displayAsOf || null,
          availability: condition.operationalMeasurements.sinceControl.availability,
          comparisonStatus: condition.operationalMeasurements.sinceControl.comparisonStatus || null,
          bucketCount: condition.operationalMeasurements.sinceControl.summary.bucketCount
            || condition.operationalMeasurements.sinceControl.buckets.length,
        },
        lifecycle: condition.conditionLifecycle || null,
      };
    },
  );
  const comparableRows = periodConditionRows.filter(
    (row) => row.comparisonStatus === 'Has history',
  );
  const activityRows = periodConditionRows.filter(
    (row) => Boolean(row.supportingActivityLabel),
  );
  const aggregatedEpisodeTotal = aggregateAlignedEpisodeTotal(activityRows);
  const scopesMisaligned = activityRows.length > 1 && aggregatedEpisodeTotal == null
    && activityRows.some((row) => row.supportingActivityTotal != null);
  const recurringObservedValueLabel = aggregatedEpisodeTotal != null
    ? formatRecurringProvisionalEpisodesLabel(
      formatCustomerNumber(aggregatedEpisodeTotal),
    )
    : scopesMisaligned
      ? null
      : formatRecurringObservedActivityLabel(
        selectedOverTimeWindow,
        selectedOverTimeWindowId,
      );
  const recordedOccurrenceRows = selectedOverTimeWindowId === 'rolling_24h'
    ? customerConditions
      .map((condition) => {
        const window = condition.operationalMeasurements.overTime[selectedOverTimeWindowId];
        const total = recurringRecordedOccurrenceTotal(window);
        return {
          total,
        };
      })
      .filter((row): row is { total: number } => (
        row.total != null && Number.isFinite(row.total) && row.total > 0
      ))
    : [];
  const recurringRecordedOccurrenceLabel = formatRecurringRecordedOccurrenceLabel(
    recordedOccurrenceRows,
  );
  const dayTotalWithheld = selectedOverTimeWindowId === 'rolling_24h'
    && recurringObservedValueLabel == null
    && periodConditionRows.some((row) => row.measuredPeriods > 0)
    && periodConditionRows.every((row) => row.supportingActivityTotal == null);
  const recurringWindowLabel = formatRecurringCustomerWindowLabel(
    selectedOverTimeWindowId,
    selectedOverTimeWindow,
    siteTimeZone,
  );
  // Prefer episode totals as the primary answer when present — do not let
  // stale/building banners be the only Recurring content. Never headline a
  // single-condition total when the panel lists multiple conditions.
  // Fail closed: never invent a sparse/anchored 1D multi-bucket day Σ.
  const dayTotalWithheldFace = dayTotalWithheld
    ? 'History building'
    : null;
  const recurringCoverageBasisLabel = formatRecurringCoverageBasisLabel(
    selectedOverTimeWindow,
  );
  const noCoverageBasisFace = recurringCoverageBasisLabel === 'Not available'
    ? 'No validated coverage measurement exists for this window.'
    : null;
  // comparableRows already means comparisonStatus === 'Has history' with periods.
  // aggregatedEpisodeTotal === 0 is a measured zero, not missing history.
  const noRecurrenceInPeriod = comparableRows.length > 0 && aggregatedEpisodeTotal === 0;
  // Brief: withheld / no-coverage-basis (with activity) / activity / building.
  // Bare no-coverage-basis (no activity) stays behind building so cold-start
  // "N of M … still building" is not replaced by the coverage-basis sentence.
  const overTimeAnswer = dayTotalWithheldFace
    ? (recurringRecordedOccurrenceLabel || dayTotalWithheldFace)
    : (
      noCoverageBasisFace
      && (recurringObservedValueLabel || activityRows.length > 0)
        ? noCoverageBasisFace
        : null
    )
    || (
      noRecurrenceInPeriod
        ? 'No recurring patterns in this period'
        : recurringObservedValueLabel
          ? recurringObservedValueLabel
          : scopesMisaligned || (activityRows.length > 1 && aggregatedEpisodeTotal == null)
            ? 'Occurrences per condition below'
            : activityRows.length === 1 && activityRows[0].supportingActivityLabel
              ? `${activityRows[0].label}: ${activityRows[0].supportingActivityLabel}`
              : recurringHistory.isColdStart
                ? (
                  recurringHistory.answer
                  || 'Not enough measured history yet'
                )
                : noCoverageBasisFace
                  || (
                    comparableRows.length === 0
                      ? 'Not enough measured history yet'
                      : comparableRows.length === 1
                        ? `${comparableRows[0].label} over time at ${comparableRows[0].location}`
                        : `${joinCustomerList(comparableRows.map((row) => row.label))} over time`
                  )
    );
  const overTimeTone: OverviewAnswerTone = recurringObservedValueLabel || activityRows.length > 0
    ? (recurringHistory.isColdStart || overTime.stateKey === 'stale' || overTime.stateKey === 'delayed'
      ? 'limited'
      : 'available')
    : dayTotalWithheld || recurringHistory.isColdStart
      ? 'limited'
      : comparableRows.length > 0
        ? 'available'
        : 'unavailable';
  const staleOrDelayedNote = (
    overTime.stateKey === 'stale' || overTime.stateKey === 'delayed'
  )
    ? overTime.storyLine
    : null;
  const dayTotalWithheldDetail = dayTotalWithheld
    ? (
      (selectedOverTimeWindow?.reasonCodes || []).includes(ROLLING_24H_ANCHORED_REASON)
        ? 'Episode day total hidden because the measured day is incomplete/anchored (last observed day, not wall-clock last 24 hours).'
        : 'Episode day total hidden because the measured day is incomplete (PT15M coverage slots too sparse for a complete day total).'
    )
    : null;
  const measuredPeriodsDetail = dayTotalWithheld
    ? 'Measured periods are PT15M coverage slots with OM present — a coverage completeness signal, not episode count.'
    : null;
  const distinctPeriodCounts = new Set(
    periodConditionRows
      .filter((row) => row.measuredPeriods > 0)
      .map((row) => row.measuredPeriods),
  );
  // Face measured-periods line: keep short. When cameras disagree, defer to rows.
  const recurringHistoryProgress = distinctPeriodCounts.size > 1
    ? 'Measured periods differ by camera — see each condition row'
    : dayTotalWithheld && recurringHistory.historyProgress
      ? recurringHistory.historyProgress.replace(
        /\s*\(PT15M coverage slots with OM — not episode count\)/i,
        '',
      ).trim()
      : recurringHistory.historyProgress;
  // Face: short support under the headline. Long caveats → measurementDetailLines.
  // When day total is withheld, the Brief sentence is already the answer — keep
  // support short. "Measured periods differ by camera" lives once on the
  // Measured periods fact (historyProgress), not also in supportLine.
  const activitySupportLine = recurringObservedValueLabel || activityRows.length > 0
    ? (
      activityRows.length > 1
        ? activityRows
          .map((row) => `${row.label}: ${row.supportingActivityLabel}`)
          .join(' · ')
        : comparableRows.length > 0
          ? comparableRows.map((row) => `${row.label} at ${row.location}`).join(' · ')
          : recurringHistory.supportLine
            || 'Activity for this period is listed per condition.'
    )
    : null;
  const recordedOccurrenceSupportLine = recordedOccurrenceRows.length > 0
    ? 'History building - rate withheld until zero-occurrence intervals are measured.'
    : null;
  const measuredPeriodsDifferByCamera = /differ by camera/i.test(
    recurringHistoryProgress || '',
  );
  // Face support under the withheld headline — never a second "see each row"
  // pointer. Measured-periods guidance lives once on the Measured periods fact
  // (historyProgress) and/or per-camera row labels.
  const withheldConditionSupport = comparableRows.length > 0
    ? comparableRows.map((row) => `${row.label} at ${row.location}`).join(' · ')
    : periodConditionRows
      .map((row) => `${row.label} at ${row.location}`)
      .filter(Boolean)
      .join(' · ')
      || null;
  const overTimeSupportLine = dayTotalWithheld
    ? (
      recordedOccurrenceSupportLine
      || (measuredPeriodsDifferByCamera
        ? withheldConditionSupport
        : (
          // Avoid repeating the Measured periods fact as the support line.
          withheldConditionSupport
          || recurringHistory.supportLine
          || null
        ))
    )
    : overTimeAnswer === noCoverageBasisFace
      ? (
        recurringObservedValueLabel
          ? `Observed activity (separate from coverage): ${recurringObservedValueLabel}`
          : activitySupportLine
            ? `Observed activity (separate from coverage): ${activitySupportLine}`
            : 'Observed activity is shown separately when present.'
      )
      : (
        activitySupportLine
        || (
          measuredPeriodsDifferByCamera
            ? null
            : recurringHistory.supportLine
        )
        || collapsedHistory.historySpanNote
        || (
          comparableRows.length > 0
            ? comparableRows.map((row) => `${row.label} at ${row.location}`).join(' · ')
            : null
        )
        || 'No history to compare for this period.'
      );
  const panelIncludedThrough = resolvePanelIncludedThrough(
    customerConditions,
    selectedOverTimeWindowId,
  );
  const panelEnvelopeEnd = resolvePanelEnvelopeEnd(selectedOverTimeWindow);
  const recurringAnchoredToObserved = (
    selectedOverTimeWindow?.reasonCodes || []
  ).includes(ROLLING_24H_ANCHORED_REASON);
  const recurringAsOfLabel = formatAnchoredRecurringHistoryAsOfFace({
    historyAsOf: panelIncludedThrough,
    queryAsOf: response.queryAsOf,
    anchored: recurringAnchoredToObserved,
    timeZone: siteTimeZone,
  });
  const envelopeDiffersFromIncluded = Boolean(
    selectedOverTimeWindowId === 'rolling_24h'
    && panelEnvelopeEnd
    && panelIncludedThrough
    && panelIncludedThrough !== '—'
    && panelEnvelopeEnd !== panelIncludedThrough,
  );
  const recurringEnvelopeDetail = envelopeDiffersFromIncluded
    ? `24-hour window ending ${formatCustomerTime(panelEnvelopeEnd, siteTimeZone)}`
    : null;
  const recurringIncludedEndDetail = envelopeDiffersFromIncluded
    ? `Latest included measurement ended ${formatCustomerTime(panelIncludedThrough, siteTimeZone)}`
    : null;
  // Face already owns the no-coverage-basis sentence — do not repeat in details.
  const recurringCoverageNote = null;
  const historyTrustClass = resolveRecurringHistoryTrustClass({
    historyTrustClass: options?.historyTrustClass,
    isProvisionalRebuild: options?.isProvisionalRebuild,
  });
  const historyTrustNote = recurringHistoryTrustNote(historyTrustClass);
  const basisAlignment = alignCurrentRecurringBasis(
    nowWindow,
    selectedOverTimeWindow,
    {
      current: { sourceRecordType: options?.sourceRecordType?.current },
      recurring: { sourceRecordType: options?.sourceRecordType?.recurring },
    },
  );
  const recurringProvisionalCaveat = (
    recurringObservedValueLabel || activityRows.length > 0 || dayTotalWithheld
  )
    ? PROVISIONAL_GROUPING_CAVEAT
    : null;
  // Brief pre-vNext: one provisional/trust note per panel — prefer history trust note.
  const recurringSeriesProvisionalNote = historyTrustNote || recurringProvisionalCaveat;
  // Customer-facing details never echo demo/canary/test/hash/raw cam identifiers.
  const customerSafeBasisDiffs = basisAlignment.inspectableDiffs
    .map((line) => line.replace(
      /\b(?:cam[_-][a-z0-9]+|demo[a-z0-9_-]*|canary[a-z0-9_-]*|test[a-z0-9_-]*|[a-f0-9]{12,})\b/gi,
      'declared policy',
    ))
    .filter((line) => !/\b(?:demo|canary|test)\b/i.test(line));
  const measurementDetailLines = [
    dayTotalWithheldDetail,
    measuredPeriodsDetail,
    recurringEnvelopeDetail,
    recurringIncludedEndDetail,
    selectedOverTimeWindowId === 'rolling_24h' ? recurringWindowScopeLabel : null,
    // Keep building-history / cold-start detail out of the face when another
    // short sentence already owns supportLine.
    recurringHistory.supportLine
    && recurringHistory.supportLine !== overTimeSupportLine
      ? recurringHistory.supportLine
      : null,
    staleOrDelayedNote,
    recurringCoverageNote,
    basisAlignment.comparable ? null : basisAlignment.boundaryLabel,
    ...customerSafeBasisDiffs,
    recurringSeriesProvisionalNote,
  ].filter((line, index, all): line is string => (
    Boolean(line)
    && line !== overTimeSupportLine
    && all.indexOf(line) === index
  ));

  const linkedConditionCount = customerConditions.filter(
    (condition) => Boolean(condition.operationalMeasurements.sinceControl.windowAnchorId),
  ).length;
  const linkedControlLocations = Array.from(new Set(
    customerConditions
      .filter((condition) => Boolean(
        condition.operationalMeasurements.sinceControl.windowAnchorId,
      ))
      .map((condition) => conditionLocation(condition, locationLookups))
      .map((location) => String(location || '').trim())
      .filter((location) => location && location !== '—' && location !== 'Location not recorded'),
  ));
  const linkedControlLocation = linkedControlLocations.length === 0
    ? null
    : linkedControlLocations.join(' · ');
  const sincePresentation = resolveSinceControlPanelPresentation({
    window: sinceControlWindow,
    linkedConditionCount,
    comparisonBlocked: sinceControl.comparisonBlocked,
    defaultAnswer: 'No control recorded',
    defaultSupportLine: 'Record a control to begin follow-up measurement.',
    defaultTone: 'unavailable',
    controlLocation: linkedControlLocation,
  });
  const followUpState = sincePresentation.followUpState;
  const observedResult = sincePresentation.observedResult;
  const sinceAnswer = sincePresentation.answer;
  const sinceTone = sincePresentation.answerTone;
  const sinceSupportLine = sincePresentation.supportLine;
  const openWorkItems = buildMeasurementOpenWorkItems({
    ...response,
    conditions: customerConditions,
  }, locationLookups);
  const nowFreshnessLabel = formatOverviewFreshnessLabel({
    ageLabel: nowAgeLabel,
    readStale: isIncremental ? nowReadStale : false,
  });
  const measurementHealth = buildOverviewMeasurementHealth({
    conditions: customerConditions,
    usableHours: isIncremental ? (nowCoverageEnvelope?.usableHours ?? null) : null,
    expectedHours: isIncremental ? (nowCoverageEnvelope?.expectedHours ?? null) : null,
    shortfallReasonBreakdown: isIncremental
      ? (nowCoverageEnvelope?.shortfallReasonBreakdown || {})
      : {},
    coveragePartition: isIncremental
      ? (nowCoverageEnvelope?.coveragePartition ?? null)
      : null,
    producerStaleness: isIncremental ? nowProducerStaleness : null,
    ageLabel: nowAgeLabel,
    readStale: isIncremental ? nowReadStale : false,
    openWorkItems,
    locationLookups,
  });
  const decisionActions = buildOverviewPendingReviewDecisionActions({
    siteId: response.siteId,
    queryAsOf: response.queryAsOf,
    conditions: customerConditions,
    locationLookups,
  });

  const nowOperationalProvenance =
    nowWindow?.operationalMeasurementProvenance ?? null;
  const nowMeasurementIdentityKey = operationalMeasurementIdentityKey(
    nowOperationalProvenance,
  );
  const k1SuppressedNote = k1SuppressedOrphans.length > 0
    ? `K1 suppressed ${k1SuppressedOrphans.length} no-device orphan twin${k1SuppressedOrphans.length === 1 ? '' : 's'} because a device-scoped sibling exists (${k1SuppressedOrphans.map((row) => row.conditionId.slice(0, 8)).join(', ')}).`
    : null;
  const diagnosticNotes = [
    k1SuppressedNote,
    overviewSuppressedOrphanCount > 0
      ? `Suppressed ${overviewSuppressedOrphanCount} incomplete duplicate condition identit${overviewSuppressedOrphanCount === 1 ? 'y' : 'ies'} sharing the same family, zone, and camera.`
      : null,
    formatOperationalIdentityNote(nowOperationalProvenance),
    collapsedHistory.historySpanNote,
    primary?.conditionFamily
      ? null
      : 'Canonical condition identity is not declared on K1.',
    readiness.blockedReasons[0] || null,
    overTime.comparabilitySummary || null,
    basisAlignment.comparable ? null : basisAlignment.boundaryLabel,
    ...basisAlignment.inspectableDiffs,
    historyTrustNote,
    sinceControl.basisSummary || null,
  ].filter(Boolean) as string[];

  const provenanceMap: Record<string, OverviewFieldProvenance> = {
    measurementState: provenance('K1 now.display_state'),
    updateMode: provenance(
      nowWindow?.updateMode === undefined ? null : 'K1 now.update_mode',
    ),
    asOf: provenance(
      readiness.asOf ? 'K1 now.display_as_of|observed_through|query_as_of' : null,
    ),
    coverage: provenance(primary?.canonicalScope ? 'K1 condition.canonical_scope' : null),
    topMeasuredCondition: provenance(primary?.conditionFamily ? 'K1 condition.condition_family' : null),
    topLocation: provenance(
      primary?.canonicalScope?.zoneIds?.length || primary?.canonicalScope?.areaId
        ? 'K1 condition.canonical_scope + buyer location labels'
        : null,
    ),
    operationalMeasurementIdentity: provenance(
      nowMeasurementIdentityKey
        ? 'K1 now.operational_measurement_provenance'
        : null,
    ),
    comparablePeriodResult: provenance('K1 over_time[selected_window]'),
    controlAnchor: provenance(
      sinceControlWindow?.windowAnchorId ? 'K1 since_control.window_anchor_id' : null,
    ),
    controlOwner: provenance(null),
    followUpState: provenance(
      sinceControlWindow ? 'K1 since_control availability/comparison state' : null,
    ),
    observedResult: provenance(
      followUpState === 'Complete' ? 'K1 since_control descriptive result' : null,
    ),
  };

  return {
    siteId: response.siteId,
    queryAsOf: response.queryAsOf,
    siteTimezone: siteTimeZone,
    conditionCount: customerConditions.length,
    readCompleteness: response.readCompleteness === 'partial' ? 'partial' : 'complete',
    siteTotalsAuthoritative: response.siteTotalsAuthoritative !== false
      && response.readCompleteness !== 'partial',
    failedReads: response.failedReads || [],
    primaryConditionId: primary?.conditionId || null,
    now: {
      cardShape: nowCardShape,
      answer: nowAnswer,
      answerTone: nowTone,
      supportLine: nowSupportLine,
      coverageState: nowCoverageState?.state || null,
      coveragePct: nowCoveragePct,
      coverageShortfallReason: nowCoverageState?.coverageShortfallReason ?? null,
      coverageHoursAccumulated: nowCoverageState?.hoursAccumulated ?? null,
      coverageHoursRequired: nowCoverageState?.hoursRequired ?? null,
      coverageEstimatedDaysRemaining: nowCoverageState?.estimatedDaysRemaining ?? null,
      windowUsableHours: isIncremental ? (nowCoverageEnvelope?.usableHours ?? null) : null,
      windowExpectedHours: isIncremental ? (nowCoverageEnvelope?.expectedHours ?? null) : null,
      shortfallReasonBreakdown: isIncremental
        ? (nowCoverageEnvelope?.shortfallReasonBreakdown || {})
        : {},
      coveragePartition: isIncremental
        ? (nowCoverageEnvelope?.coveragePartition ?? null)
        : null,
      coverageDiagnostics: isIncremental
        ? (nowCoverageEnvelope?.coverageDiagnostics || [])
        : [],
      coverageReconciliationInvalid: Boolean(
        nowCoverageEnvelope?.coverageReconciliationInvalid,
      ),
      telemetryOutageLabel: nowTelemetryOutageLabel,
      rateLabel: nowIssuedRateLabel,
      measurementType: nowMeasurementType,
      producerStaleness: isIncremental ? nowProducerStaleness : null,
      readStale: isIncremental ? nowReadStale : false,
      expectedRefreshLabel: nowExpectedRefreshLabel,
      freshnessLabel: nowFreshnessLabel,
      reviewBlockerLabel: nowReviewBlockerLabel,
      reviewWindowStateLabel: nowReviewWindowStateLabel,
      reviewProgressLabel: nowReviewProgressLabel,
      oldestPendingLabel: nowOldestPendingLabel,
      blockedByLabel: nowBlockedByLabel,
      dashboardAsOfLabel: formatCustomerTime(response.queryAsOf, siteTimeZone),
      measurementState: displayStateLabel(nowDisplayState, nowCurrentThrough),
      updateMode: updateModeLabel(readiness.updateMode),
      lastCompleted: formatCustomerTime(readiness.asOf, siteTimeZone),
      measuredWindow: nowMeasuredWindow,
      coverageLabel: nowCoverageLabel,
      resultLabel: nowResultLabel,
      ageLabel: nowAgeLabel,
      measurementValue: resolveClaimMeasurementValue({
        rawValue: nowWindow?.summary.measurementValue,
        measurementStatus: nowCoverageEnvelope?.measurementStatus,
        producerStaleness: nowProducerStaleness,
        readStale: nowReadStale,
        coverageGatePassed: nowCoverageEnvelope?.coverageGatePassed,
        reviewGatePassed: nowCoverageEnvelope?.reviewGatePassed,
      }),
      supportingActivityLabel: nowSupportingActivityLabel,
      provisionalGroupingCaveat: nowSupportingActivityLabel
        ? PROVISIONAL_GROUPING_CAVEAT
        : null,
      nextUpdate: nowNextUpdate,
      cadenceContext: nowCadenceContext,
      schedulingIncomplete,
      schedulingNote,
      canShowConditionSummary,
      conditionId: primary?.conditionId || null,
      conditionFamily: primary?.conditionFamily || null,
      zoneId: primary?.canonicalScope?.zoneIds?.[0] || null,
      cameraId: primary?.canonicalScope?.cameraIds?.[0] || null,
      deviceId: primary?.canonicalScope?.deviceIds?.[0] || null,
      operationalProvenance: nowOperationalProvenance,
      measurementIdentityKey: nowMeasurementIdentityKey,
      conditionRows: latestReadingRows,
    },
    overTime: {
      answer: overTimeAnswer,
      answerTone: overTimeTone,
      supportLine: overTimeSupportLine || 'No history to compare for this period.',
      measurementDetailLines,
      coverageState: resolveCoverageState(selectedOverTimeWindow),
      stateLabel: overTime.stateLabel,
      asOfLabel: recurringAsOfLabel,
      measurementValue: selectedOverTimeWindow?.summary.measurementValue ?? null,
      observedValueLabel: recurringObservedValueLabel,
      // Once per Recurring panel: prefer trust note in details; do not also
      // expose the provisional caveat as a second panel-level note.
      provisionalGroupingCaveat: historyTrustNote ? null : recurringProvisionalCaveat,
      coverageBasisLabel: recurringCoverageBasisLabel,
      coverageRateLabel: formatRecurringCoverageRateLabel(selectedOverTimeWindow),
      historySpanNote: collapsedHistory.historySpanNote,
      historyProgress: recurringHistoryProgress,
      episodeDayTotalWithheld: dayTotalWithheld,
      leanFace: isRecurringLeanFaceState({
        episodeDayTotalWithheld: dayTotalWithheld,
        answer: overTimeAnswer,
        observedValueLabel: recurringObservedValueLabel,
        coverageState: resolveCoverageState(selectedOverTimeWindow),
      }),
      historyTrustClass,
      historyTrustNote,
      basisAlignment,
      selectedWindowId: selectedOverTimeWindowId,
      windowLabel: recurringWindowLabel,
      windowScopeLabel: recurringWindowScopeLabel,
      periodFacts: [
        recurringWindowLabel,
        recurringWindowScopeLabel,
        recurringHistoryProgress
          || `${comparableRows.length} condition${comparableRows.length === 1 ? '' : 's'} with history`,
        collapsedHistory.sevenEqualsThirty
          ? '1d is the useful view; 7d and 30d match short history right now'
          : null,
        basisAlignment.comparable ? null : 'Current/Recurring basis boundary',
      ].filter(Boolean).join(' · '),
      windowChoices,
      conditionRows: periodConditionRows,
    },
    sinceControl: {
      answer: sinceAnswer,
      answerTone: sinceTone,
      supportLine: sinceSupportLine,
      followUpState,
      observedResult,
      controlId: sinceControl.controlAnchorId,
      linkedConditionCount,
      totalConditionCount: customerConditions.length,
      presentationKind: sincePresentation.kind,
      comparisonSides: sincePresentation.comparisonSides,
      variationCaveat: sincePresentation.variationCaveat,
      provenanceLine: sincePresentation.provenanceLine,
      basisDifferedLabel: sincePresentation.basisDifferedLabel,
      actionLabel: sincePresentation.actionLabel,
      controlLocation: sincePresentation.controlLocation,
      controlDateLabel: sincePresentation.controlDateLabel,
    },
    siteComposition: siteCompositionLabel(customerConditions, locationLookups),
    measurementHealth,
    decisionActions,
    openWork: {
      // Customer-facing conditions only — suppress orphan-twin duplicate issue lines.
      items: openWorkItems,
      excludedPendingReviewTotal: sumExcludedPendingReviewCount(response),
    },
    provenance: provenanceMap,
    methodology: {
      nowMeasurementValue: nowWindow?.summary.measurementValue ?? null,
      overTimeMeasurementValue:
        overTimeWindows[selectedOverTimeWindowId]?.summary.measurementValue ?? null,
      overTimeWindowLabel: overTime.windowLabel,
      nowLimitations: readiness.blockedReasons[0]
        || overTime.limitation
        || 'No additional Now limitations declared.',
      overTimeLimitations: overTime.limitation,
      sinceControlLimitations: sinceControl.limitation,
      diagnosticNotes,
    },
  };
}

/** @deprecated Prefer buildOverviewDecisionSummary — kept for transitional imports. */
export const buildOverviewSiteConditionPosture = buildOverviewDecisionSummary;

const OVERVIEW_ASSEMBLY_UNAVAILABLE = 'Site Condition data is temporarily unavailable.';

function uniqueSurvivorConditionIds(conditions: SiteConditionState[]): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const condition of conditions || []) {
    const conditionId = String(condition?.conditionId || '').trim();
    if (!conditionId || seen.has(conditionId)) {
      continue;
    }
    seen.add(conditionId);
    ids.push(conditionId);
  }
  return ids;
}

function conditionStateAsOfInstantsEqual(left: string, right: string): boolean {
  const leftMs = Date.parse(String(left || '').trim());
  const rightMs = Date.parse(String(right || '').trim());
  return Number.isFinite(leftMs) && Number.isFinite(rightMs) && leftMs === rightMs;
}

function overviewAssemblyError(
  result?: ContinuousSafetyFetchResult<SiteConditionStateResponse>,
): ContinuousSafetyFetchResult<SiteConditionStateResponse> {
  if (result?.status === 'error') {
    return result;
  }
  return {
    status: 'error',
    httpStatus: null,
    message: OVERVIEW_ASSEMBLY_UNAVAILABLE,
  };
}

function assembledEmptySiteConditionState(
  identity: SiteConditionStateResponse,
): SiteConditionStateResponse {
  return {
    ok: true,
    orgId: identity.orgId,
    siteId: identity.siteId,
    queryAsOf: identity.queryAsOf,
    conditionCount: 0,
    conditions: [],
    identityOnly: false,
    readCompleteness: 'complete',
    siteTotalsAuthoritative: true,
    failedReads: [],
    diagnostics: {
      suppressedDeviceScopeOrphanTwins:
        identity.diagnostics?.suppressedDeviceScopeOrphanTwins || [],
    },
  };
}

const OVERVIEW_DETAIL_CONCURRENCY = 2;
const OVERVIEW_DETAIL_MAX_ATTEMPTS = 3;
const OVERVIEW_DETAIL_RETRY_DELAYS_MS = [40, 80];

function isTransientConditionRead(
  result: ContinuousSafetyFetchResult<SiteConditionStateResponse>,
): boolean {
  if (result.status !== 'error') return false;
  const status = result.httpStatus;
  if (status == null) return true;
  return status === 408 || status === 429 || status >= 500;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from(
    { length: Math.min(Math.max(limit, 1), items.length) },
    async () => {
      while (next < items.length) {
        const index = next;
        next += 1;
        results[index] = await fn(items[index]);
      }
    },
  );
  await Promise.all(workers);
  return results;
}

async function readOverviewConditionDetail(
  siteId: string,
  conditionId: string,
  queryAsOfIso: string,
): Promise<ContinuousSafetyFetchResult<SiteConditionStateResponse>> {
  let last: ContinuousSafetyFetchResult<SiteConditionStateResponse> = {
    status: 'error',
    httpStatus: null,
    message: OVERVIEW_ASSEMBLY_UNAVAILABLE,
  };
  for (let attempt = 1; attempt <= OVERVIEW_DETAIL_MAX_ATTEMPTS; attempt += 1) {
    try {
      last = await fetchSiteConditionState({
        siteId,
        conditionIds: [conditionId],
        asOf: queryAsOfIso,
      });
    } catch {
      last = {
        status: 'error',
        httpStatus: null,
        message: OVERVIEW_ASSEMBLY_UNAVAILABLE,
      };
    }
    if (!isTransientConditionRead(last) || attempt === OVERVIEW_DETAIL_MAX_ATTEMPTS) {
      return last;
    }
    const delay = OVERVIEW_DETAIL_RETRY_DELAYS_MS[attempt - 1] ?? 80;
    await new Promise((resolve) => setTimeout(resolve, delay));
  }
  return last;
}

function classifyOverviewDetail(
  identity: SiteConditionStateResponse,
  conditionId: string,
  detail: ContinuousSafetyFetchResult<SiteConditionStateResponse>,
): { row: SiteConditionState } | { failure: NonNullable<SiteConditionStateResponse['failedReads']>[number] } {
  if (detail.status !== 'success') {
    return {
      failure: {
        conditionId,
        httpStatus: detail.status === 'error' ? detail.httpStatus : null,
        code: 'http_error',
      },
    };
  }
  if (detail.data.orgId !== identity.orgId || detail.data.siteId !== identity.siteId) {
    return { failure: { conditionId, httpStatus: null, code: 'scope_mismatch' } };
  }
  if (!conditionStateAsOfInstantsEqual(detail.data.queryAsOf, identity.queryAsOf)) {
    return { failure: { conditionId, httpStatus: null, code: 'as_of_mismatch' } };
  }
  const rows = detail.data.conditions || [];
  const matched = rows.filter((row) => String(row?.conditionId || '').trim() === conditionId);
  if (rows.length !== 1 || matched.length !== 1) {
    return { failure: { conditionId, httpStatus: null, code: 'condition_mismatch' } };
  }
  return { row: matched[0] };
}

/**
 * Overview condition-state assembly: identity_only → freeze as_of →
 * bounded per-survivor condition-scoped reads.
 * A failed detail stays unavailable. Successful rows for the same scope and
 * as-of are kept, and site-wide totals are withheld. Identity failure or
 * failure of every detail is unavailable, not an empty site.
 * Does not call the unscoped site-wide detail endpoint.
 */
export async function fetchOverviewAssembledSiteConditionState(
  siteId: string,
): Promise<ContinuousSafetyFetchResult<SiteConditionStateResponse>> {
  const identity = await fetchSiteConditionState({
    siteId,
    identityOnly: true,
  });
  if (identity.status !== 'success') {
    return identity;
  }
  if (identity.data.identityOnly !== true) {
    return overviewAssemblyError();
  }

  const queryAsOfIso = normalizeConditionStateAsOf(identity.data.queryAsOf);
  if (!queryAsOfIso) {
    return overviewAssemblyError();
  }

  const survivorIds = uniqueSurvivorConditionIds(identity.data.conditions);
  if (survivorIds.length === 0) {
    return {
      status: 'success',
      data: assembledEmptySiteConditionState(identity.data),
    };
  }

  const classified = await mapWithConcurrency(
    survivorIds,
    OVERVIEW_DETAIL_CONCURRENCY,
    async (conditionId) => classifyOverviewDetail(
      identity.data,
      conditionId,
      await readOverviewConditionDetail(siteId, conditionId, queryAsOfIso),
    ),
  );

  const assembledConditions: SiteConditionState[] = [];
  const failedReads: NonNullable<SiteConditionStateResponse['failedReads']> = [];
  classified.forEach((item) => {
    if ('row' in item) assembledConditions.push(item.row);
    else failedReads.push(item.failure);
  });

  if (!assembledConditions.length) {
    const httpFailure = failedReads.find((item) => item.httpStatus != null);
    return {
      status: 'error',
      httpStatus: httpFailure?.httpStatus ?? null,
      message: OVERVIEW_ASSEMBLY_UNAVAILABLE,
    };
  }

  return {
    status: 'success',
    data: {
      ok: true,
      orgId: identity.data.orgId,
      siteId: identity.data.siteId,
      queryAsOf: identity.data.queryAsOf,
      conditionCount: assembledConditions.length,
      conditions: assembledConditions,
      identityOnly: false,
      readCompleteness: failedReads.length ? 'partial' : 'complete',
      siteTotalsAuthoritative: failedReads.length === 0,
      failedReads,
      diagnostics: {
        suppressedDeviceScopeOrphanTwins:
          identity.data.diagnostics?.suppressedDeviceScopeOrphanTwins || [],
      },
    },
  };
}

async function fetchAttentionPolicyOverviewCutoverFlag(): Promise<boolean> {
  try {
    const response = await api.get('/api/v1/feature-flags');
    return response.data?.flags?.attention_policy_overview_cutover === true;
  } catch {
    return false;
  }
}

export async function fetchOverviewSiteConditionPosture(
  siteId: string,
  options?: {
    selectedOverTimeWindowId?: OverTimeWindowId;
    locationLookups?: SafetyLocationLookups;
    attentionPolicyOverviewCutover?: boolean;
  },
): Promise<ContinuousSafetyFetchResult<OverviewDecisionSummary>> {
  const [result, loadedLookups, cutoverFlag, siteTimeZone] = await Promise.all([
    fetchOverviewAssembledSiteConditionState(siteId),
    options?.locationLookups
      ? Promise.resolve(options.locationLookups)
      : loadSiteLocationLookups(siteId),
    options?.attentionPolicyOverviewCutover === undefined
      ? fetchAttentionPolicyOverviewCutoverFlag()
      : Promise.resolve(Boolean(options.attentionPolicyOverviewCutover)),
    Promise.resolve()
      .then(() => (typeof sitesApi.getSite === 'function' ? sitesApi.getSite(siteId) : null))
      .then((site) => resolveSiteTimeZone(site))
      .catch(() => null),
  ]);
  if (result.status !== 'success') {
    return result;
  }
  const summary = buildOverviewDecisionSummary(result.data, {
    ...options,
    locationLookups: options?.locationLookups || loadedLookups,
    attentionPolicyOverviewCutover: cutoverFlag,
    siteTimezone: siteTimeZone,
  });
  let withNow = summary;
  try {
    const live = await incidentsApi.getBuyerLiveAlerts({ limit: 200 });
    const items = live.ok !== false && live.read_state !== 'unavailable' ? (live.items || []) : [];
    withNow = mergeOverviewV2CompositeNow(summary, items);
  } catch {
    withNow = summary;
  }
  try {
    const persisted = await incidentsApi.getBuyerAlertHistory(200);
    const items = persisted.ok !== false && persisted.read_state !== 'unavailable'
      ? (persisted.items || [])
      : [];
    const merged = mergeOverviewV2CompositeRecurring(withNow, items);
    return { status: 'success', data: await attachCompositeLifecycle(siteId, merged) };
  } catch {
    return { status: 'success', data: await attachCompositeLifecycle(siteId, withNow) };
  }
}

async function attachCompositeLifecycle(
  siteId: string,
  summary: OverviewDecisionSummary,
): Promise<OverviewDecisionSummary> {
  const rows = summary.overTime.conditionRows || [];
  const composite = rows.filter((row) => row.subjectKind === 'COMPOSITE');
  if (!composite.length) return summary;
  const overlays = await Promise.all(composite.map(async (row) => {
    const match = /composite:([^:]+):([^:]+):(.+)$/.exec(row.conditionId);
    if (!match) return null;
    const [, ruleId, scopeType, scopeId] = match;
    try {
      const response = await api.get(
        `/api/v1/sites/${encodeURIComponent(siteId)}/condition-lifecycle`,
        {
          params: {
            subject_kind: 'COMPOSITE',
            composite_rule_id: ruleId,
            composite_scope_type: scopeType,
            composite_scope_id: scopeId,
          },
        },
      );
      const condition = (response.data?.conditions || [])[0] || {};
      const control = condition.composite_control || {};
      const since = condition.composite_since || {};
      if (!control.control_id && !control.control_revision_id) return null;
      return {
        conditionId: row.conditionId,
        controlId: control.control_id,
        controlRevisionId: control.control_revision_id,
        controlObjective: control.control_objective,
        implementedAt: control.implemented_at,
        sinceCount: since.count,
        sinceSummary: since.summary,
        latestOccurredAt: since.latest_occurred_at,
        comparability: since.comparability,
      };
    } catch {
      return null;
    }
  }));
  const nextRows = mergeOverviewV2CompositeLifecycle(
    rows,
    overlays.filter((item): item is NonNullable<typeof item> => Boolean(item)),
  );
  return {
    ...summary,
    overTime: { ...summary.overTime, conditionRows: nextRows },
  };
}
